import store from "/WebInterface/new-ui/upload/js/reducer.js";
import Tooltip from "/WebInterface/new-ui/modules/tooltip/tooltip.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import {
  openFile,
  closeFile,
  isFileOpen,
  chunkAckXHR,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  handleFileOpenError,
  handleCloseFileRetryCommon,
  startFileUploadCommon,
  failFileOpenCommon,
  handleFileOpenResponse,
  handleRetryUploading,
  updateTimeStatusOfUploadingFileCommon,
  pausingUpload,
  pauseUpload,
  resumeUpload,
  closeFileUsingBlockUnBlockUpload,
  failDirectoryPrep,
  ensureUploadDirectory,
  getChunk,
  checkFilePartiallyUploaded,
  beforeUnloadEvent,
  invoke,
} from "/WebInterface/new-ui/upload/js/common.js";
import uploadingQueue from "/WebInterface/new-ui/upload/js/uploading-queue.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";

class WSUploads {
  constructor({
    maxRetries,
    WSUploadMaxRequests = 10,
    WSUploadDefaultChunkSize = 5,
    serverWriteThresholdWSUpload = 100,
    showServerWriteProgress = false,
    chunkPreparationTimeout = 480,
    startChunkSizeMB = 5,
    maxChunkSizeMB = null,
    WSUploadChunkTimeout = 30,
  }) {
    if (this.shouldLog()) {
      // Initialize debugLogs early for constructor logging
      if (!this.debugLogs) {
        this.debugLogs = [];
      }
      const configLog = JSON.stringify(
        {
          maxRetries,
          WSUploadMaxRequests,
          WSUploadDefaultChunkSize,
          serverWriteThresholdWSUpload,
          showServerWriteProgress,
          chunkPreparationTimeout,
          startChunkSizeMB,
          maxChunkSizeMB,
          WSUploadChunkTimeout,
        },
        null,
        2
      );
      this.logToConsoleAndDebug(`Constructor config: ${configLog}`);
    }
    this.showServerWriteProgress = showServerWriteProgress;
    this.serverWriteThreshold = serverWriteThresholdWSUpload
      ? parseInt(serverWriteThresholdWSUpload)
      : 100;
    this.maxRetries = maxRetries ? parseInt(maxRetries) : 30;
    this.maxRequest = WSUploadMaxRequests ? parseInt(WSUploadMaxRequests) : 10;
    this.defaultChunkSize = WSUploadDefaultChunkSize
      ? parseInt(WSUploadDefaultChunkSize)
      : 5;
    this.chunkSizeMode = true;
    // Use maxChunkSizeMB if provided, otherwise fall back to defaultChunkSize
    const parsedMaxSize =
      maxChunkSizeMB !== null && maxChunkSizeMB !== undefined
        ? typeof maxChunkSizeMB === "number"
          ? maxChunkSizeMB
          : parseFloat(maxChunkSizeMB)
        : this.defaultChunkSize;
    this.maxChunkSizeMB =
      !isNaN(parsedMaxSize) && parsedMaxSize > 0
        ? parsedMaxSize
        : this.defaultChunkSize;
    // Configurable start chunk size (can be float MB), clamped to maxChunkSizeMB and > 0 MB
    const parsedStartSize =
      typeof startChunkSizeMB === "number"
        ? startChunkSizeMB
        : parseFloat(startChunkSizeMB);
    this.startChunkSizeMB =
      !isNaN(parsedStartSize) && parsedStartSize > 0
        ? Math.min(parsedStartSize, this.maxChunkSizeMB)
        : 5;
    // Set chunk timeout before logging (needed for log action)
    this.chunkTimeout = WSUploadChunkTimeout
      ? parseInt(WSUploadChunkTimeout)
      : 30;

    // Queue monitoring thresholds (configurable via user customizations if needed)
    this.queueWarningThresholds = {
      queueDepthWarning: 5, // Warn if socket queue has 5+ chunks
      queueWaitWarningRatio: 0.5, // Warn if chunk queued > 50% of chunkTimeout
      averageWaitTimeWarningRatio: 0.3, // Warn if avg wait time > 30% of chunkTimeout
      warningCooldownMs: 10000, // Don't repeat same warning within 10 seconds
    };

    // Constants for logging and performance
    this.MAX_DEBUG_LOGS = 5000; // Maximum number of debug log entries to keep
    this.SHOULD_LOG_PROGRESS_THRESHOLD = 95; // Only log when upload reaches 95%
    this.PERIODIC_CHECK_INTERVAL_MS = 5000; // Periodic check interval in milliseconds
    this.MAX_DEBUG_INFO_LINES = 50000; // Maximum lines in debug info

    // Track last warning times to avoid spam
    this.lastQueueWarnings = {
      deepQueue: {}, // socket index -> timestamp
      longWaitChunk: {}, // chunk index -> timestamp
      highAverageWait: null, // timestamp
    };

    this.logActionAndDebug("chunk-size", "Start chunk size resolved", {
      rawStartChunkSizeMB: startChunkSizeMB,
      parsedStartChunkSizeMB: parsedStartSize,
      effectiveStartChunkSizeMB: this.startChunkSizeMB,
      rawMaxChunkSizeMB: maxChunkSizeMB,
      parsedMaxChunkSizeMB: parsedMaxSize,
      effectiveMaxChunkSizeMB: this.maxChunkSizeMB,
      defaultChunkSizeMB: this.defaultChunkSize,
      chunkTimeout: this.chunkTimeout,
    });
    this.chunkSizeConfig = {
      checkDelayMs: 10000, // Check every 10 seconds instead of 30
      minAckedChunks: 30,
      maxAvgSecPerChunk: 10,
      targetChunkSizeMB: this.maxChunkSizeMB,
    };

    // Check if chunk size adjustment is needed (only if min != max)
    this.shouldAdjustChunkSize = this.startChunkSizeMB !== this.maxChunkSizeMB;

    if (!this.shouldAdjustChunkSize) {
      this.logAndDebug(
        `Upload: Chunk size adjustment disabled - Start chunk size (${this.startChunkSizeMB.toFixed(
          2
        )}MB) equals Max chunk size (${this.maxChunkSizeMB.toFixed(
          2
        )}MB). No dynamic adjustment needed.`
      );
      this.logActionAndDebug("chunk-size", "Chunk size adjustment disabled", {
        startChunkSizeMB: this.startChunkSizeMB,
        maxChunkSizeMB: this.maxChunkSizeMB,
        reason: "Start and max chunk sizes are equal - no adjustment possible",
      });
    } else {
      this.logAndDebug(
        `Upload: Chunk size adjustment enabled - Start: ${this.startChunkSizeMB.toFixed(
          2
        )}MB, Max: ${this.maxChunkSizeMB.toFixed(
          2
        )}MB. Dynamic adjustment will be attempted.`
      );
    }

    // testRecovery removed - was stored but never used
    this.chunkPreparationTimeout = chunkPreparationTimeout;
    // chunkTimeout is already set above before the log action
    this.initializeVariables();
    C.log(
      `Upload: Server write buffer size in MB: ${this.serverWriteThreshold}`
    );
    this.uploadSummary = {
      totalChunks: 0,
      averageUploadTime: 0,
      openFileTime: 0,
      closeFileTime: 0,
      socketInfo: [],
      sendChunksRejections: {
        noIdleSocket: 0,
        noReadyChunk: 0,
        serverWriteThresholdExceeded: 0,
      },
      xhrStats: {
        totalCalls: 0,
        successfulCalls: 0,
        failedCalls: 0,
        retryAttempts: 0,
        averageResponseTime: 0,
        totalResponseTime: 0,
        failureReasons: {},
      },
      chunkEvents: [], // Track all chunk events (retries, XHR checks, failures, etc.)
      chunkDetails: [],
      socketFailures: [], // Add socket failures tracking
      // Upload performance tracking
      averageChunkUploadTime: null, // Average upload time in seconds
      uploadTimeSamples: [], // Array to store upload time samples
    };
    this.sendChunksCallCount = 0;
    this.lastRandomCloseTime = Date.now();
    this.randomCloseInterval = 30000; // 30 seconds minimum between random closes
  }

  initializeVariables = () => {
    this.chunksInfo = {
      chunks: [],
      chunkPos: 0,
      totalChunksUploaded: 0,
      lastBytesUploaded: 0,
      bytesUploaded: 0,
      bytesSent: 0,
      elapsedTime: 0,
      intervalId: null,
      index: 0,
    };

    this.retries = 0;
    this.isPaused = false;
    this.currentChunkSize = this.startChunkSizeMB;
    // Array to store all console logs for debug info
    this.debugLogs = [];
    this.logActionAndDebug("chunk-size", "Start chunk size applied", {
      effectiveStartChunkSizeMB: this.startChunkSizeMB,
      currentChunkSizeMB: this.currentChunkSize,
    });
    this.MB = 1048576;
    this.maxConnectionRetries = 2;
    this.maxCloseFileRetries = 100;
    this.waitForAllSocketBeforeStart = true;
    this.chunksSent = [];

    // Important logs array for debugging
    this.importantLogs = [];
    this.chunksAckd = new Set(); // Use Set to prevent duplicate ACKs
    this.chunksBeingRead = {};
    this.uploadStartTime = null;
    this.uploadEndTime = null;
    this.pauseLog = [];
    this.uploadDone = false;
    this.lastChunkSentTime = null;
    this.lastChunkSentIsRetry = false;
    this.sockets = [];
    this.chunkStart = 0;
    this.chunkNum = 0;
    this.fileClosed = false;
    this.chunkSizeTimerId = null;
    this.chunkSizeEvaluationResults = null; // Store evaluation results for debug info
    this.connectionRetries = 0;
    this.closeFileStreamCallCount = 0; // Track number of times closeFileStream is called (reset per file)
    this.isSendingChunks = false; // Guard to prevent concurrent sendChunks execution

    //to know if we are stuck in chunk preparation
    this.lastChunkGeneratedTime = null;
    this.chunkPreparationTimeoutId = null;
    this.chunkPreparationStalled = false;
    this.chunkPreparationStallCheckInterval = null;

    //Related to the upload summary
    this.serverWriteUpdateInterval = null;
    this.totalSocketsRespawned = 0;
    this.totalChunkPreparationStalls = 0;
    this.totalRecoveryActions = 0;
    this.totalChunkResendAttempts = 0;
    this.totalStuckSocketIdentifications = 0;
    this.lastPeriodicCheckTime = null;

    // Last chunk multi-socket retry cooldown
    this.lastChunkMultiSocketRetryTime = null;
    this.lastChunkMultiSocketRetryCooldown = 30000; // 30 seconds cooldown

    this.allChunksCreated = false;

    // Cache for unacknowledged chunks to avoid repeated sorting
    this.cachedUnackdChunks = null;
    this.cachedUnackdChunksTime = 0;
    this.cachedUnackdChunksTimeout = 100; // Cache for 100ms

    // DOM update optimization - cache previous values to avoid unnecessary updates
    this.lastSummaryValues = {};

    // Socket management optimization - maintain separate array of open sockets
    this.openSockets = [];

    // Debounced function storage for DOM updates
    this.debouncedUpdates = {
      summaryPanel: null,
      summaryRow: null,
      serverWriteProgress: null,
    };

    // Critical fix: Reset uploadSummary arrays per file to prevent memory leaks
    // These arrays accumulate data for each file, and with 100+ files can cause browser crashes
    if (this.uploadSummary) {
      this.uploadSummary.chunkEvents = []; // Reset chunk events array per file
      this.uploadSummary.chunkDetails = []; // Reset chunk details array per file
      this.uploadSummary.socketFailures = []; // Reset socket failures array per file
      this.uploadSummary.uploadTimeSamples = []; // Reset upload time samples per file
      this.uploadSummary.totalChunks = 0; // Reset total chunks counter per file
      this.uploadSummary.openFileTime = 0; // Reset open file time per file
      this.uploadSummary.closeFileTime = 0; // Reset close file time per file
      this.uploadSummary.averageChunkUploadTime = null; // Reset average upload time per file
      this.uploadSummary.socketInfo = []; // Reset socket info per file
    }
  };

  // Simple debounce utility for DOM updates
  debounce = (func, delay, key) => {
    // Clear existing timeout
    if (this.debouncedUpdates[key]) {
      clearTimeout(this.debouncedUpdates[key]);
    }

    // Set new timeout
    this.debouncedUpdates[key] = setTimeout(() => {
      func();
      this.debouncedUpdates[key] = null;
    }, delay);
  };

  schedulechunkSizeCheck = () => {
    if (this.chunkSizeTimerId) return;

    // Don't schedule if chunk size adjustment is disabled (min === max)
    if (!this.shouldAdjustChunkSize) {
      this.logAndDebug(
        `Upload: ChunkSize check not scheduled - adjustment disabled (start chunk size equals max chunk size)`
      );
      return;
    }

    const { checkDelayMs } = this.chunkSizeConfig;
    this.chunkSizeTimerId = setTimeout(() => {
      this.chunkSizeTimerId = null;
      this.reCalculateChunkSize();
      // Reschedule for periodic checks (allow dynamic adjustment)
      // Only reschedule if adjustment is still enabled
      if (
        this.shouldAdjustChunkSize &&
        !this.uploadDone &&
        !this.fileClosed &&
        !this.isPaused
      ) {
        this.schedulechunkSizeCheck();
      }
    }, checkDelayMs);
    if (this.shouldLog()) {
      this.logAndDebug(
        `Upload: ChunkSize check scheduled in ${Math.round(
          checkDelayMs / 1000
        )}s`
      );
    }
  };

  reCalculateChunkSize = () => {
    const timestamp = new Date().toISOString();
    const timestampLocal = new Date().toLocaleString();

    // Early return if chunk size adjustment is disabled (min === max)
    if (!this.shouldAdjustChunkSize) {
      if (this.shouldLog()) {
        this.logAndDebug(
          `Upload: ChunkSize evaluation skipped - adjustment disabled (start chunk size ${this.startChunkSizeMB.toFixed(
            2
          )}MB equals max chunk size ${this.maxChunkSizeMB.toFixed(2)}MB)`
        );
      }
      return;
    }

    // Log that the method was called
    if (this.shouldLog()) {
      this.logAndDebug(
        `Upload: ChunkSize evaluation called - checking if chunk size should be adjusted`
      );
    }

    if (this.uploadDone || this.fileClosed || this.isPaused) {
      if (this.shouldLog()) {
        this.logAndDebug(
          `Upload: ChunkSize evaluation skipped - upload done: ${this.uploadDone}, file closed: ${this.fileClosed}, paused: ${this.isPaused}`
        );
      }
      return;
    }

    const { minAckedChunks, maxAvgSecPerChunk, targetChunkSizeMB } =
      this.chunkSizeConfig;

    const ackedCount = this.chunksAckd.size;
    const samples = this.uploadSummary.uploadTimeSamples || [];
    const avgTime =
      this.uploadSummary.averageChunkUploadTime ||
      (samples.length
        ? samples.reduce((sum, n) => sum + n, 0) / samples.length
        : null);

    // Need minimum samples to make a decision
    if (ackedCount < minAckedChunks || avgTime === null) {
      if (this.shouldLog()) {
        this.logAndDebug(
          `Upload: ChunkSize evaluation skipped - Conditions not met:` +
            `\n  - ACKs required: ${minAckedChunks}, ACKs received: ${ackedCount} (${
              ackedCount < minAckedChunks ? "NOT MET" : "MET"
            })` +
            `\n  - Average time: ${
              avgTime !== null ? avgTime.toFixed(2) + "s" : "N/A"
            } (${avgTime !== null ? "MET" : "NOT MET"})` +
            `\n  - Current chunk size: ${this.currentChunkSize.toFixed(2)}MB`
        );
      }

      // Log evaluation attempt even when conditions don't match
      this.logImportantEvent("CHUNK_SIZE_EVALUATION_ATTEMPT", {
        evaluationTime: timestamp,
        evaluationTimeLocal: timestampLocal,
        ackedCount,
        minAckedChunks,
        avgTimeSec: avgTime,
        currentChunkSizeMB: this.currentChunkSize,
        conditionsMet: false,
        reason:
          ackedCount < minAckedChunks
            ? `Insufficient ACKs (need ${minAckedChunks}, have ${ackedCount})`
            : `Average time not available`,
      });

      return;
    }

    // Capture currentChunkSize once at the start to ensure consistency throughout the evaluation
    // This prevents issues if the value is read multiple times
    const previousChunkSize = this.currentChunkSize;
    const currentChunkSizeSnapshot = this.currentChunkSize;
    let chunkSizeChanged = false;
    let changeReason = "";

    // Check if we should INCREASE chunk size
    const condition1Increase = currentChunkSizeSnapshot < targetChunkSizeMB;
    const condition2Increase = avgTime <= maxAvgSecPerChunk;
    const canIncreaseChunkSize = condition1Increase && condition2Increase;

    // Check if we should REDUCE chunk size (when speeds are poor)
    const condition1Decrease = currentChunkSizeSnapshot > this.startChunkSizeMB;
    const condition2Decrease = avgTime > maxAvgSecPerChunk;
    const shouldReduceChunkSize = condition1Decrease && condition2Decrease;

    // Log evaluation attempt with all condition details
    if (this.shouldLog()) {
      this.logAndDebug(
        `Upload: ChunkSize evaluation - Checking conditions:` +
          `\n  - ACKs: ${ackedCount} (required: ${minAckedChunks}) ✓` +
          `\n  - Average upload time: ${avgTime.toFixed(
            2
          )}s (threshold: ${maxAvgSecPerChunk}s)` +
          `\n  - Current chunk size: ${currentChunkSizeSnapshot.toFixed(2)}MB` +
          `\n  - Start chunk size (min): ${this.startChunkSizeMB.toFixed(
            2
          )}MB` +
          `\n  - Target chunk size (max): ${targetChunkSizeMB.toFixed(2)}MB` +
          `\n  - INCREASE conditions:` +
          `\n    * Current < Target: ${currentChunkSizeSnapshot.toFixed(
            2
          )} < ${targetChunkSizeMB.toFixed(2)} = ${
            condition1Increase ? "✓ MET" : "✗ NOT MET"
          }` +
          `\n    * Avg time <= Threshold: ${avgTime.toFixed(
            2
          )} <= ${maxAvgSecPerChunk} = ${
            condition2Increase ? "✓ MET" : "✗ NOT MET"
          }` +
          `\n    * Result: ${
            canIncreaseChunkSize ? "CAN INCREASE" : "CANNOT INCREASE"
          }` +
          `\n  - DECREASE conditions:` +
          `\n    * Current > Start: ${currentChunkSizeSnapshot.toFixed(
            2
          )} > ${this.startChunkSizeMB.toFixed(2)} = ${
            condition1Decrease ? "✓ MET" : "✗ NOT MET"
          }` +
          `\n    * Avg time > Threshold: ${avgTime.toFixed(
            2
          )} > ${maxAvgSecPerChunk} = ${
            condition2Decrease ? "✓ MET" : "✗ NOT MET"
          }` +
          `\n    * Result: ${
            shouldReduceChunkSize ? "SHOULD DECREASE" : "NO DECREASE NEEDED"
          }`
      );
    }

    // Log evaluation attempt to debug info
    this.logImportantEvent("CHUNK_SIZE_EVALUATION_ATTEMPT", {
      evaluationTime: timestamp,
      evaluationTimeLocal: timestampLocal,
      ackedCount,
      minAckedChunks,
      avgTimeSec: avgTime,
      maxAvgSecPerChunk,
      currentChunkSizeMB: currentChunkSizeSnapshot,
      startChunkSizeMB: this.startChunkSizeMB,
      targetChunkSizeMB,
      increaseConditions: {
        currentLessThanTarget: condition1Increase,
        avgTimeLessThanThreshold: condition2Increase,
        canIncrease: canIncreaseChunkSize,
      },
      decreaseConditions: {
        currentGreaterThanStart: condition1Decrease,
        avgTimeGreaterThanThreshold: condition2Decrease,
        shouldDecrease: shouldReduceChunkSize,
      },
      conditionsMet: canIncreaseChunkSize || shouldReduceChunkSize,
    });

    if (canIncreaseChunkSize) {
      // Increase by 1MB, capped at targetChunkSizeMB
      const increasedSize = currentChunkSizeSnapshot + 1;
      const newChunkSize = Math.min(increasedSize, targetChunkSizeMB);
      if (newChunkSize > currentChunkSizeSnapshot) {
        this.currentChunkSize = newChunkSize;
        chunkSizeChanged = true;
        changeReason = `increased by 1MB (avg ${avgTime.toFixed(
          2
        )}s <= ${maxAvgSecPerChunk}s threshold)`;
      }
    } else if (shouldReduceChunkSize) {
      // Decrease by 1MB, but don't go below startChunkSizeMB
      const decreasedSize = currentChunkSizeSnapshot - 1;
      const newChunkSize = Math.max(decreasedSize, this.startChunkSizeMB);
      if (newChunkSize < currentChunkSizeSnapshot) {
        this.currentChunkSize = newChunkSize;
        chunkSizeChanged = true;
        changeReason = `reduced by 1MB (avg ${avgTime.toFixed(
          2
        )}s > ${maxAvgSecPerChunk}s threshold)`;
      }
    }

    // Count chunks ready to be sent (status === "init" and not in chunksSent)
    const chunksReadyToSend = this.chunksInfo?.chunks
      ? this.chunksInfo.chunks.filter(
          (chunk) =>
            chunk.status === "init" && !this.chunksSent.includes(chunk.index)
        ).length
      : 0;

    const evalContext = {
      acked: ackedCount,
      minAckedChunks,
      avgTimeSec: avgTime,
      maxAvgSecPerChunk,
      currentChunkSizeMB: this.currentChunkSize,
      previousChunkSizeMB: previousChunkSize,
      targetChunkSizeMB,
      increase: canIncreaseChunkSize,
      decrease: shouldReduceChunkSize,
      changed: chunkSizeChanged,
      changeReason,
      chunksReadyToSend,
    };

    // Store evaluation results for debug info
    this.chunkSizeEvaluationResults = {
      ...evalContext,
      evaluationTime: timestamp,
      evaluationTimeLocal: timestampLocal,
    };

    // Log evaluation result (whether changed or not)
    if (chunkSizeChanged) {
      // Console log with detailed information following standard format
      const direction = canIncreaseChunkSize ? "INCREASED" : "DECREASED";
      const previousSizeMB = `${previousChunkSize.toFixed(2)} MB`;
      const newSizeMB = `${this.currentChunkSize.toFixed(2)} MB`;

      if (canIncreaseChunkSize) {
        this.logAndDebug(
          `[CHUNK_SIZE] ${direction} - Previous: ${previousSizeMB}, New: ${newSizeMB}, ACKs: ${ackedCount}, Avg: ${avgTime.toFixed(
            2
          )}s, Threshold: ${maxAvgSecPerChunk}s, Target: ${targetChunkSizeMB.toFixed(
            2
          )} MB, Ready: ${chunksReadyToSend}`
        );
      } else if (shouldReduceChunkSize) {
        this.logAndDebug(
          `[CHUNK_SIZE] ${direction} - Previous: ${previousSizeMB}, New: ${newSizeMB}, ACKs: ${ackedCount}, Avg: ${avgTime.toFixed(
            2
          )}s, Threshold: ${maxAvgSecPerChunk}s, Min: ${this.startChunkSizeMB.toFixed(
            2
          )} MB, Ready: ${chunksReadyToSend}`
        );
      }

      // Log to debug info with full details
      this.logImportantEvent("CHUNK_SIZE_CHANGED", {
        direction: canIncreaseChunkSize ? "INCREASE" : "DECREASE",
        previousSizeMB: previousChunkSize,
        newSizeMB: this.currentChunkSize,
        changePercentage: canIncreaseChunkSize ? "+1MB" : "-1MB",
        ackedCount,
        avgTimeSec: avgTime,
        maxAvgSecPerChunk,
        targetChunkSizeMB,
        startChunkSizeMB: this.startChunkSizeMB,
        changeReason,
        chunksReadyToSend,
        timestamp,
        timestampLocal,
      });

      // Also log via C.logAction for action tracking
      this.logActionAndDebug(
        "chunk-size",
        `ChunkSize ${canIncreaseChunkSize ? "increased" : "decreased"}`,
        {
          previous: previousChunkSize.toFixed(2) + "MB",
          new: this.currentChunkSize.toFixed(2) + "MB",
          change: canIncreaseChunkSize ? "+1MB" : "-1MB",
          acked: ackedCount,
          avgTime: avgTime.toFixed(2) + "s",
          reason: changeReason,
          chunksReadyToSend,
        }
      );
    } else {
      // Log when conditions are met but no change is made (e.g., already at max/min)
      if (this.shouldLog()) {
        this.logAndDebug(
          `Upload: ChunkSize evaluation - Conditions ${
            canIncreaseChunkSize || shouldReduceChunkSize
              ? "matched"
              : "did not match"
          }, but no change made. Current: ${this.currentChunkSize.toFixed(2)}MB`
        );
      }

      this.logImportantEvent("CHUNK_SIZE_EVALUATION_NO_CHANGE", {
        evaluationTime: timestamp,
        evaluationTimeLocal: timestampLocal,
        ackedCount,
        avgTimeSec: avgTime,
        currentChunkSizeMB: this.currentChunkSize, // Use actual current value after potential change
        previousChunkSizeMB: previousChunkSize,
        canIncrease: canIncreaseChunkSize,
        shouldDecrease: shouldReduceChunkSize,
        reason: canIncreaseChunkSize
          ? `Already at or near target size (${targetChunkSizeMB.toFixed(2)}MB)`
          : shouldReduceChunkSize
          ? `Already at or near start size (${this.startChunkSizeMB.toFixed(
              2
            )}MB)`
          : `Conditions not met for increase or decrease`,
      });
    }
  };

  // Helper method to check if we should log based on upload progress
  shouldLog = () => {
    if (!this.streamingFileInfo?.size) return true; // Always log if no file size info
    const progress = this.getPercentage(
      this.streamingFileInfo.size,
      this.chunksInfo.bytesUploaded
    );
    return progress >= this.SHOULD_LOG_PROGRESS_THRESHOLD;
  };

  createBuffer = (chunkSize, chunkIndex, chunkDataBuffer) => {
    const buffer = new ArrayBuffer(4 + chunkSize);
    const dataView = new Uint8Array(buffer, 4);
    const indexView = new DataView(buffer);
    indexView.setUint32(0, chunkIndex + 1);
    dataView.set(new Uint8Array(chunkDataBuffer));
    return buffer;
  };

  getPercentage = (fileSize, bytesReceived) => {
    if (fileSize === 0) return 0;
    const progress = Math.floor((bytesReceived / fileSize) * 100);
    // Cap at 99% to ensure 100% only shows when truly complete
    return Math.min(progress, 99);
  };

  // Common method to log to console AND store for debug info
  logToConsoleAndDebug = (message) => {
    // Format timestamp in "12:44:58 PM" format
    const timestamp = new Date().toLocaleTimeString();

    // Prepend timestamp in "12:44:58 PM /" format
    const timestampedMessage = `${timestamp} / ${message}`;

    // Log to console
    console.log(timestampedMessage);
    // Store for debug info (initialize if needed)
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(timestampedMessage);

    // Prevent memory leak: limit debugLogs to last MAX_DEBUG_LOGS entries
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      // Remove oldest entries, keep the most recent ones
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Common method to warn to console AND store for debug info
  warnToConsoleAndDebug = (message) => {
    // Warn to console
    console.warn(message);
    // Store for debug info (initialize if needed)
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(`[WARN] ${message}`);

    // Prevent memory leak: limit debugLogs to last MAX_DEBUG_LOGS entries
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Common method to error to console AND store for debug info
  errorToConsoleAndDebug = (message) => {
    // Format timestamp in "12:44:58 PM" format
    const timestamp = new Date().toLocaleTimeString();

    // Prepend timestamp in "12:44:58 PM /" format
    const timestampedMessage = `${timestamp} / ${message}`;

    // Error to console
    console.error(timestampedMessage);
    // Store for debug info (initialize if needed)
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(`[ERROR] ${timestampedMessage}`);

    // Prevent memory leak: limit debugLogs to last MAX_DEBUG_LOGS entries
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Helper method to capture C.logAction to debug logs
  // This ensures all action logs appear in debug info
  logActionAndDebug = (category, message, context = {}, skipDedupe = false) => {
    // Format timestamp in "12:44:58 PM" format
    const timestamp = new Date().toLocaleTimeString();

    // Format the complete message with timestamp first: "1:07:56 PM / [ACTION:chunk] Chunk sent"
    const timestampedMessage = `${timestamp} / [ACTION:${category}] ${message}`;

    // Call C.logAction to add to debugLogs (it will add [ACTION:category] prefix internally)
    // But we'll override the console.log output with our timestamped format
    if (C.debugLogs) {
      C.debugLogs.add("action", category, message, context, skipDedupe);
    }

    // Log to console with our custom format (timestamp first) - matching C.logAction's verbose check
    // We need to check the same conditions C.logAction checks, but we'll use console.log directly
    // Since we can't easily access the verbose/environment flags, we'll just always log
    // (C.logAction will also log, but with different format - that's okay, both will appear)
    console.log(timestampedMessage, context);

    // Also capture to our debug logs with timestamp
    const formattedMessage = `${timestamp} / [ACTION:${category}] ${message} ${JSON.stringify(
      context
    )}`;
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(formattedMessage);

    // Prevent memory leak
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Helper method to capture C.log to debug logs
  logAndDebug = (...args) => {
    // Format timestamp in "12:44:58 PM" format
    const timestamp = new Date().toLocaleTimeString();

    // Join all arguments into a single message
    const message = args.join(" ");

    // Prepend timestamp in "12:44:58 PM /" format
    const timestampedMessage = `${timestamp} / ${message}`;

    // Call C.log (logs to console when dev/staging/verbose)
    C.log(timestampedMessage);

    // Also capture to our debug logs
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(timestampedMessage);

    // Prevent memory leak
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Helper method to capture C.warn to debug logs
  warnAndDebug = (message) => {
    // Format timestamp in "12:44:58 PM" format
    const timestamp = new Date().toLocaleTimeString();

    // Prepend timestamp in "12:44:58 PM /" format
    const timestampedMessage = `${timestamp} / ${message}`;

    // Call C.warn (always logs to console)
    C.warn(timestampedMessage);

    // Also capture to our debug logs
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(`[WARN] ${timestampedMessage}`);

    // Prevent memory leak
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Helper method to capture C.error to debug logs
  errorAndDebug = (message) => {
    // Call C.error (always logs to console)
    C.error(message);

    // Also capture to our debug logs
    if (!this.debugLogs) {
      this.debugLogs = [];
    }
    this.debugLogs.push(`[ERROR] ${message}`);

    // Prevent memory leak
    if (this.debugLogs.length > this.MAX_DEBUG_LOGS) {
      this.debugLogs = this.debugLogs.slice(-this.MAX_DEBUG_LOGS);
    }
  };

  // Helper method to format chunk size in MB
  formatChunkSizeMB = (bytes) => {
    if (!bytes || bytes === 0) return "0 MB";
    const mb = bytes / this.MB;
    return `${mb.toFixed(2)} MB`;
  };

  // Lightweight method to get chunk size in MB (for cases where only size is needed)
  getChunkSizeMB = (chunk) => {
    if (!chunk || !chunk.size) return "0 MB";
    return this.formatChunkSizeMB(chunk.size);
  };

  // Helper method to format wait time (difference between init_time and sent_at)
  formatWaitTime = (initTime, sentAt) => {
    if (!initTime) return "N/A";
    if (!sentAt || sentAt === null) return "Queued (waiting)";
    const waitMs = sentAt - initTime;
    if (waitMs < 0) return "0ms";
    if (waitMs < 1000) return `${Math.round(waitMs)}ms`;
    return `${(waitMs / 1000).toFixed(2)}s`;
  };

  // Helper method to set chunk timing (init_time and sent_at) based on socket queue state
  setChunkTiming = (chunk, socket, isRetry = false) => {
    const currentTime = Date.now();

    // Always set init_time when chunk is assigned to socket (for fallback if ACKed before sent_at is set)
    chunk.init_time = currentTime;

    // Check if socket queue is empty (before this chunk is added)
    const isSocketQueueEmpty = socket.chunksBeingSent.length === 0;

    if (isSocketQueueEmpty) {
      // Socket is empty, chunk will be sent immediately
      chunk.sent_at = currentTime;
      if (this.shouldLog()) {
        C.log(
          `Upload: ${isRetry ? "Retry " : ""}Chunk ${
            chunk.index
          } sent on socket ${socket.index}`
        );
      }
      // Detailed console log for chunk sent immediately (no queuing)
      const sizeMB = this.getChunkSizeMB(chunk);
      this.logToConsoleAndDebug(
        `[INFLIGHT] Chunk ${chunk.index} - Size: ${sizeMB}, Socket: ${
          socket.index
        }, Init: ${this.formatSimpleTime(
          chunk.init_time
        )}, Start: ${this.formatSimpleTime(chunk.sent_at)}, Wait: 0ms`
      );
    } else {
      // Socket has chunks ahead, this chunk is queued
      chunk.sent_at = null;
      const chunksAhead = socket.chunksBeingSent.length;
      const sizeMB = this.getChunkSizeMB(chunk);
      if (this.shouldLog()) {
        C.log(
          `Upload: ${isRetry ? "Retry " : ""}Chunk ${
            chunk.index
          } queued on socket ${socket.index} (${chunksAhead} ahead)`
        );
      }
      // Detailed console log for queued chunk
      this.logToConsoleAndDebug(
        `[QUEUED] Chunk ${chunk.index} - Size: ${sizeMB}, Socket: ${
          socket.index
        }, Init: ${this.formatSimpleTime(
          chunk.init_time
        )}, Ahead: ${chunksAhead}`
      );
    }

    return { initTime: chunk.init_time, sentAt: chunk.sent_at };
  };

  // ========== QUEUE MONITORING HELPER METHODS ==========

  // Check if a chunk has been queued too long
  checkChunkQueueTime = (chunk) => {
    if (!chunk || !chunk.init_time || chunk.sent_at !== null) {
      return { isTooLong: false, waitTime: 0 };
    }

    const waitTime = Date.now() - chunk.init_time;
    const warningThreshold =
      this.chunkTimeout *
      this.queueWarningThresholds.queueWaitWarningRatio *
      1000;
    const isTooLong = waitTime > warningThreshold;

    return { isTooLong, waitTime, warningThreshold };
  };

  // Check if a socket's queue depth is too high
  checkSocketQueueDepth = (socket) => {
    if (!socket || !socket.chunksBeingSent) {
      return { isTooDeep: false, depth: 0 };
    }

    const depth = socket.chunksBeingSent.length;
    const isTooDeep = depth >= this.queueWarningThresholds.queueDepthWarning;

    return { isTooDeep, depth };
  };

  // Calculate average wait time for queued chunks
  calculateAverageWaitTime = (chunks) => {
    if (!chunks || chunks.length === 0) {
      return { average: 0, count: 0 };
    }

    const queuedChunks = chunks.filter(
      (chunk) =>
        chunk.init_time &&
        chunk.sent_at === null &&
        chunk.status === "uploading"
    );

    if (queuedChunks.length === 0) {
      return { average: 0, count: 0 };
    }

    const waitTimes = queuedChunks.map((chunk) => Date.now() - chunk.init_time);
    const average =
      waitTimes.reduce((sum, wt) => sum + wt, 0) / waitTimes.length;

    return { average, count: queuedChunks.length };
  };

  // Check if average wait time is too high
  checkAverageWaitTime = (chunks) => {
    const { average, count } = this.calculateAverageWaitTime(chunks);
    if (count === 0) {
      return { isTooHigh: false, average: 0, count: 0 };
    }

    const warningThreshold =
      this.chunkTimeout *
      this.queueWarningThresholds.averageWaitTimeWarningRatio *
      1000;
    const isTooHigh = average > warningThreshold;

    return { isTooHigh, average, count, warningThreshold };
  };

  // Check if we should warn (cooldown check)
  shouldWarn = (warningType, identifier = null) => {
    const now = Date.now();
    const cooldown = this.queueWarningThresholds.warningCooldownMs;

    if (warningType === "deepQueue" && identifier !== null) {
      const lastWarning = this.lastQueueWarnings.deepQueue[identifier];
      if (lastWarning && now - lastWarning < cooldown) {
        return false;
      }
      this.lastQueueWarnings.deepQueue[identifier] = now;
      return true;
    }

    if (warningType === "longWaitChunk" && identifier !== null) {
      const lastWarning = this.lastQueueWarnings.longWaitChunk[identifier];
      if (lastWarning && now - lastWarning < cooldown) {
        return false;
      }
      this.lastQueueWarnings.longWaitChunk[identifier] = now;
      return true;
    }

    if (warningType === "highAverageWait") {
      const lastWarning = this.lastQueueWarnings.highAverageWait;
      if (lastWarning && now - lastWarning < cooldown) {
        return false;
      }
      this.lastQueueWarnings.highAverageWait = now;
      return true;
    }

    // Default: allow warning if warningType doesn't match any known type
    return true;
  };

  // Main method to check queue health and issue warnings
  checkQueueHealth = (context = {}) => {
    const { chunk = null, socket = null, checkAll = false } = context;
    let warningsIssued = 0;

    // Check individual chunk queue time
    if (chunk) {
      const { isTooLong, waitTime, warningThreshold } =
        this.checkChunkQueueTime(chunk);
      if (isTooLong && this.shouldWarn("longWaitChunk", chunk.index)) {
        const waitSeconds = (waitTime / 1000).toFixed(2);
        const thresholdSeconds = (warningThreshold / 1000).toFixed(2);
        C.warn(
          `Upload: Chunk ${chunk.index} has been queued for ${waitSeconds}s (threshold: ${thresholdSeconds}s). ` +
            `This may indicate a bottleneck. Socket queue depth: ${
              socket?.chunksBeingSent?.length || "N/A"
            }`
        );
        warningsIssued++;
      }
    }

    // Check socket queue depth
    if (socket) {
      const { isTooDeep, depth } = this.checkSocketQueueDepth(socket);
      if (isTooDeep && this.shouldWarn("deepQueue", socket.index)) {
        C.warn(
          `Upload: Socket ${socket.index} has deep queue (${depth} chunks, threshold: ${this.queueWarningThresholds.queueDepthWarning}). ` +
            `This may indicate the socket is slow or overloaded.`
        );
        warningsIssued++;
      }
    }

    // Check average wait time across all chunks (only if checkAll is true to avoid performance impact)
    if (checkAll && this.chunksInfo?.chunks) {
      const { isTooHigh, average, count, warningThreshold } =
        this.checkAverageWaitTime(this.chunksInfo.chunks);
      if (isTooHigh && this.shouldWarn("highAverageWait")) {
        const avgSeconds = (average / 1000).toFixed(2);
        const thresholdSeconds = (warningThreshold / 1000).toFixed(2);
        C.warn(
          `Upload: Average queue wait time is high: ${avgSeconds}s (${count} queued chunks, threshold: ${thresholdSeconds}s). ` +
            `Consider increasing socket count or reducing chunk size.`
        );
        warningsIssued++;
      }
    }

    // Log that check was performed (even if no warnings)
    if (this.shouldLog()) {
      const contextInfo = [];
      if (chunk) contextInfo.push(`C${chunk.index}`);
      if (socket) contextInfo.push(`S${socket.index}`);
      if (checkAll) contextInfo.push("all");
      const contextStr =
        contextInfo.length > 0 ? ` [${contextInfo.join(",")}]` : "";
      if (warningsIssued === 0) {
        C.log(`Queue check${contextStr} - OK`);
      } else {
        C.log(`Queue check${contextStr} - ${warningsIssued} warn`);
      }
    }
  };

  // Helper method to format time using toLocaleTimeString for consistency
  // This is a thin wrapper that provides consistent error handling
  formatSimpleTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleTimeString();
  };

  // Helper method to format chunk time strings for logging (consolidated with formatChunkTimeInfo)
  formatChunkTimeStrings = (chunk) => {
    let initTimeStr = "N/A";
    if (chunk?.init_time) {
      const initDate = new Date(chunk.init_time);
      initTimeStr = initDate.toLocaleTimeString();
    }

    let sentAtStr = "N/A";
    if (chunk?.sent_at && chunk.sent_at !== null) {
      const sentDate = new Date(chunk.sent_at);
      sentAtStr = sentDate.toLocaleTimeString();
    } else if (chunk?.sent_at === null) {
      sentAtStr = "Queued";
    }

    const waitTime = chunk
      ? this.formatWaitTime(chunk.init_time, chunk.sent_at)
      : "N/A";
    return { initTimeStr, sentAtStr, waitTime };
  };

  // Helper method to format chunk upload time (timeTaken)
  formatUploadTime = (chunk) => {
    if (!chunk) return "N/A";

    // Use timeTaken if already calculated (more accurate, calculated in updateChunkStatus)
    if (chunk.timeTaken !== null && chunk.timeTaken !== undefined) {
      const timeTaken = chunk.timeTaken;
      if (timeTaken < 0) return "0.00s";
      if (timeTaken < 1) {
        return `${(timeTaken * 1000).toFixed(0)}ms`;
      }
      return `${timeTaken.toFixed(2)}s`;
    }

    // Fallback: Calculate timeTaken: use sent_at if available, otherwise fall back to init_time
    const timeToUse =
      chunk.sent_at && chunk.sent_at !== null ? chunk.sent_at : chunk.init_time;

    if (!timeToUse) return "N/A";

    const timeTaken = (Date.now() - timeToUse) / 1000; // Convert to seconds
    if (timeTaken < 0) return "0.00s";

    if (timeTaken < 1) {
      return `${(timeTaken * 1000).toFixed(0)}ms`;
    }
    return `${timeTaken.toFixed(2)}s`;
  };

  // Helper method to format comprehensive chunk information for logging
  // Helper method to format comprehensive chunk information for logging
  // Returns: {sizeMB, initTime, startTime, waitTime, uploadTime}
  // Used for: Detailed chunk logging with full context
  // Note: For size-only needs, use getChunkSizeMB() instead
  formatChunkInfoForLog = (chunk, includeUploadTime = false) => {
    if (!chunk) {
      return {
        sizeMB: "N/A",
        initTime: "N/A",
        startTime: "N/A",
        waitTime: "N/A",
        uploadTime: "N/A",
      };
    }

    const sizeMB = this.formatChunkSizeMB(chunk.size);
    const initTime = this.formatSimpleTime(chunk.init_time);
    const startTime =
      chunk.sent_at && chunk.sent_at !== null
        ? this.formatSimpleTime(chunk.sent_at)
        : "Queued";
    const waitTime = this.formatWaitTime(chunk.init_time, chunk.sent_at);
    const uploadTime = includeUploadTime ? this.formatUploadTime(chunk) : "N/A";

    return {
      sizeMB,
      initTime,
      startTime,
      waitTime,
      uploadTime,
    };
  };

  isSocketOpen = (socket) => {
    return socket && socket.readyState === WebSocket.OPEN;
  };

  // Add socket to open sockets array when it opens
  addToOpenSockets = (socket) => {
    if (this.isSocketOpen(socket) && !this.openSockets.includes(socket)) {
      this.openSockets.push(socket);
    }
  };

  // Remove socket from open sockets array when it closes
  removeFromOpenSockets = (socket) => {
    const index = this.openSockets.indexOf(socket);
    if (index !== -1) {
      this.openSockets.splice(index, 1);
    }
  };

  removeSocketFromStorage = (socket) => {
    if (!socket) return;
    if (socket.removeEventListener) {
      const socketEvents = Object.getOwnPropertyNames(socket);
      for (const event of socketEvents) {
        if (event.startsWith("on") && typeof socket[event] === "function") {
          socket.removeEventListener(event.slice(2), socket[event]);
        }
      }
    }
    const chunksBeingSent = [...socket.chunksBeingSent];

    if (this.shouldLog()) {
      C.log(
        `Upload: Removing socket from storage, chunksBeingSent: ${chunksBeingSent.join(
          ","
        )}`
      );
    }

    const index = this.sockets.indexOf(socket);
    if (index !== -1) {
      this.sockets.splice(index, 1);
      if (this.shouldLog()) {
        C.log(`Upload: Socket closed/released: ${socket}`);
      }
    }
    if (this.isSocketOpen(socket)) {
      socket.close();
    }
  };

  // Consolidated socket failure tracking method
  trackSocketFailure = (socket, event, type) => {
    if (!this.showServerWriteProgress) return;

    const socketIndex =
      socket && typeof socket.index !== "undefined" ? socket.index : "unknown";

    this.uploadSummary.socketFailures.push({
      socketIndex: socketIndex,
      type: type,
      timestamp: new Date().toLocaleString(),
      details: {
        event: event,
        socket: socket
          ? {
              index: socket.index,
              chunksBeingSent: socket.chunksBeingSent,
              chunksAckd: socket.chunksAckd,
              bufferedAmount: socket.bufferedAmount,
            }
          : null,
      },
    });
  };

  // Helper method to handle chunks with null sent_at when socket errors/closes
  handleQueuedChunksOnSocketError = (socket) => {
    if (
      !socket ||
      !socket.chunksBeingSent ||
      socket.chunksBeingSent.length === 0
    ) {
      return;
    }

    // For chunks that were queued (sent_at === null) on this socket, we need to handle them
    // They should be retried, so we mark them appropriately
    socket.chunksBeingSent.forEach((chunkIndex) => {
      const chunk = this.chunksInfo.chunks[chunkIndex];
      if (chunk && chunk.sent_at === null) {
        // Chunk was queued but socket errored, reset it for retry
        // Remove from chunksSent so it can be resent
        const sentIndex = this.chunksSent.indexOf(chunkIndex);
        if (sentIndex !== -1) {
          this.chunksSent.splice(sentIndex, 1);
        }
        // Reset chunk status to allow retry
        chunk.status = "init";
        chunk.sent_at = null;
        chunk.init_time = null; // Reset init_time for consistency (will be set fresh on retry)
        if (this.shouldLog()) {
          C.warn(
            `Upload: Queued chunk ${chunkIndex} on socket ${socket.index} reset for retry due to socket error/close`
          );
        }
      }
    });
  };

  // Consolidated socket cleanup method
  cleanupSocket = (socket) => {
    // Handle queued chunks before removing socket
    this.handleQueuedChunksOnSocketError(socket);
    this.removeSocketFromStorage(socket);
    this.handleError(socket);
  };

  // Consolidated socket event logging method
  logSocketEvent = (event, socket, message = null) => {
    if (this.shouldLog()) {
      const socketInfo = socket ? { ...socket } : "N/A";
      this.logToConsoleAndDebug(
        `Socket event: ${JSON.stringify({ event, socket: socketInfo })}`
      );
    }
  };

  // Consolidated socket event handler setup
  setupSocketEventHandlers = (socket) => {
    socket.onclose = (event) => {
      // Remove from open sockets array when socket closes
      this.removeFromOpenSockets(socket);

      if (!this.fileClosed) {
        C.log(`Upload: Socket closed`);
        this.logSocketEvent(event, socket);
        this.trackSocketFailure(socket, event, "close");
        this.cleanupSocket(socket);
      }
    };

    socket.onerror = (event) => {
      // Remove from open sockets array when socket has error
      this.removeFromOpenSockets(socket);

      // Log socket error
      this.logActionAndDebug("socket", "Socket error", {
        socketIndex: socket.index,
        error: event.error?.message || event.message || "Unknown",
      });

      C.error("Upload: Socket error");
      this.logSocketEvent(event, socket);
      this.trackSocketFailure(socket, event, "error");
      this.cleanupSocket(socket);
    };

    socket.onmessage = (event) => {
      this.handleMessageFromSocket(event, socket);
    };
  };

  createSocket = (cb = () => {}, retrySocket = false, index) => {
    if (this.isPaused || this.uploadDone || this.fileClosed) {
      C.warn(
        `Upload: Socket creation failed, because ${
          this.isPaused
            ? "upload is paused"
            : this.uploadDone
            ? "upload is done"
            : this.fileClosed
            ? "file is closed"
            : "unknown"
        }`
      );
      return;
    }
    if (typeof index !== "number") {
      // Calculate next socket index, handling empty array case
      index =
        this.sockets.length > 0
          ? this.sockets.reduce(
              (max, socket) => Math.max(max, socket.index),
              0
            ) + 1
          : 1;
    }
    if (!retrySocket && this.sockets.length >= this.maxRequest) {
      C.warn(
        `Upload: Socket creation failed, because max sockets reached: ${this.maxRequest} and index: ${index} Length of sockets: ${this.sockets.length}, isRetrySocket: ${retrySocket}`
      );
      return;
    }
    // Log socket open called
    this.logActionAndDebug("socket", "Socket open called", {
      socketIndex: index,
      ...(retrySocket && { isRetrySocket: true }),
    });

    C.log(
      `Upload: Socket creation started, index: ${index}, length of sockets: ${this.sockets.length}, isRetrySocket: ${retrySocket}`
    );
    const { hostname, port, protocol } = location;
    const ws_protocol = protocol.startsWith("https") ? "wss" : "ws";
    const { id: fileId, reUploadId } = this.streamingFileInfo;
    C.time(`Upload: Creating socket ${index}`);
    const openStartTime = Date.now();
    const socket = new WebSocket(
      `${ws_protocol}://${hostname}:${port}/U/${reUploadId || fileId}`
    );
    socket.binaryType = "arraybuffer";
    if (!retrySocket) {
      this.sockets.push(socket);
    } else {
      this.retrySocket = socket;
      socket.isRetrySocket = true;
    }
    socket.chunksBeingSent = [];
    socket.chunksAckd = [];
    socket.index = index || this.sockets.length - 1;

    socket.onopen = () => {
      if (this.isPaused || this.uploadDone || this.fileClosed) {
        socket.close();
        return;
      }
      C.timeEnd(`Upload: Creating socket ${index}`);
      const openEndTime = Date.now();
      socket.timeTakenToOpen = openEndTime - openStartTime;

      // Log socket opened
      this.logActionAndDebug("socket", "Socket opened", {
        socketIndex: socket.index,
        timeMs: socket.timeTakenToOpen,
      });

      // Add to open sockets array when socket opens
      this.addToOpenSockets(socket);

      cb(socket);
      if (this.showServerWriteProgress) {
        this.uploadSummary.socketInfo.push({
          index: socket.index,
          timeTakenToOpen: socket.timeTakenToOpen,
          chunksSent: 0,
        });
      }
    };

    // Use consolidated socket event handler setup
    this.setupSocketEventHandlers(socket);
  };

  initializeSockets = (cb = () => {}) => {
    C.success(
      `Upload: Initializing sockets, creating ${this.maxRequest} sockets`
    );
    C.success(`Upload: Default Chunk size: ${this.defaultChunkSize}`);
    C.success(`Upload: Server write buffer size: ${this.serverWriteThreshold}`);
    const totalSockets = this.sockets.length || 0;
    const socketsToCreate = this.maxRequest - totalSockets + 1;

    if (socketsToCreate <= 0) {
      cb();
      return;
    }

    if (this.waitForAllSocketBeforeStart) {
      let createdSockets = 0;
      const intervalId = setInterval(() => {
        if (createdSockets < socketsToCreate) {
          createdSockets++;
          this.createSocket(
            () => {
              if (createdSockets === socketsToCreate) {
                clearInterval(intervalId);
                cb();
              }
            },
            socketsToCreate - createdSockets === 0,
            createdSockets - 1
          );
        } else {
          clearInterval(intervalId);
        }
      }, 1);
    } else {
      Array.from({ length: socketsToCreate }, () => {
        this.createSocket(cb);
      });
    }
  };

  findMostIdleSocket = (isRetry) => {
    if (isRetry) {
      if (this.isSocketOpen(this.retrySocket)) {
        const emptySockets = this.sockets.filter(
          (s) =>
            this.isSocketOpen(s) &&
            s.bufferedAmount < 5 * this.MB &&
            s.chunksBeingSent.length < 5
        );

        if (this.retrySocket.bufferedAmount === 0) {
          if (this.shouldLog()) {
            C.log(
              `Upload: Its a retry and retry socket is empty, using retry socket`
            );
          }
          return { socket: this.retrySocket, emptySockets };
        } else if (emptySockets.length > 0) {
          if (this.shouldLog()) {
            C.log(
              `Upload: Its a retry, retry socket is non-empty, using an empty socket`
            );
          }
          return {
            socket: emptySockets[0],
            emptySockets: emptySockets.map((s) => ({
              socket: s,
              index: s.index,
            })),
          };
        } else {
          if (this.shouldLog()) {
            C.log(
              `Upload: Its a retry, using non-empty retry socket as no empty sockets available`
            );
          }
          if (this.retrySocket.chunksBeingSent.length < 5) {
            return { socket: this.retrySocket };
          }
        }
      } else {
        C.warn(
          `Upload: Retry socket is not available or not open, finding idle socket`
        );
        return this.findMostIdleSocket();
      }
    }
    let mostIdleSocket = null;
    const emptySockets = [];
    if (typeof this.lastUsedSocket !== "number") {
      this.lastUsedSocket = -1;
    } else if (this.lastUsedSocket >= this.sockets.length - 1) {
      this.lastUsedSocket = -1;
    }
    this.sockets.forEach((socket, index) => {
      if (
        index > this.lastUsedSocket &&
        this.isSocketOpen(socket) &&
        !socket.renewed &&
        !socket.isStuck
      ) {
        if (socket.bufferedAmount === 0 && socket.chunksBeingSent.length < 1) {
          emptySockets.push({ socket, index });
        }
        if (
          !mostIdleSocket ||
          (socket.bufferedAmount < mostIdleSocket.bufferedAmount &&
            socket.chunksBeingSent.length <
              mostIdleSocket.chunksBeingSent.length)
        ) {
          mostIdleSocket = socket;
        }
      }
    });
    return { socket: mostIdleSocket, emptySockets };
  };

  // Helper method to check queue health for all sockets (called periodically)
  checkAllQueuesHealth = () => {
    if (!this.chunksInfo?.chunks) return;

    // Check all sockets
    this.sockets.forEach((socket) => {
      if (socket.chunksBeingSent && socket.chunksBeingSent.length > 0) {
        // Check socket queue depth
        this.checkQueueHealth({ socket });

        // Check each queued chunk on this socket
        socket.chunksBeingSent.forEach((chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          if (chunk && chunk.sent_at === null) {
            this.checkQueueHealth({ chunk, socket });
          }
        });
      }
    });

    // Check retry socket
    if (
      this.retrySocket &&
      this.retrySocket.chunksBeingSent &&
      this.retrySocket.chunksBeingSent.length > 0
    ) {
      this.checkQueueHealth({ socket: this.retrySocket });
      this.retrySocket.chunksBeingSent.forEach((chunkIndex) => {
        const chunk = this.chunksInfo.chunks[chunkIndex];
        if (chunk && chunk.sent_at === null) {
          this.checkQueueHealth({ chunk, socket: this.retrySocket });
        }
      });
    }

    // Check average wait time (less frequently to avoid performance impact)
    this.checkQueueHealth({ checkAll: true });
  };

  // Helper method to update sent_at for next chunk in socket queue when previous chunk is ACKed
  updateNextChunkSentTime = (socket, ackedChunkIndex) => {
    if (
      !socket ||
      !socket.chunksBeingSent ||
      socket.chunksBeingSent.length === 0
    ) {
      return;
    }

    // Find the next chunk in the queue (first chunk in chunksBeingSent after removing ACKed one)
    // Note: The ACKed chunk is already removed from chunksBeingSent in processAck before this is called
    const nextChunkIndex = socket.chunksBeingSent[0];
    if (nextChunkIndex === undefined) {
      return;
    }

    const nextChunk = this.chunksInfo.chunks[nextChunkIndex];
    if (nextChunk && nextChunk.sent_at === null) {
      // This chunk was queued and is now the first in line, set its sent_at
      // Calculate wait time before setting sent_at
      const currentTime = Date.now();
      const waitTime = nextChunk.init_time
        ? currentTime - nextChunk.init_time
        : 0;
      const waitTimeFormatted =
        waitTime < 1000
          ? `${Math.round(waitTime)}ms`
          : `${(waitTime / 1000).toFixed(2)}s`;

      // Set sent_at to current time (when chunk is actually being sent)
      // This is when the previous chunk was ACKed, so this chunk can now be sent
      nextChunk.sent_at = currentTime;

      if (this.shouldLog()) {
        C.log(
          `Upload: Chunk ${nextChunkIndex} queued->inflight on socket ${socket.index} (chunk ${ackedChunkIndex} ACKed)`
        );
      }

      // Detailed console log for chunk transitioning from queued to inflight
      const sizeMB = this.getChunkSizeMB(nextChunk);
      this.logToConsoleAndDebug(
        `[INFLIGHT] Chunk ${nextChunkIndex} - Size: ${sizeMB}, Socket: ${
          socket.index
        }, Init: ${this.formatSimpleTime(
          nextChunk.init_time
        )}, Start: ${this.formatSimpleTime(
          nextChunk.sent_at
        )}, Wait: ${waitTimeFormatted}, Last ACK: ${ackedChunkIndex}`
      );
    }
  };

  isChunkEligibleToResend = (chunkIndex) => {
    const chunk = this.chunksInfo.chunks[chunkIndex];
    if (!chunk) return false;
    // Skip chunks that are still queued (sent_at is null) - they haven't actually been sent yet
    if (chunk.sent_at === null) return false;
    return chunk.status !== "done" &&
      chunk.status !== "failed" &&
      Date.now() - chunk.sent_at >
        (chunk.retries ? (chunk.retries + 2) * 60000 : 120000)
      ? chunk
      : undefined;
  };

  findChunksToResend = (socket) => {
    const { chunksSent, chunksAckd } = this;
    if (!chunksSent.length) return null;

    // Get current time once
    const now = Date.now();

    // Use cached unacknowledged chunks if available and fresh
    let unackdChunks = this.cachedUnackdChunks;
    if (
      !unackdChunks ||
      now - this.cachedUnackdChunksTime > this.cachedUnackdChunksTimeout
    ) {
      // Create Set for O(1) lookups of acknowledged chunks
      const ackdSet = new Set(chunksAckd);

      // Sort chunks by sent_at time to prioritize oldest chunks
      // Filter out chunks with sent_at === null (queued chunks) before sorting for efficiency
      unackdChunks = chunksSent
        .filter((chunk) => !ackdSet.has(chunk))
        .map((chunk) => this.chunksInfo.chunks[chunk])
        .filter(
          (chunk) =>
            chunk &&
            chunk.status !== "done" &&
            chunk.status !== "failed" &&
            chunk.sent_at !== null
        )
        .sort((a, b) => a.sent_at - b.sent_at);

      // Cache the result
      this.cachedUnackdChunks = unackdChunks;
      this.cachedUnackdChunksTime = now;
    }

    // Calculate upload progress once outside the loop (same for all chunks)
    const uploadProgress = this.streamingFileInfo?.size
      ? (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) * 100
      : 0;

    // Find first eligible chunk
    // Note: Chunks with sent_at === null are already filtered out above, so this check is redundant but kept for safety
    for (const chunk of unackdChunks) {
      // Skip chunks that are still queued (sent_at is null) - they haven't actually been sent yet
      if (chunk.sent_at === null) {
        continue;
      }
      const timeSinceSent = now - chunk.sent_at;
      let retryThreshold;
      const avgUploadTime = this.uploadSummary?.averageChunkUploadTime || 0;
      const baseThreshold = avgUploadTime
        ? Math.min(avgUploadTime * 1000 * 10, 30000)
        : 45000; // 2x avg time or 30s min, fallback to 45s

      if (chunk.retries === 0) {
        retryThreshold = baseThreshold; // Use calculated threshold for first retry
      } else {
        // Increase by 50% of base threshold each retry, capped at 2x base threshold
        const increment = Math.max(baseThreshold * 0.5, 15000); // 50% of base or 15s min
        retryThreshold = Math.min(
          baseThreshold + chunk.retries * increment,
          baseThreshold * 2
        );
      }

      if (timeSinceSent > retryThreshold) {
        this.checkChunkACKWithServer(parseInt(chunk.index), socket);

        this.totalChunkResendAttempts++;

        // Log chunk resend attempt event
        this.logChunkEvent(chunk.index, "CHUNK_RESEND_ATTEMPT", {
          timeSinceSent: Math.floor(timeSinceSent / 1000),
          retryThreshold: Math.floor(retryThreshold / 1000),
          retries: chunk.retries > 0 ? chunk.retries : 1,
          socketIndex: socket?.index || -1,
          status: chunk.status,
          uploadProgress: uploadProgress.toFixed(1),
        });

        const { initTimeStr, sentAtStr, waitTime } =
          this.formatChunkTimeStrings(chunk);
        C.log(
          `Upload: Found a chunk to resend: ${
            chunk.index
          }, Size: ${this.formatChunkSizeMB(chunk.size)}, ` +
            `Init time: ${initTimeStr}, ` +
            `Sent at: ${sentAtStr}, ` +
            `Wait time: ${waitTime}, ` +
            `its not Acked since ${Math.floor(
              timeSinceSent / 1000
            )} seconds. This is its retries: ${
              chunk.retries > 0 ? chunk.retries : 1
            }, threshold: ${Math.floor(
              retryThreshold / 1000
            )}s, progress: ${uploadProgress.toFixed(1)}%`
        );
        return chunk;
      }
    }

    return null;
  };

  finishedUpload = async () => {
    // Guard against multiple calls
    if (this.uploadDone) {
      C.warn(`Upload: finishedUpload called multiple times, ignoring`);
      return;
    }

    // Mark upload as done FIRST to stop message processing
    this.uploadDone = true;
    this.uploadEndTime = Date.now();

    // Close all sockets immediately to stop receiving messages
    this.closeSockets();
    if (this.shouldLog()) {
      C.log(
        `Upload: Closed ${this.sockets.length} socket(s) after upload completion`
      );
    }

    // Log upload completion
    this.logActionAndDebug("upload", "Upload finished", {
      duration: this.uploadEndTime - this.uploadStartTime,
      chunks: this.chunksInfo.chunks.length,
    });

    // Set 100% progress when upload is truly complete
    const file = this.streamingFileInfo;
    const { id: fileId } = file;
    store.dispatch("updateFileUploadStatus", {
      fileId,
      size: file.size, // Show actual file size (100%)
      maxSize: file.size,
    });

    // Close file stream FIRST (ensure it's called and awaited) before generating debug info
    if (this.streamingFileInfo) {
      await this.closeFileStream(this.streamingFileInfo);
    } else {
      // Log if file info is missing
      C.logAction(
        "file",
        "Close file called",
        {
          error: "streamingFileInfo is null/undefined",
        },
        true
      );
    }

    // Capture debug info BEFORE clearing any data structures
    const finalDebugInfo = this.formatDebugInfo();

    // Clear debugLogs to prevent memory leak after capturing final debug info
    if (this.debugLogs) {
      this.debugLogs = [];
    }

    this.logToConsoleAndDebug(
      `Finished upload for file ${fileId}, capturing final debug info: ${JSON.stringify(
        {
          fileExists: !!store.fileList[fileId],
          debugInfoLength: finalDebugInfo.length,
          storeFileList: store.fileList[fileId],
        }
      )}`
    );

    // Ensure the file exists in the store, create it if it doesn't
    if (!store.fileList[fileId]) {
      this.warnToConsoleAndDebug(
        `File ${fileId} not found in store.fileList when finishing upload, creating entry`
      );
      store.fileList[fileId] = {
        id: fileId,
        nameF: this.streamingFileInfo?.nameF || "Unknown",
        status: "done",
      };
    }

    // Store debug info in the file item in store to preserve it permanently
    store.fileList[fileId] = {
      ...store.fileList[fileId],
      debugInfo: finalDebugInfo,
      debugInfoCapturedAt: new Date().toISOString(),
    };
    this.logToConsoleAndDebug(`Final debug info stored for file ${fileId}`);

    // Also dispatch to update the store
    store.dispatch("updateFileDebugInfo", {
      fileId,
      debugInfo: finalDebugInfo,
    });

    // Log debug info when upload is completed (AFTER close file so logs are included)
    if (this.showServerWriteProgress) {
      this.logToConsoleAndDebug("=== UPLOAD COMPLETION DEBUG INFO ===");
      this.logToConsoleAndDebug(finalDebugInfo);
      this.logToConsoleAndDebug("=====================================");
    }
    // Sockets already closed at the start of finishedUpload, just clear arrays
    this.sockets = [];
    this.openSockets = []; // Clear open sockets array
    this.stopAllIntervals();
    this.stopZeroByteChunkSending(); // Stop zero-byte chunk sending on completion

    // Additional cleanup to ensure no intervals are running
    this.cleanupAllTimers();

    // Critical fix: Clear chunk data to free memory after file upload completes
    // With 100+ files, accumulated chunk data can cause browser crashes
    // NOTE: Debug info has already been captured and stored above
    if (this.chunksInfo && this.chunksInfo.chunks) {
      // Clear chunk buffers to free memory
      this.chunksInfo.chunks.forEach((chunk) => {
        chunk.chunkData = null;
        chunk.buffer = null;
      });
      // Clear the chunks array itself
      this.chunksInfo.chunks = [];
    }
    // Clear other arrays that might hold references
    this.chunksSent = [];
    this.chunksAckd = new Set(); // Use Set to prevent duplicate ACKs
    this.importantLogs = [];
    this.cachedUnackdChunks = null;
  };

  checkChunkACKWithServer = (chunkNum, socket) => {
    if (this.chunksAckd.has(chunkNum)) {
      this.updateStore();
      return;
    }
    if (this.pendingAckChecks && this.pendingAckChecks.has(chunkNum)) {
      return;
    }
    if (!this.pendingAckChecks) {
      this.pendingAckChecks = new Set();
    }
    this.pendingAckChecks.add(chunkNum);

    // Set ackVerifying flag on the chunk to prevent it from appearing in recovery info
    const chunk = this.chunksInfo.chunks[chunkNum];
    if (chunk) {
      chunk.ackVerifying = true;
    }

    if (this.shouldLog()) {
      C.log(
        `Upload: Initiating chunk ${chunkNum} acknowledgment check (pending: ${this.pendingAckChecks.size})`
      );
    }
    this.checkChunkAck(chunkNum)
      .then((isAcknowledged) => {
        if (isAcknowledged) {
          this.processAck(chunkNum, socket);
        }
        this.pendingAckChecks.delete(chunkNum);
        // Clear ackVerifying flag when verification is complete
        if (chunk) {
          chunk.ackVerifying = false;
        }
      })
      .catch((error) => {
        C.warn(
          `Upload: Error checking chunk ${chunkNum} acknowledgment: ${error.message}`
        );
        this.pendingAckChecks.delete(chunkNum);
        // Clear ackVerifying flag when verification fails
        if (chunk) {
          chunk.ackVerifying = false;
        }
      });
  };

  logImportantEvent = (eventType, data) => {
    if (!this.showServerWriteProgress) return;

    const logEntry = {
      eventType,
      data,
      timestamp: new Date().toISOString(),
    };

    // Keep only last 200 important logs to prevent memory issues
    if (this.importantLogs.length >= 200) {
      this.importantLogs.shift(); // Remove oldest entry
    }

    this.importantLogs.push(logEntry);
  };

  // ========== DEBUG INFO HELPER METHODS ==========

  // Format time difference from now
  formatTimeDifference = (timestamp) => {
    if (!timestamp) return "N/A";
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 1) return "just now";
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return "1 minute ago";
    return `${minutes} minutes ago`;
  };

  // Format duration between two timestamps
  formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";
    const diffMs = endTime - startTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m ${diffSeconds % 60}s`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ${diffSeconds % 60}s`;
    } else {
      return `${diffSeconds}s`;
    }
  };

  // Get upload status string
  getUploadStatusString = () => {
    if (this.isPaused) return "Paused";
    if (this.fileClosed) return "Closed";
    if (this.uploadDone) return "Done";
    return "Uploading";
  };

  // Get file size category string
  getFileSizeCategory = (fileSize) => {
    if (this.isVeryLargeFile(fileSize)) return "Very Large (>10GB)";
    if (this.isLargeFile(fileSize)) return "Large (>1GB)";
    return "Normal (<1GB)";
  };

  // Get recovery information
  getRecoveryInformation = () => {
    const recoveryReasons = [];
    if (!this.isPaused && !this.fileClosed && !this.uploadDone) {
      const stuckSockets = this.sockets.filter((socket) => socket.isStuck);
      if (stuckSockets.length > 0) {
        recoveryReasons.push(
          `Stuck sockets: ${stuckSockets.map((s) => s.index).join(", ")}`
        );
      }

      const retryingChunks = this.chunksInfo.chunks.filter(
        (chunk) =>
          chunk.status !== "done" && chunk.retries > 0 && !chunk.ackVerifying
      );
      if (retryingChunks.length > 0) {
        const chunkDetails = retryingChunks
          .map((chunk) => `${chunk.index} (${chunk.retries})`)
          .join(", ");
        recoveryReasons.push(`Retrying chunks: ${chunkDetails}`);
      }
    }
    return recoveryReasons;
  };

  // Format chunk time information
  formatChunkTimeInfo = (chunk) => {
    let initTime = "N/A";
    if (chunk.init_time) {
      const initDate = new Date(chunk.init_time);
      initTime = initDate.toLocaleTimeString();
    }

    let sentTime = "N/A";
    if (chunk.sent_at && chunk.sent_at !== null) {
      const sentDate = new Date(chunk.sent_at);
      sentTime = sentDate.toLocaleTimeString();
    } else if (chunk.sent_at === null) {
      sentTime = "Queued";
    }

    // Calculate wait time (difference between init_time and sent_at)
    const waitTime = this.formatWaitTime(chunk.init_time, chunk.sent_at);

    let ackTime = "N/A";
    let duration = "N/A";
    if (this.chunksAckd.has(chunk.index)) {
      if (chunk.sent_at && chunk.timeTaken) {
        const ackDate = new Date(chunk.sent_at + chunk.timeTaken * 1000);
        if (!isNaN(ackDate.getTime())) {
          ackTime = ackDate.toLocaleTimeString();
        }
      }

      if (chunk.timeTaken) {
        const minutes = Math.floor(chunk.timeTaken / 60);
        const seconds = Math.floor(chunk.timeTaken % 60);
        duration = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
      }
    }

    return { initTime, sentTime, waitTime, ackTime, duration };
  };

  // Get chunk status indicator
  getChunkStatusIndicator = (status) => {
    const indicators = {
      done: "✓",
      uploading: "→",
      retry: "↻",
      init: "○",
      failed: "✗",
    };
    return indicators[status] || "";
  };

  // Format chunk for debug output
  formatChunkForDebug = (chunk, options = {}) => {
    const { includeHighlight = true, includeDetails = true } = options;
    const { initTime, sentTime, waitTime, ackTime, duration } =
      this.formatChunkTimeInfo(chunk);
    const statusIndicator = this.getChunkStatusIndicator(chunk.status);
    const retries = chunk.retries || 0;
    const retryIndicator =
      retries > 0 ? ` (${retries} retry${retries > 1 ? "ies" : "y"})` : "";

    let highlight = "";
    if (includeHighlight) {
      if (chunk.status === "retry") {
        highlight = "*** ";
      } else if (
        chunk.status === "uploading" &&
        chunk.sent_at &&
        chunk.sent_at !== null
      ) {
        const timeSinceSent = (Date.now() - chunk.sent_at) / 1000;
        if (timeSinceSent > 60) highlight = "*** ";
      }
    }

    const line = `${highlight}[${chunk.index
      .toString()
      .padStart(4)}] ${statusIndicator} ${
      chunk.status
    } | Init: ${initTime} | Sent: ${sentTime} | Wait: ${waitTime} | Ack: ${ackTime} | Duration: ${duration}${retryIndicator}`;

    const details = [];
    if (includeDetails && highlight) {
      if (chunk.status === "retry") {
        details.push(`    ↳ Chunk marked for retry - needs resending`);
      } else if (
        chunk.status === "uploading" &&
        chunk.sent_at &&
        chunk.sent_at !== null
      ) {
        const timeSinceSent = Math.floor((Date.now() - chunk.sent_at) / 1000);
        const waitTime = chunk.init_time
          ? this.formatWaitTime(chunk.init_time, chunk.sent_at)
          : "N/A";
        details.push(
          `    ↳ Chunk sent ${timeSinceSent}s ago but not acknowledged, waited ${waitTime} in queue`
        );
      } else if (chunk.status === "uploading" && chunk.sent_at === null) {
        const waitTime = chunk.init_time
          ? this.formatWaitTime(chunk.init_time, null)
          : "N/A";
        details.push(
          `    ↳ Chunk is queued in socket buffer, waiting to be sent (waited ${waitTime} so far)`
        );
      }
    }

    return { line, details };
  };

  formatImportantLogs = () => {
    if (!this.showServerWriteProgress) {
      return ["Debug logging disabled (showServerWriteProgress is false)"];
    }

    if (this.importantLogs.length === 0) {
      return ["No important logs recorded"];
    }

    const formattedLogs = [];
    const allLogs = this.importantLogs; // Show all logs

    allLogs.forEach((log, index) => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      const { eventType, data } = log;

      switch (eventType) {
        case "XHR_ACK_CHECK":
          formattedLogs.push(
            `[${time}] Chunk ${data.chunkNum} -> ${data.response} (${
              data.isAcknowledged ? "ACK" : "NOT_ACK"
            }) - ${data.duration}ms`
          );
          break;
        case "XHR_ACK_CHECK_ERROR":
          formattedLogs.push(
            `[${time}] Chunk ${data.chunkNum} -> ERROR: ${data.error} - ${data.duration}ms`
          );
          break;
        case "CLOSE_FILE":
          formattedLogs.push(
            `[${time}] Close File -> ${data.response} (${data.totalChunks} chunks) - ${data.duration}ms`
          );
          break;
        case "CLOSE_FILE_RETRY":
          formattedLogs.push(
            `[${time}] Close File Retry #${data.retryCount} -> ${data.response} - ${data.duration}ms`
          );
          break;
        case "CLOSE_FILE_ERROR":
          formattedLogs.push(
            `[${time}] Close File Error -> ${data.error} (retry #${data.retryCount})`
          );
          break;
        case "CLOSE_FILE_RETRY_ERROR":
          formattedLogs.push(
            `[${time}] Close File Retry Error #${data.retryCount} -> ${data.error}`
          );
          break;
        case "CHUNK_SIZE_CHANGED":
          formattedLogs.push(
            `[${time}] Chunk Size ${
              data.direction
            }: ${data.previousSizeMB.toFixed(2)}MB → ${data.newSizeMB.toFixed(
              2
            )}MB (${data.changePercentage}) | ACKs: ${
              data.ackedCount
            }, avgTime: ${data.avgTimeSec.toFixed(2)}s, reason: ${
              data.changeReason
            }`
          );
          break;
        default:
          formattedLogs.push(`[${time}] ${eventType}: ${JSON.stringify(data)}`);
      }
    });

    return formattedLogs;
  };

  checkChunkAck = async (chunkNum, retryCount = 0) => {
    const apiChunkNum = chunkNum + 1;
    const uploadId =
      this.streamingFileInfo?.reUploadId || this.streamingFileInfo?.id;
    const startTime = Date.now();

    // Track XHR statistics
    if (this.showServerWriteProgress) {
      this.uploadSummary.xhrStats.totalCalls++;
    }

    // Log XHR check called
    this.logActionAndDebug("chunk", "XHR check called", {
      chunk: chunkNum,
      ...(retryCount > 0 && { retry: retryCount }),
    });

    if (this.shouldLog()) {
      C.log(
        `Upload: Checking chunk ${chunkNum} (API: ${apiChunkNum}) acknowledgment for upload ${uploadId}${
          retryCount > 0 ? ` (retry ${retryCount})` : ""
        }`
      );
    }

    try {
      const response = await chunkAckXHR({
        transfer_type: "upload",
        chunk_num: apiChunkNum.toString(),
        upload_id: uploadId,
      });

      const endTime = Date.now();
      const duration = endTime - startTime;
      const isAcknowledged =
        response.trim().toLowerCase() === "ok" ||
        response.trim() === "1" ||
        response.trim() === "true";

      // Track successful XHR call
      if (this.showServerWriteProgress) {
        this.uploadSummary.xhrStats.successfulCalls++;
        this.uploadSummary.xhrStats.totalResponseTime += duration;
        this.uploadSummary.xhrStats.averageResponseTime =
          this.uploadSummary.xhrStats.totalResponseTime /
          this.uploadSummary.xhrStats.successfulCalls;
      }

      // Log XHR acknowledgment check event
      this.logChunkEvent(chunkNum, "XHR_ACK_CHECK_SUCCESS", {
        apiChunkNum,
        uploadId,
        response: response.trim(),
        isAcknowledged,
        duration,
        retryCount,
      });

      // Log important XHR acknowledgment check
      this.logImportantEvent("XHR_ACK_CHECK", {
        chunkNum,
        apiChunkNum,
        uploadId,
        response: response.trim(),
        isAcknowledged,
        duration,
        retryCount,
        timestamp: new Date().toISOString(),
      });

      if (this.shouldLog()) {
        C.log(
          `Upload: Chunk ${chunkNum} (API: ${apiChunkNum}) acknowledgment check result: ${response.trim()} -> ${
            isAcknowledged ? "ACKNOWLEDGED" : "NOT ACKNOWLEDGED"
          } (${duration}ms)${retryCount > 0 ? ` (retry ${retryCount})` : ""}`
        );
      }

      return isAcknowledged;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      const errorType = this.categorizeXHRError(error);

      // Track failed XHR call
      if (this.showServerWriteProgress) {
        this.uploadSummary.xhrStats.failedCalls++;
        this.uploadSummary.xhrStats.failureReasons[errorType] =
          (this.uploadSummary.xhrStats.failureReasons[errorType] || 0) + 1;
      }

      // Log XHR failure event
      this.logChunkEvent(chunkNum, "XHR_ACK_CHECK_FAILURE", {
        apiChunkNum,
        uploadId,
        error: error.message,
        errorType,
        duration,
        retryCount,
      });

      // Log important XHR acknowledgment check error
      this.logImportantEvent("XHR_ACK_CHECK_ERROR", {
        chunkNum,
        apiChunkNum,
        uploadId,
        error: error.message,
        duration,
        retryCount,
        errorType,
        timestamp: new Date().toISOString(),
      });

      if (this.shouldLog()) {
        C.warn(
          `Upload: Failed to check chunk ${chunkNum} (API: ${apiChunkNum}) acknowledgment: ${
            error.message
          } (${duration}ms)${retryCount > 0 ? ` (retry ${retryCount})` : ""}`
        );
      }

      // XHR retry mechanism
      if (retryCount < 5 && this.shouldRetryXHR(error)) {
        if (this.showServerWriteProgress) {
          this.uploadSummary.xhrStats.retryAttempts++;
        }
        if (this.shouldLog()) {
          C.log(
            `Upload: Retrying XHR call for chunk ${chunkNum} in 2 seconds...`
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
        return this.checkChunkAck(chunkNum, retryCount + 1);
      } else {
        C.log(
          `Upload: Failed to check chunk ${chunkNum} (API: ${apiChunkNum}) acknowledgment: ${
            error.message
          } (${duration}ms)${retryCount > 0 ? ` (retry ${retryCount})` : ""}`
        );
      }
      return false;
    }
  };

  // Categorize XHR errors for better statistics
  categorizeXHRError = (error) => {
    const message = error.message.toLowerCase();
    if (message.includes("timeout")) return "timeout";
    if (message.includes("network") || message.includes("connection"))
      return "network";
    if (message.includes("404") || message.includes("not found"))
      return "not_found";
    if (message.includes("500") || message.includes("server error"))
      return "server_error";
    if (message.includes("403") || message.includes("forbidden"))
      return "forbidden";
    if (message.includes("401") || message.includes("unauthorized"))
      return "unauthorized";
    return "unknown";
  };

  // Determine if XHR should be retried based on error type
  shouldRetryXHR = (error) => {
    const errorType = this.categorizeXHRError(error);
    // Retry for network issues and timeouts, but not for auth or server errors
    return ["timeout", "network", "unknown"].includes(errorType);
  };

  // Log chunk events for detailed tracking
  logChunkEvent = (chunkIndex, eventType, details = {}) => {
    if (!this.showServerWriteProgress) return;

    const event = {
      chunkIndex,
      eventType,
      timestamp: Date.now(),
      details,
    };

    // Ensure chunkEvents array exists
    if (!this.uploadSummary.chunkEvents) {
      this.uploadSummary.chunkEvents = [];
    }

    this.uploadSummary.chunkEvents.push(event);

    // Only log essential chunk events with minimal details
    const essentialEvents = [
      "CHUNK_INITIAL_SENT",
      "CHUNK_ACKNOWLEDGED",
      "CHUNK_RETRY_SENT",
      "XHR_ACK_CHECK_SUCCESS",
      "XHR_ACK_CHECK_FAILURE",
      "SERVER_ERROR_RETRY",
    ];

    if (essentialEvents.includes(eventType)) {
      // Map event types to simple messages
      const eventMessages = {
        CHUNK_INITIAL_SENT: "Chunk sent",
        CHUNK_ACKNOWLEDGED: "Chunk ackd",
        CHUNK_RETRY_SENT: "Chunk retried",
        XHR_ACK_CHECK_SUCCESS: "XHR check success",
        XHR_ACK_CHECK_FAILURE: "XHR check failed",
        SERVER_ERROR_RETRY: "Chunk failed",
      };

      // Build minimal context
      const minimalContext = {
        chunk: chunkIndex,
      };

      // Add socket index if available
      if (details.socketIndex !== undefined) {
        minimalContext.socket = details.socketIndex;
      }

      // Add chunk size (in MB) if available
      if (details.chunkSize !== undefined && details.chunkSize !== null) {
        minimalContext.chunkSizeMB = this.formatChunkSizeMB(details.chunkSize);
        minimalContext.chunkSize = details.chunkSize; // Also include raw size in bytes
      }

      // For CHUNK_ACKNOWLEDGED, add upload time and retry info
      if (eventType === "CHUNK_ACKNOWLEDGED") {
        // Format upload time (timeSinceSent is in milliseconds)
        if (
          details.timeSinceSent !== null &&
          details.timeSinceSent !== undefined
        ) {
          const timeMs = details.timeSinceSent;
          let formattedTime;
          if (timeMs < 1000) {
            // For very fast uploads, show milliseconds
            formattedTime = `${Math.round(timeMs)}ms`;
          } else if (timeMs < 60000) {
            // For fast uploads (< 1 minute), show seconds with decimals
            formattedTime = `${(timeMs / 1000).toFixed(2)}s`;
          } else {
            // For longer uploads, use utils.formatTime for consistency
            const timeInSeconds = timeMs / 1000;
            formattedTime = utils.formatTime({
              secs: timeInSeconds,
              minLabel: "m",
              secondsLabel: "s",
            });
          }
          minimalContext.uploadTime = formattedTime;
        }

        // Always include retry information (even if 0, to show it wasn't retried)
        const retryCount = details.retries || details.newRetries || 0;
        minimalContext.retries = retryCount;
        if (retryCount > 0) {
          minimalContext.wasRetried = true;
        } else {
          minimalContext.wasRetried = false;
        }
      } else {
        // For other events, add retries only if > 0
        if (details.retries > 0 || details.newRetries > 0) {
          minimalContext.retries = details.retries || details.newRetries;
        }
      }

      // Add error info for failures
      if (
        eventType === "XHR_ACK_CHECK_FAILURE" ||
        eventType === "SERVER_ERROR_RETRY"
      ) {
        if (details.error || details.errorMsg) {
          minimalContext.error = details.error || details.errorMsg;
        }
      }

      const actionMessage = eventMessages[eventType] || eventType;

      // Log to console and capture to debug logs
      this.logActionAndDebug("chunk", actionMessage, minimalContext);
    }

    // Log chunk event details (console only, not in debug logs)
    if (this.shouldLog()) {
      this.logToConsoleAndDebug(
        `Upload: Chunk ${chunkIndex} - ${eventType}: ${JSON.stringify(details)}`
      );
    }
  };

  // Common method to format chunk events summary
  formatChunkEventsSummary = (outputType = "lines") => {
    if (!this.showServerWriteProgress) {
      return outputType === "lines"
        ? ["Debug logging disabled (showServerWriteProgress is false)"]
        : ["Debug logging disabled (showServerWriteProgress is false)\n"];
    }

    const result = [];

    // Header
    if (outputType === "lines") {
      result.push("");
      result.push("=== CHUNK EVENTS SUMMARY ===");
    } else {
      result.push(`\n=== CHUNK EVENTS SUMMARY ===\n`);
    }

    // Basic info
    const totalEvents = this.uploadSummary.chunkEvents?.length || 0;

    if (outputType === "lines") {
      result.push(`Total chunk events: ${totalEvents}`);
    } else {
      result.push(`Total chunk events: ${totalEvents}\n`);
    }

    // Check if chunkEvents exists and has data
    if (
      this.uploadSummary.chunkEvents &&
      this.uploadSummary.chunkEvents.length > 0
    ) {
      // Group events by type
      const eventCounts = {};
      this.uploadSummary.chunkEvents.forEach((event) => {
        eventCounts[event.eventType] = (eventCounts[event.eventType] || 0) + 1;
      });

      if (outputType === "lines") {
        result.push("");
        result.push("Event counts:");
        Object.entries(eventCounts).forEach(([eventType, count]) => {
          result.push(`${eventType}: ${count}`);
        });
      } else {
        result.push(`\n`);
        Object.entries(eventCounts).forEach(([eventType, count]) => {
          result.push(`${eventType}: ${count}\n`);
        });
      }

      // Show detailed chunk events for chunks with multiple events
      const chunkEventCounts = {};
      this.uploadSummary.chunkEvents.forEach((event) => {
        chunkEventCounts[event.chunkIndex] =
          (chunkEventCounts[event.chunkIndex] || 0) + 1;
      });

      // Only show chunks that had problematic events (not just normal flow)
      const eventfulChunks = Object.entries(chunkEventCounts)
        .filter(([chunkIndex, count]) => {
          const chunkEvents = this.uploadSummary.chunkEvents.filter(
            (event) => event.chunkIndex == chunkIndex
          );

          // Check if chunk had any problematic events
          const hasProblematicEvents = chunkEvents.some(
            (event) =>
              event.eventType === "CHUNK_RETRY_SENT" ||
              event.eventType === "CHUNK_RESEND_ATTEMPT" ||
              event.eventType === "XHR_ACK_CHECK_SUCCESS" ||
              event.eventType === "XHR_ACK_CHECK_FAILURE" ||
              event.eventType === "SERVER_ERROR_RETRY"
          );

          return hasProblematicEvents;
        })
        .sort((a, b) => b[1] - a[1]); // Show all truly eventful chunks

      if (eventfulChunks.length > 0) {
        if (outputType === "lines") {
          result.push("");
          result.push("Most eventful chunks:");
          eventfulChunks.forEach(([chunkIndex, eventCount]) => {
            const chunkEvents = this.uploadSummary.chunkEvents
              .filter((event) => event.chunkIndex == chunkIndex)
              .sort((a, b) => a.timestamp - b.timestamp);

            result.push(`Chunk ${chunkIndex} (${eventCount} events):`);
            chunkEvents.forEach((event) => {
              const time = new Date(event.timestamp).toLocaleTimeString();
              let eventLog = `  [${time}] ${event.eventType}`;
              if (event.details) {
                const details = Object.entries(event.details)
                  .map(([key, value]) => `${key}=${value}`)
                  .join(", ");
                eventLog += ` (${details})`;
              }
              result.push(eventLog);
            });
          });
        } else {
          result.push(`\nMost eventful chunks:\n`);
          eventfulChunks.forEach(([chunkIndex, eventCount]) => {
            const chunkEvents = this.uploadSummary.chunkEvents
              .filter((event) => event.chunkIndex == chunkIndex)
              .sort((a, b) => a.timestamp - b.timestamp);

            result.push(`Chunk ${chunkIndex} (${eventCount} events):\n`);
            chunkEvents.forEach((event) => {
              const time = new Date(event.timestamp).toLocaleTimeString();
              let eventLog = `  [${time}] ${event.eventType}`;
              if (event.details) {
                const details = Object.entries(event.details)
                  .map(([key, value]) => `${key}=${value}`)
                  .join(", ");
                eventLog += ` (${details})`;
              }
              result.push(eventLog + `\n`);
            });
          });
        }
      } else {
        if (outputType === "lines") {
          result.push("");
          result.push(
            "No eventful chunks found (no chunks had retries, XHR checks, or failures)"
          );
        } else {
          result.push(
            `\nNo eventful chunks found (no chunks had retries, XHR checks, or failures)\n`
          );
        }
      }
    } else {
      if (outputType === "lines") {
        result.push("No chunk events found");
      } else {
        result.push("No chunk events found\n");
      }
    }

    return result;
  };

  stopPeriodicCheck = () => {
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
      this.periodicCheckInterval = null;
    }
  };

  checkSocketPoolHealth = () => {
    // Skip if upload is done, paused, or file is closed
    if (this.isPaused || this.fileClosed || this.uploadDone) {
      return;
    }

    // Count open sockets (sockets that are actually OPEN state)
    const openSocketsCount = this.sockets.filter((socket) =>
      this.isSocketOpen(socket)
    ).length;

    // Check if we need to create replacement sockets
    // Note: createSocket checks this.sockets.length, so we need to account for closed sockets still in array
    const totalSocketsInArray = this.sockets.length;
    const canCreateMore = totalSocketsInArray < this.maxRequest;
    const needMoreOpen = openSocketsCount < this.maxRequest;

    if (needMoreOpen && canCreateMore) {
      const socketsNeeded = Math.min(
        this.maxRequest - openSocketsCount,
        this.maxRequest - totalSocketsInArray
      );

      if (this.shouldLog()) {
        C.log(
          `Upload: Socket pool health check - Open sockets: ${openSocketsCount}/${this.maxRequest}, Total in array: ${totalSocketsInArray}, creating ${socketsNeeded} replacement socket(s)`
        );
      }

      // Create replacement sockets (one at a time to avoid overwhelming)
      for (let i = 0; i < socketsNeeded; i++) {
        this.createSocket(() => {
          this.totalSocketsRespawned++;
          if (this.shouldLog()) {
            C.log(
              `Upload: Socket pool health check - Replacement socket created, starting upload`
            );
          }
          this.sendChunks();
        }, false);
      }
    } else if (needMoreOpen && !canCreateMore) {
      // We need more open sockets but array is at capacity - this means we have closed sockets
      // that haven't been removed. Log this for debugging.
      if (this.shouldLog()) {
        C.warn(
          `Upload: Socket pool health check - Need ${
            this.maxRequest - openSocketsCount
          } more open sockets, but socket array is at capacity (${totalSocketsInArray}). Closed sockets may need cleanup.`
        );
      }
    }
  };

  startPeriodicCheck = () => {
    // Stop any existing interval to prevent duplicates
    this.stopPeriodicCheck();

    const periodicCheck = () => {
      if (this.isPaused || this.fileClosed || this.uploadDone) {
        // Clear the interval if paused, file is closed, or upload is done
        this.stopPeriodicCheck();
        if (this.shouldLog()) {
          this.logToConsoleAndDebug(
            `VVV: Upload: Periodic check stopped - isPaused: ${this.isPaused}, fileClosed: ${this.fileClosed}, uploadDone: ${this.uploadDone}`
          );
        }
        return;
      }
      this.lastPeriodicCheckTime = Date.now();
      this.sendChunks();

      // Additional completion check for large files
      this.checkForCompletionFallback();

      // Check socket pool health and create replacement sockets if needed
      this.checkSocketPoolHealth();

      // Check queue health periodically (warns about deep queues and long waits)
      this.checkAllQueuesHealth();
    };
    this.periodicCheckInterval = setInterval(
      periodicCheck,
      this.PERIODIC_CHECK_INTERVAL_MS
    );
    periodicCheck(); // Run immediately
    this.lastPeriodicCheckTime = Date.now();
  };

  checkForCompletionFallback = () => {
    // Fallback completion check for large files
    if (this.uploadDone || this.fileClosed) return;

    const completionStatus = this.getCompletionStatus();
    const { isVeryLargeFile } = completionStatus;

    // Conservative fallback: only trigger if we're very close to completion and all chunks are done
    if (
      isVeryLargeFile &&
      completionStatus.isAllChunksCreated &&
      completionStatus.totalChunks > 0 &&
      completionStatus.totalChunksUploaded >= completionStatus.totalChunks &&
      completionStatus.bytesUploaded >= completionStatus.fileSize * 0.999 // Within 0.1% of completion
    ) {
      C.warn(
        `Upload: Fallback completion check triggered for large file - all chunks uploaded but completion not detected`
      );
      this.logFallbackCompletionDetails(completionStatus);
      this.finishedUpload();
    }
  };

  handleMessageFromSocket = (event, socket) => {
    // Stop processing messages once upload is complete
    if (this.uploadDone || this.fileClosed) {
      if (this.shouldLog()) {
        C.log(
          `Upload: Ignoring message from socket after upload completion: ${event.data}`
        );
      }
      return;
    }

    if (this.isErrorMessage(event.data)) {
      this.handleErrorMessage(event.data);
      return;
    }
    const chunkIndex = parseInt(event.data) - 1;

    if (this.isDuplicateAck(chunkIndex)) {
      return;
    }
    this.processAck(chunkIndex, socket);
    this.sendChunks();
  };

  isErrorMessage = (data) => {
    return typeof data === "string" && data.toLowerCase().startsWith("error");
  };

  handleErrorMessage = (errorMsg) => {
    // Stop processing errors once upload is complete
    if (this.uploadDone || this.fileClosed) {
      if (this.shouldLog()) {
        C.log(
          `Upload: Ignoring error message after upload completion: ${errorMsg}`
        );
      }
      return;
    }

    if (errorMsg.indexOf("CHUNK_NUM_") >= 0) {
      this.retryChunk(errorMsg);
      return;
    }
    C.error(`Upload: Error from server: ${errorMsg}`);
    this.handleChunkError(errorMsg);
  };

  retryChunk = (errorMsg) => {
    const match = errorMsg.match(/CHUNK_NUM_(\d+)_/);
    const chunkToRetry = match ? match[1] : null;
    if (
      chunkToRetry &&
      !this.isPaused &&
      !this.uploadDone &&
      !this.fileClosed
    ) {
      const chunkIndex = parseInt(chunkToRetry) - 1;
      const chunkData = this.chunksInfo.chunks[chunkIndex];
      const oldRetries = chunkData.retries || 0;
      chunkData.status = "uploading";
      // chunkData.retries = chunkData.retries - 1;
      // Use chunkTimeout (in seconds) to set artificial time for server-requested retry
      // This allows timeout mechanism to trigger retry immediately
      const currentDate = new Date();
      const timeoutMs = (this.chunkTimeout || 30) * 1000;
      const dateMinusTimeout = new Date(currentDate.getTime() - timeoutMs);
      chunkData.sent_at = dateMinusTimeout;
      // Set init_time for consistency (server requested retry, so chunk was initially sent)
      chunkData.init_time = chunkData.init_time || dateMinusTimeout;

      // Log chunk retry event
      this.logChunkEvent(chunkIndex, "SERVER_ERROR_RETRY", {
        errorMsg,
        oldRetries,
        newRetries: chunkData.retries,
        artificialTimeSet: true,
        timeSetTo: dateMinus30Mins.toISOString(),
      });

      C.error(
        `Upload: The Catch: ${errorMsg}, the chunk will be retried, index ${chunkIndex}`
      );
    }
  };

  isDuplicateAck = (chunkIndex) => {
    return this.chunksAckd.has(chunkIndex);
  };

  processAck = (chunkIndex, socket) => {
    if (this.chunksAckd && !this.chunksAckd.has(chunkIndex)) {
      this.chunksAckd.add(chunkIndex);
    } else {
      if (this.shouldLog()) {
        C.warn(
          `Upload: Duplicate ACK detected for chunk ${chunkIndex}, skipping processAck`
        );
      }
      return;
    }

    if (socket && socket.chunksAckd) {
      if (socket.chunksAckd.indexOf(chunkIndex) === -1) {
        socket.chunksAckd.push(chunkIndex);
      }
    }

    // Log chunk acknowledgment event
    const chunkData = this.chunksInfo.chunks[chunkIndex];

    // Track chunk upload time for average calculation (independent of debug flag)
    // Use sent_at if available, otherwise fall back to init_time (chunk was ACKed before sent_at was set)
    const timeToUse =
      chunkData?.sent_at && chunkData.sent_at !== null
        ? chunkData.sent_at
        : chunkData?.init_time;

    if (chunkData && timeToUse) {
      const uploadTime = (Date.now() - timeToUse) / 1000; // Convert to seconds
      this.uploadSummary.uploadTimeSamples.push(uploadTime);

      // Keep only last 50 samples to avoid memory bloat
      if (this.uploadSummary.uploadTimeSamples.length > 50) {
        this.uploadSummary.uploadTimeSamples.shift();
      }

      // Calculate average upload time
      const totalTime = this.uploadSummary.uploadTimeSamples.reduce(
        (sum, time) => sum + time,
        0
      );
      this.uploadSummary.averageChunkUploadTime =
        totalTime / this.uploadSummary.uploadTimeSamples.length;
    }

    // Use sent_at if available, otherwise fall back to init_time for timeSinceSent calculation
    const timeForCalculation =
      chunkData?.sent_at && chunkData.sent_at !== null
        ? chunkData.sent_at
        : chunkData?.init_time;

    // Calculate wait time (difference between init_time and sent_at)
    const waitTime =
      chunkData?.init_time && chunkData?.sent_at && chunkData.sent_at !== null
        ? chunkData.sent_at - chunkData.init_time
        : null;

    this.logChunkEvent(chunkIndex, "CHUNK_ACKNOWLEDGED", {
      socketIndex: socket?.index,
      chunkSize: chunkData?.size,
      retries: chunkData?.retries || 0,
      initTime: chunkData?.init_time,
      sentAt: chunkData?.sent_at,
      waitTime: waitTime,
      timeSinceSent: timeForCalculation
        ? Date.now() - timeForCalculation
        : null,
      usedInitTime: !chunkData?.sent_at || chunkData.sent_at === null,
      totalAcknowledged: this.chunksAckd.size,
    });

    // Invalidate cache when chunk is acknowledged
    this.cachedUnackdChunks = null;

    // Log processed ACK (console only, not in debug logs)
    if (this.shouldLog()) {
      const socketLabel =
        socket && typeof socket.index !== "undefined" ? socket.index : "N/A";
      const chunk = this.chunksInfo.chunks[chunkIndex];
      const chunkSizeMB = chunk ? this.formatChunkSizeMB(chunk.size) : "N/A";
      const { initTimeStr, sentAtStr, waitTime } = chunk
        ? this.formatChunkTimeStrings(chunk)
        : { initTimeStr: "N/A", sentAtStr: "N/A", waitTime: "N/A" };
      this.logToConsoleAndDebug(
        `Upload: ACK chunk ${chunkIndex}, socket: ${socketLabel}, Size: ${chunkSizeMB}, Init: ${initTimeStr}, Sent: ${sentAtStr}, Wait: ${waitTime}`
      );
    }

    // Find which socket had this chunk before removing it (needed to update next chunk's sent_at)
    let socketWithChunk = null;
    this.sockets.forEach((s) => {
      if (s.chunksBeingSent.includes(chunkIndex)) {
        socketWithChunk = s;
      }
      s.chunksBeingSent = s.chunksBeingSent.filter((x) => x !== chunkIndex);
    });

    if (
      this.retrySocket &&
      this.retrySocket.chunksBeingSent.includes(chunkIndex)
    ) {
      socketWithChunk = this.retrySocket;
      this.retrySocket.chunksBeingSent =
        this.retrySocket.chunksBeingSent.filter((x) => x !== chunkIndex);
    }

    // Update sent_at for next chunk in queue if this chunk was ACKed
    if (socketWithChunk) {
      this.updateNextChunkSentTime(socketWithChunk, chunkIndex);
    }

    const chunk = this.chunksInfo.chunks[chunkIndex];
    if (chunk) {
      // Calculate timeTaken: use sent_at if available, otherwise fall back to init_time
      // This handles the case where chunk was ACKed before sent_at was set (race condition)
      const timeToUse =
        chunk.sent_at && chunk.sent_at !== null
          ? chunk.sent_at
          : chunk.init_time;
      const timeTaken = timeToUse ? (Date.now() - timeToUse) / 1000 : null;
      chunk.timeTaken = timeTaken;

      // If we had to use init_time, log a warning for debugging and set sent_at for consistency
      if (
        timeTaken !== null &&
        (!chunk.sent_at || chunk.sent_at === null) &&
        chunk.init_time
      ) {
        if (this.shouldLog()) {
          C.warn(
            `Upload: Chunk ${chunkIndex} ACKed before sent_at was set, used init_time (${new Date(
              chunk.init_time
            ).toISOString()}) for timeTaken calculation`
          );
        }
        // Set sent_at to init_time for consistency (chunk was actually sent, just ACKed very quickly)
        chunk.sent_at = chunk.init_time;
      }
      if (chunk.real_sent_at) {
        chunk.timeTakenReal = (Date.now() - chunk.real_sent_at) / 1000;
      }
      // Note: timeTaken should not be null with our init_time fallback, but if it is,
      // we still mark chunk as done (it was ACKed by server, so it's valid)
      if (timeTaken === null) {
        C.warn(
          `Upload: Chunk ${chunkIndex} ACKed but timeTaken is null (both sent_at and init_time are missing). Chunk will still be marked as done.`,
          {
            chunk: {
              index: chunkIndex,
              sent_at: chunk.sent_at,
              init_time: chunk.init_time,
            },
          }
        );
      }

      const chunkDetail = this.uploadSummary.chunkDetails.find(
        (c) => c.index === chunkIndex
      );
      if (chunkDetail) {
        chunkDetail.timeTaken = timeTaken;
        chunkDetail.timeTakenReal = chunk.timeTakenReal;
        chunkDetail.retries = chunk.retries;
        chunkDetail.bufferringTime = chunk.bufferringTime;
      }

      this.logAck(chunkIndex, timeTaken);

      // Critical fix: Only update chunk status if chunk is not already done
      // This prevents double-counting and ensures status consistency
      // Note: Chunk is marked as done regardless of sent_at being null (we use init_time as fallback)
      // The chunk was ACKed by the server, so it's valid and should be tracked as done
      if (chunk.status !== "done") {
        this.updateChunkStatus(chunkIndex);
      } else {
        // Chunk already marked as done, log warning for debugging
        if (this.shouldLog()) {
          C.warn(
            `Upload: Duplicate ACK detected for chunk ${chunkIndex} (already done)`
          );
        }
      }

      // Enhanced logging for large files
      const file = this.streamingFileInfo;
      // Only log for very large files (>10GB) and only occasionally to avoid performance impact
      if (
        this.shouldLog() &&
        this.isVeryLargeFile(file.size) &&
        chunkIndex % 100 === 0
      ) {
        // Log every 100th chunk for very large files
        C.log(
          `Upload: Large file chunk ack - chunkIndex: ${chunkIndex}, totalChunksUploaded: ${this.chunksInfo.totalChunksUploaded}, totalChunks: ${this.chunksInfo.chunks.length}, bytesUploaded: ${this.chunksInfo.bytesUploaded}, fileSize: ${file.size}`
        );
      }

      this.updateStore();
    } else {
      if (chunkIndex === -1) {
        C.log(`Upload: Zero-byte chunk ACKed`);
      } else {
        // Check if this is actually a duplicate ACK (chunk was already acknowledged)
        // This can happen if chunks array was cleared but late ACKs still arrive
        if (this.chunksAckd.has(chunkIndex)) {
          if (this.shouldLog()) {
            C.warn(
              `Upload: Duplicate ACK detected for chunk ${chunkIndex}, skipping processAck (chunk not in chunks array but already in chunksAckd)`
            );
          }
        } else {
          C.error(
            `Upload: Chunk ACKed by server, but its not in our list. ${chunkIndex}`
          );
        }
      }
    }
  };

  logAck = (chunkIndex, timeTaken) => {
    if (timeTaken > this.chunkTimeout) {
      C.warn(
        `Upload: Ack: ${chunkIndex}, it took ${timeTaken} Seconds to upload and ${this.chunksInfo.chunks[chunkIndex].retries} Retries`
      );

      // If a chunk takes more than the configured timeout, immediately reduce chunk size by 1MB
      // This provides faster response to poor network conditions
      // Only adjust if chunk size adjustment is enabled
      if (
        this.shouldAdjustChunkSize &&
        timeTaken > this.chunkTimeout &&
        this.currentChunkSize > this.startChunkSizeMB &&
        !this.uploadDone &&
        !this.fileClosed &&
        !this.isPaused
      ) {
        const previousChunkSize = this.currentChunkSize;
        // Decrease by 1MB (consistent with reCalculateChunkSize), but don't go below startChunkSizeMB
        const decreasedSize = this.currentChunkSize - 1;
        const newChunkSize = Math.max(decreasedSize, this.startChunkSizeMB);

        if (newChunkSize < this.currentChunkSize) {
          this.currentChunkSize = newChunkSize;
          const timestamp = new Date().toISOString();
          const timestampLocal = new Date().toLocaleString();

          this.warnAndDebug(
            `Upload: ChunkSize IMMEDIATELY DECREASED from ${previousChunkSize.toFixed(
              2
            )}MB to ${this.currentChunkSize.toFixed(
              2
            )}MB (1MB decrease) due to slow chunk ${chunkIndex} (${timeTaken.toFixed(
              2
            )}s > ${this.chunkTimeout}s threshold)`
          );

          // Log to debug info with full details
          this.logImportantEvent("CHUNK_SIZE_CHANGED", {
            direction: "DECREASE",
            previousSizeMB: previousChunkSize,
            newSizeMB: this.currentChunkSize,
            changePercentage: "-1MB",
            ackedCount: this.chunksAckd.size,
            avgTimeSec: timeTaken,
            maxAvgSecPerChunk: this.chunkTimeout, // Special threshold for immediate reduction
            targetChunkSizeMB: this.maxChunkSizeMB,
            startChunkSizeMB: this.startChunkSizeMB,
            changeReason: `Immediate reduction: Chunk ${chunkIndex} took ${timeTaken.toFixed(
              2
            )}s (>${this.chunkTimeout}s threshold)`,
            chunkIndex,
            timeTaken,
            immediateReduction: true,
            timestamp,
            timestampLocal,
          });

          this.logActionAndDebug(
            "chunk-size",
            "ChunkSize reduced (slow chunk)",
            {
              chunkIndex,
              timeTaken,
              previousChunkSizeMB: previousChunkSize,
              newChunkSizeMB: this.currentChunkSize,
              change: "-1MB",
              reason: `Chunk ${chunkIndex} took ${timeTaken.toFixed(2)}s (>${
                this.chunkTimeout
              }s threshold)`,
            }
          );
        }
      }
    }
  };

  updateChunkStatus = (chunkIndex) => {
    const chunk = this.chunksInfo.chunks[chunkIndex];

    // Defensive check: Ensure chunk exists and is valid
    if (!chunk) {
      C.error(
        `Upload: updateChunkStatus called for non-existent chunk ${chunkIndex}`
      );
      return;
    }

    // Critical fix: Prevent double-counting if chunk is already marked as done
    // This can happen if processAck is called multiple times for the same chunk
    if (chunk.status === "done") {
      // Chunk already processed, skip to avoid double-counting
      if (this.shouldLog()) {
        C.warn(
          `Upload: Chunk ${chunkIndex} already marked as done, skipping duplicate updateChunkStatus`
        );
      }
      return;
    }

    // Validate chunk has size before incrementing bytesUploaded
    const chunkSize = chunk.size || 0;
    if (chunkSize <= 0 && chunkIndex !== -1) {
      C.warn(`Upload: Chunk ${chunkIndex} has invalid size: ${chunkSize}`);
    }

    // Update chunk status to done
    chunk.status = "done";
    chunk.chunkData = null;
    chunk.buffer = null;
    this.chunksInfo.chunkPos = chunkIndex;

    // Increment counters (only if not already done)
    this.chunksInfo.totalChunksUploaded++;
    this.chunksInfo.bytesUploaded += chunkSize;
    // Log chunk acknowledgment details (console only, not in debug logs)
    if (this.shouldLog()) {
      const { sizeMB, initTime, startTime, waitTime, uploadTime } =
        this.formatChunkInfoForLog(chunk, true);
      const progress = (
        (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
        100
      ).toFixed(1);
      this.logToConsoleAndDebug(
        `Upload: Chunk ${chunkIndex} ACKed - Size: ${sizeMB}, Init: ${initTime}, Start: ${startTime}, Wait: ${waitTime}, Upload: ${uploadTime}, Progress: ${progress}%`
      );
    }
  };

  updateStore = () => {
    const file = this.streamingFileInfo;
    const { id: fileId } = file;

    // Log progress bar calculation details (console only, not in debug logs)
    if (this.shouldLog()) {
      const progressPercentage =
        file.size > 0 ? (this.chunksInfo.bytesUploaded / file.size) * 100 : 0;
      this.logToConsoleAndDebug(
        `Upload: Progress - ${progressPercentage.toFixed(1)}%, Sent: ${
          this.chunksSent.length
        }, ACKed: ${this.chunksAckd.size}`
      );
    }

    store.dispatch("updateFileUploadStatus", {
      fileId,
      size: this.chunksInfo.bytesUploaded,
      maxSize: file.size,
    });

    // Guard against multiple completion calls
    if (this.uploadDone || this.fileClosed) {
      return;
    }

    const completionStatus = this.getCompletionStatus();
    const completionResult = this.isUploadComplete(completionStatus);

    if (completionResult.isComplete) {
      C.success(`Upload: File upload completed, closing sockets`);
      this.logCompletionDetails(completionStatus, completionResult);
      this.finishedUpload();
    }
  };

  checkFileOpenStatus = async () => {
    if (this.fileClosed || this.uploadDone || this.isPaused) return false;
    if (!this._fileOpenRequestQueue) {
      this._fileOpenRequestQueue = [];
      this._isFileOpenRequestInProgress = false;
    }
    if (this._isFileOpenRequestInProgress) {
      return new Promise((resolve) => {
        this._fileOpenRequestQueue.push(resolve);
      });
    }
    this._isFileOpenRequestInProgress = true;
    try {
      const fileInfo = this.streamingFileInfo;
      const { id: fileId, reUploadId } = fileInfo;

      // Log check file open called
      this.logActionAndDebug("file", "Check file open called", {
        fileId: reUploadId || fileId,
      });

      const response = await isFileOpen({
        upload_id: reUploadId || fileId,
        random: Math.random(),
        chunkInfo: JSON.stringify({}),
      });
      const fileOpen = response.toLowerCase() === "open";

      // Log check file open result
      C.logAction(
        "file",
        fileOpen ? "Check file open: open" : "Check file open: closed",
        {
          fileId: reUploadId || fileId,
          response: response.trim(),
        }
      );

      if (!fileOpen && !this.fileClosed) {
        this.closeSockets();
        clearInterval(this.chunksInfo.intervalId);
        this.fileClosed = true;
        this.closeFileUsingBlockUnBlock(false, null, {
          status: "failed",
          uploadingError: response || t("failedFileClosed"),
        });

        handleFileOpenError(fileInfo, t("failedFileClosed"));
      }
      while (this._fileOpenRequestQueue.length > 0) {
        const resolve = this._fileOpenRequestQueue.shift();
        resolve(fileOpen);
      }

      return fileOpen;
    } catch (error) {
      while (this._fileOpenRequestQueue.length > 0) {
        const resolve = this._fileOpenRequestQueue.shift();
        resolve(false);
      }
      throw error;
    } finally {
      this._isFileOpenRequestInProgress = false;
    }
  };

  handleChunkError = async (err) => {
    try {
      if (
        err?.toLowerCase().indexOf("quota") >= 0 &&
        err?.toLowerCase().indexOf("exceeded") >= 0
      ) {
        C.error(`Upload: Received an error from server: ${err}`);
        this.closeSockets();
        clearInterval(this.chunksInfo.intervalId);
        C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);
        this.fileClosed = true;

        invoke("growl", [
          t("yourFileQuotaExceeded"),
          t("fileUploadingFailed"),
          4000,
          true,
        ]);
        this.stopFileUploadingQueueOnQuotaStorageExceeded(
          decodeURIComponent(response)
            .toLowerCase()
            .indexOf("no such upload open") >= 0
            ? decodeURIComponent(response)
            : false
        );
        return;
      }
      const fileOpen = await this.checkFileOpenStatus();
      if (!fileOpen) {
        return;
      }
      this.handleRetry(this.retries, () => {
        this.startUploading(this.chunkStart);
      });
    } catch (error) {
      C.error(error);
      invoke("growl", [error, "", 4000, true]);
      this.closeFileUsingBlockUnBlock(false, null, {
        status: "failed",
        uploadingError: t("somethingWentWrong"),
      });
    }
  };

  uploadHealthCheck = () => {
    if (!this.lastChunkSentTime) return;

    if (!this.lastLogTime) {
      this.lastLogTime = Date.now();
    }
    const shouldLog = Date.now() - this.lastLogTime > 10000;
    if (shouldLog) {
      this.logUploadStatus();
    }
  };

  //Upload Summary
  logUploadStatus = () => {
    if (this.shouldLog()) {
      this.lastLogTime = Date.now();
      const chunks = [...this.chunksInfo.chunks];
      const statusCounts = this.getChunkStatusCounts(chunks);
      const chunksYetToAck = this.getChunksToAck();
      this.warnToConsoleAndDebug(
        `Upload: Total chunks: ${chunks.length}, Pending chunks: ${statusCounts.init}, ` +
          `Done: ${statusCounts.done}, Failed: ${statusCounts.failed}, ` +
          `Uploading: ${statusCounts.uploading}, Retrying: ${statusCounts.retrying}, ` +
          `Chunks Sent: ${this.chunksSent.length}, Chunks Ackd: ${this.chunksAckd.size}, ` +
          `Sent but not Ackd: ${chunksYetToAck}, Current Chunk Size: ${this.currentChunkSize} MB`
      );
    }
  };

  getChunksToAck = () => {
    return this.chunksSent
      .filter((value) => !this.chunksAckd.has(value))
      .sort((a, b) => a - b);
  };

  startZeroByteChunkSending = () => {
    // Initialize tracking if not already done
    if (!this.zeroByteChunkTracking) {
      this.zeroByteChunkTracking = {
        startTime: null,
        stopTime: null,
        totalChunksSent: 0,
        intervalId: null,
        lastSentTime: null,
        isActive: false,
      };
    }

    // Don't start if already active
    if (this.zeroByteChunkTracking.isActive) {
      return;
    }

    // Start tracking
    this.zeroByteChunkTracking.startTime = Date.now();
    this.zeroByteChunkTracking.isActive = true;
    this.zeroByteChunkTracking.totalChunksSent = 0;

    // Start sending zero-byte chunks every 500ms
    this.zeroByteChunkTracking.intervalId = setInterval(() => {
      this.sendZeroByteChunksToAllSockets();
    }, 500);
  };

  // Send zero-byte chunks to all open sockets
  sendZeroByteChunksToAllSockets = () => {
    if (!this.zeroByteChunkTracking || !this.zeroByteChunkTracking.isActive) {
      return;
    }

    const currentTime = Date.now();

    // Send zero-byte chunk to each open socket
    this.sockets.forEach((socket, index) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          // Create a zero-byte chunk buffer in the same format as regular chunks
          // Index -1 tells server to ignore this chunk
          const zeroByteChunkBuffer = this.createZeroByteChunkBuffer(
            0,
            currentTime
          );

          // Send the zero-byte chunk using the same format as regular chunks
          socket.send(zeroByteChunkBuffer);

          // Update tracking
          this.zeroByteChunkTracking.totalChunksSent++;
        } catch (error) {
          if (this.shouldLog()) {
            C.warn(
              `Upload: Failed to send zero-byte chunk to socket ${index}: ${error.message}`
            );
          }
        }
      }
    });

    // Update last sent time
    this.zeroByteChunkTracking.lastSentTime = currentTime;
  };

  // Create zero-byte chunk buffer in the same format as regular chunks
  createZeroByteChunkBuffer = (chunkIndex, timestamp) => {
    // Create a minimal buffer with chunk index -1 and zero size
    // This matches the format expected by the server
    const buffer = new ArrayBuffer(8); // 8 bytes: 4 for index, 4 for size
    const view = new DataView(buffer);

    // Write chunk index (-1) as 32-bit signed integer
    view.setInt32(0, chunkIndex, true); // little-endian

    // Write chunk size (0) as 32-bit signed integer
    view.setInt32(4, 0, true); // little-endian

    return buffer;
  };

  // Stop zero-byte chunk sending
  stopZeroByteChunkSending = () => {
    if (!this.zeroByteChunkTracking || !this.zeroByteChunkTracking.isActive) {
      return;
    }

    // Clear the interval
    if (this.zeroByteChunkTracking.intervalId) {
      clearInterval(this.zeroByteChunkTracking.intervalId);
      this.zeroByteChunkTracking.intervalId = null;
    }

    // Update tracking
    this.zeroByteChunkTracking.stopTime = Date.now();
    this.zeroByteChunkTracking.isActive = false;
  };

  // Get zero-byte chunk statistics for debug info
  getZeroByteChunkStats = () => {
    if (!this.zeroByteChunkTracking) {
      return null;
    }

    const currentTime = Date.now();
    const duration = this.zeroByteChunkTracking.isActive
      ? currentTime - this.zeroByteChunkTracking.startTime
      : (this.zeroByteChunkTracking.stopTime || currentTime) -
        this.zeroByteChunkTracking.startTime;

    return {
      startTime: this.zeroByteChunkTracking.startTime,
      stopTime: this.zeroByteChunkTracking.stopTime,
      duration: Math.floor(duration / 1000),
      totalChunksSent: this.zeroByteChunkTracking.totalChunksSent,
      lastSentTime: this.zeroByteChunkTracking.lastSentTime,
      isActive: this.zeroByteChunkTracking.isActive,
    };
  };

  // Format zero-byte chunk statistics for debug info
  formatZeroByteChunkStats = () => {
    if (!this.showServerWriteProgress) {
      return ["Debug logging disabled (showServerWriteProgress is false)"];
    }

    const stats = this.getZeroByteChunkStats();

    if (!stats) {
      return ["Zero-byte chunk sending: Not started"];
    }

    const formatTime = (timestamp) => {
      if (!timestamp) return "N/A";
      return new Date(timestamp).toLocaleString();
    };

    return [
      `Zero-byte chunk sending: ${stats.isActive ? "Active" : "Inactive"}`,
      `Start time: ${formatTime(stats.startTime)}`,
      `Stop time: ${formatTime(stats.stopTime)}`,
      `Duration: ${stats.duration} seconds`,
      `Total chunks sent: ${stats.totalChunksSent}`,
      `Last sent: ${formatTime(stats.lastSentTime)}`,
    ];
  };

  getSizeOfServerYetToWrite = () => {
    const chunksYetToAck = this.chunksSent.filter(
      (chunk) => !this.chunksAckd.has(chunk)
    );

    const sizeYetToAck = chunksYetToAck.reduce((total, chunkIndex) => {
      const chunk = this.chunksInfo.chunks[chunkIndex];
      if (!chunk) {
        C.warn(`Upload: Chunk ${chunkIndex} not found in chunksInfo`);
        return total;
      }
      return total + chunk.size;
    }, 0);

    let chunksBeingSent = this.sockets.reduce((total, socket) => {
      return (
        total +
        socket.chunksBeingSent.reduce((socketTotal, chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          return socketTotal + (chunk?.size || 0);
        }, 0)
      );
    }, 0);

    if (this.retrySocket) {
      chunksBeingSent += this.retrySocket.chunksBeingSent.reduce(
        (total, chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          return total + (chunk?.size || 0);
        },
        0
      );
    }
    const serverYetToWrite = (
      (sizeYetToAck + chunksBeingSent) /
      this.MB
    ).toFixed(2);

    return parseFloat(serverYetToWrite);
  };

  //Upload Summary
  updateServerWriteProgress = (serverYetToWrite) => {
    // Skip updates if upload is done to reduce CPU usage
    if (this.isPaused || this.fileClosed || this.uploadDone) return;

    const progressDiv = document.getElementById("server-write-progress-upload");
    if (!progressDiv) return;

    _$(progressDiv).show();

    this.updateRecoveryMessage(progressDiv);
    this.updateProgressBar(progressDiv, serverYetToWrite);
    this.updateSocketTable(progressDiv);
  };

  //Upload Summary
  updateRecoveryMessage = (progressDiv) => {
    let recoveryMessage = progressDiv.querySelector(".recovery-message");
    if (!recoveryMessage) {
      recoveryMessage = document.createElement("div");
      recoveryMessage.className = "recovery-message";
      // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
      recoveryMessage.className = "recovery-message pos-absolute t-5px r-5px p-2px-5px br-3px fs-12px";
      _$(recoveryMessage).css("backgroundColor", "rgba(255, 255, 0, 0.7)");
      _$(recoveryMessage).css("color", "black");
      progressDiv.appendChild(recoveryMessage);
    }

    const recoveryReasons = this.getRecoveryInformation();
    const isRecovering = recoveryReasons.length > 0;
    const newRecoveryText = isRecovering
      ? `Recovery: ${recoveryReasons.join(" | ")}`
      : "";

    // Always update display style based on recovery state
    if (isRecovering) {
      _$(recoveryMessage).show();
    } else {
      _$(recoveryMessage).hide();
    }

    // Only update text content if it has changed (performance optimization)
    const currentText = recoveryMessage.textContent || "";
    if (currentText !== newRecoveryText) {
      recoveryMessage.textContent = newRecoveryText;
    }
  };

  //Upload Summary
  updateProgressBar = (progressDiv, serverYetToWrite) => {
    const progressBar = progressDiv.querySelector(".progress-fill");
    const progressText = progressDiv.querySelector(".progress-text");
    const threshold = this.serverWriteThreshold;

    const percentage = Math.min((serverYetToWrite / threshold) * 100, 100);

    // Round percentage to nearest integer for utility class compatibility
    const roundedPercentage = Math.round(percentage);
    _$(progressBar).css("width", `${roundedPercentage}%`);
    // Background color needs to be set via CSS class or data attribute
    // For CSP compliance, use a CSS class based on threshold state
    progressBar.classList.remove("progress-fill-warning", "progress-fill-normal");
    progressBar.classList.add(serverYetToWrite > threshold ? "progress-fill-warning" : "progress-fill-normal");

    // Base server write status text
    let text = `Server write: ${serverYetToWrite.toFixed(
      2
    )} MB / ${threshold.toFixed(2)} MB`;
    if (serverYetToWrite > threshold) {
      text += ` (Extra ${(serverYetToWrite - threshold).toFixed(2)} MB)`;
    }

    // Append current chunk size information
    text += ` | Chunk size: ${this.currentChunkSize} MB`;

    progressText.textContent = text;
  };

  //Upload Summary
  updateSocketTable = (progressDiv) => {
    let socketTable = progressDiv.querySelector(".socket-info-table");
    if (!socketTable) {
      socketTable = document.createElement("table");
      socketTable.className = "socket-info-table";
      // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
      socketTable.className = "socket-info-table w-100 mt-10px";
      _$(socketTable).css("borderCollapse", "collapse");
      progressDiv.appendChild(socketTable);

      // Add copy button
      const copyButton = document.createElement("button");
      copyButton.className = "copy-debug-info";
      copyButton.setAttribute(
        "data-tooltip",
        "Copy debug info (alt+cilck to download)"
      );
      copyButton.innerHTML = '<i class="icon-paste"></i>';
      // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
      copyButton.className = "copy-debug-info pos-absolute b-5px r-5px op-0-7 p-5px-10px border-none br-3px cursor-pointer fs-12px";
      _$(copyButton).css("color", "black");
      const tooltip = new Tooltip();

      copyButton.onclick = (event) => this.copyDebugInfo(event);
      progressDiv.appendChild(copyButton);
      _$(progressDiv)
        .find("[data-tooltip]")
        .sel()
        .forEach((button) => {
          tooltip.addTooltip({
            element: button,
            fixedPosition: true,
            position: "left",
          });
        });
    }

    const [indexRow, bufferRow] = this.getOrCreateRows(socketTable);
    this.updateSocketRows(indexRow, bufferRow);
    this.updateStatusRow(socketTable);
    this.debounce(() => this.updateSummaryRow(socketTable), 150, "summaryRow");
  };

  //Upload Summary
  getOrCreateRows = (table) => {
    const createRow = (className) => {
      let row = table.querySelector(`.${className}`);
      if (!row) {
        row = table.insertRow();
        row.className = className;
      }
      // Clear existing cells before rebuilding
      row.innerHTML = "";
      return row;
    };
    return [createRow("index-row"), createRow("buffer-row")];
  };

  //Upload Summary
  updateSocketRows = (indexRow, bufferRow) => {
    // Clear rows first (already done in getOrCreateRows, but ensure it's clean)
    indexRow.innerHTML = "";
    bufferRow.innerHTML = "";

    // Create header cells
    const socketHeaderCell = indexRow.insertCell();
    socketHeaderCell.className = "w-80px text-left";
    socketHeaderCell.style.setProperty("color", "var(--text-primary)");
    socketHeaderCell.textContent = "Socket:";
    
    const bufferHeaderCell = bufferRow.insertCell();
    bufferHeaderCell.className = "w-80px text-left";
    bufferHeaderCell.style.setProperty("color", "var(--text-primary)");
    bufferHeaderCell.textContent = "Bfr:";

    let totalBufferedAmount = 0;
    let totalChunksBeingSent = 0;

    const allSockets = [...this.sockets, this.retrySocket].filter(Boolean);
    const cellWidth = `${100 / (allSockets.length + 1)}%`;

    allSockets.forEach((socket, index) => {
      const socketBufferedAmount = socket.bufferedAmount / (1024 * 1024);
      totalBufferedAmount += socketBufferedAmount;
      totalChunksBeingSent += socket.chunksBeingSent.length;

      const socketLabel =
        socket === this.retrySocket ? "R" : index + "(" + socket.index + ")";
      
      // Create socket index cell
      const socketIndexCell = indexRow.insertCell();
      socketIndexCell.className = "text-center";
      socketIndexCell.style.setProperty("width", cellWidth);
      socketIndexCell.style.setProperty("color", "var(--text-primary)");
      const socketIndexStrong = document.createElement("strong");
      socketIndexStrong.textContent = socketLabel;
      socketIndexStrong.style.setProperty("color", "var(--text-primary)");
      socketIndexCell.appendChild(socketIndexStrong);
      
      // Create buffer cell
      const bufferCell = bufferRow.insertCell();
      bufferCell.className = "text-center";
      bufferCell.style.setProperty("width", cellWidth);
      bufferCell.style.setProperty("color", "var(--text-primary)");
      bufferCell.textContent = `${socketBufferedAmount.toFixed(2)} (${socket.chunksBeingSent.length})`;
    });

    // Add total cell for buffer row
    const totalBufferCell = bufferRow.insertCell();
    totalBufferCell.className = "text-center has-text-weight-bold";
    totalBufferCell.style.setProperty("width", cellWidth);
    totalBufferCell.style.setProperty("color", "var(--text-primary)");
    totalBufferCell.textContent = `${totalBufferedAmount.toFixed(2)} (${totalChunksBeingSent})`;
  };

  //Upload Summary
  createCell = (row, width, content, isTotal = false) => {
    const cell = row.insertCell();
    // Round width to nearest utility class if it's a pixel value
    const widthValue = width.includes("px") 
      ? `${Math.round(parseFloat(width))}px` 
      : width;
    _$(cell).css("width", widthValue);
    cell.className = `text-center ${isTotal ? "has-text-weight-bold" : ""}`;
    cell.innerHTML = content;
  };

  //Upload Summary
  updateStatusRow = (table) => {
    const chunksReadyInMemory = this.chunksInfo.chunks.filter(
      (chunk) => chunk.status === "init"
    ).length;
    const allChunksRead =
      this.chunksInfo.chunks.length > 0 &&
      this.chunksInfo.chunks[this.chunksInfo.chunks.length - 1].isLastChunk;

    let statusText = `${this.streamingFileInfo.name} (${
      this.streamingFileInfo.reUploadId || this.streamingFileInfo.id
    })`;
    statusText += `Chunks ready in memory: ${chunksReadyInMemory}`;
    if (allChunksRead) statusText += " | All chunks read";

    let statusRow = table.querySelector(".status-row") || table.insertRow();
    statusRow.className = "status-row";
    statusRow.innerHTML = `<td colspan="${
      this.sockets.length + 2
    }" class="text-left">${statusText}</td>`;
  };

  //Upload Summary
  updateSummaryRow = (table) => {
    // Skip updates if upload is done to reduce CPU usage
    if (this.isPaused || this.fileClosed || this.uploadDone) return;

    const chunks = this.chunksInfo.chunks;
    const chunksYetToAck = this.getChunksToAck();
    const serverYetToWrite = this.getSizeOfServerYetToWrite();
    const socketInfo = this.getSocketInfo();

    // Get recovery information using helper
    const recoveryReasons = this.getRecoveryInformation();

    // Get chunk status counts
    const statusCounts = this.getChunkStatusCounts(chunks);

    // Create a hash of recovery information for change detection
    // This ensures recovery info updates when chunks finish retrying
    const recoveryInfoHash =
      recoveryReasons.length > 0 ? recoveryReasons.join("|") : "";

    // Check if values have changed to avoid unnecessary DOM updates
    const currentValues = {
      totalChunks: chunks.length,
      uploading: statusCounts.uploading,
      inMemory: chunks.filter((chunk) => chunk.status === "init").length,
      retrying: statusCounts.retrying,
      chunksSent: this.chunksSent.length,
      chunksAckd: this.chunksAckd.size,
      chunksYetToAck: chunksYetToAck.length,
      bytesSent: this.chunksInfo.bytesSent,
      bytesUploaded: this.chunksInfo.bytesUploaded,
      // Add missing values that change during retries
      lastChunkSentTime: this.lastChunkSentTime,
      lastChunkSentIsRetry: this.lastChunkSentIsRetry,
      totalChunkResendAttempts: this.totalChunkResendAttempts,
      // Add time-based values that change every second
      currentTime: Math.floor(Date.now() / 1000),
      // Critical fix: Include recovery info in change detection
      // This ensures recovery information updates when chunks finish retrying
      recoveryInfo: recoveryInfoHash,
    };

    // Only update if values have changed
    const valuesChanged =
      !this.lastSummaryValues ||
      Object.keys(currentValues).some(
        (key) => this.lastSummaryValues[key] !== currentValues[key]
      );

    if (!valuesChanged) {
      return; // No changes, skip DOM update
    }
    this.lastSummaryValues = currentValues;

    let summaryRow = table.querySelector(".summary-row") || table.insertRow();
    summaryRow.className = "summary-row";
    // Add relative positioning to the row for CSP compliance
    summaryRow.classList.add("pos-relative");
    summaryRow.innerHTML = `
      <td colspan="${this.sockets.length + 2}" class="text-left">
        <!-- Use data attribute and CSS class for CSP compliance -->
        <span class="pos-absolute t-5px l--8px d-ib w-7px h-7px br-50 periodic-check-indicator" data-status="${
          this.periodicCheckInterval &&
          Date.now() - this.lastPeriodicCheckTime < 6000
            ? "active"
            : "inactive"
        }" title="${
      this.periodicCheckInterval &&
      Date.now() - this.lastPeriodicCheckTime < 6000
        ? "Periodic check active"
        : "Periodic check inactive"
    }"></span>
        Total: ${chunks.length} |
        Uploading: ${statusCounts.uploading} |
        In memory: ${chunks.filter((chunk) => chunk.status === "init").length} |
        Retrying: ${statusCounts.retrying} |
        Last generated ${
          this.lastChunkGeneratedTime
            ? Math.round((Date.now() - this.lastChunkGeneratedTime) / 1000)
            : 0
        } secs ago |
        Last sent ${
          this.lastChunkSentTime
            ? Math.round((Date.now() - this.lastChunkSentTime) / 1000)
            : 0
        } secs ago ${this.lastChunkSentIsRetry ? " (R)" : ""} |
        Sockets respawned: ${this.totalSocketsRespawned} <br>
        Chunk prep stalls: ${this.totalChunkPreparationStalls} |
        Recovery actions: ${this.totalRecoveryActions} |
        Chunk resend attempts: ${this.totalChunkResendAttempts} |
        Stuck socket identification: ${
          this.totalStuckSocketIdentifications
        } <br>
        Sent: ${this.chunksSent.length} |
        Ackd: ${this.chunksAckd.size} |
        Yet to ack: ${chunksYetToAck.length}
        ${
          chunksYetToAck.length > 0
            ? `(${chunksYetToAck
                .slice(0, 20)
                .map((chunkIndex) => {
                  const chunk = this.chunksInfo.chunks[chunkIndex];
                  return chunk && chunk.retries > 0
                    ? `${chunkIndex} (${chunk.retries})`
                    : chunkIndex;
                })
                .join(", ")}${chunksYetToAck.length > 20 ? "..." : ""})`
            : ""
        } <br>
        Bytes Sent: ${utils.formatBytes(this.chunksInfo.bytesSent)} |
        Bytes ACKed: ${utils.formatBytes(this.chunksInfo.bytesUploaded)} |
        Bytes Pending: ${utils.formatBytes(
          this.chunksInfo.bytesSent - this.chunksInfo.bytesUploaded
        )} |
        Progress: ${(
          (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
          100
        ).toFixed(1)}% <br>
      </td>
    `;
  };

  //Upload Summary
  getChunkStatusCounts = (chunks) => {
    return chunks.reduce(
      (counts, chunk) => {
        // Count by actual status
        counts[chunk.status]++;

        // Critical fix: Only count chunks with status "retry" as retrying
        // Do NOT count "uploading" chunks that have retries > 0 as retrying
        // This prevents incorrectly counting chunks that were retried and are now uploading
        if (chunk.status === "retry" && !chunk.ackVerifying) {
          counts.retrying++;
        }

        return counts;
      },
      { done: 0, failed: 0, init: 0, uploading: 0, retrying: 0 }
    );
  };

  //Upload Summary
  logSocketInfo = () => {
    const chunkSize = this.MB * this.currentChunkSize;
    const socketInfo = this.sockets.map((socket) =>
      this.getIndividualSocketInfo(socket, chunkSize)
    );
    socketInfo.push(
      this.getIndividualSocketInfo(this.retrySocket, chunkSize, "Retry Socket")
    );
    // C.table(socketInfo);
    if (this.shouldLog()) {
      this.sockets.forEach((socket) => {
        C.log(
          `Upload: Socket ${socket.index}: Buffer: ${socket.bufferedAmount}, Chunks being sent: ${socket.chunksBeingSent?.length}, Chunks acked: ${socket.chunksAckd?.length}`
        );
      });
    }
  };

  //Upload Summary
  getIndividualSocketInfo = (socket, chunkSize, index = socket.index) => {
    return {
      index,
      chunksBeingSent: socket.chunksBeingSent,
      chunksAckd: socket.chunksAckd,
      isStuck: socket.isStuck,
      beingSentVsAckd: `${(
        socket.chunksBeingSent.length / socket.chunksAckd.length
      ).toFixed(2)}`,
      buffer: `${(socket.bufferedAmount / 1048576).toFixed(2)} MB`,
      lastBufferedAmount: socket.lastBufferedAmount
        ? `${(socket.lastBufferedAmount / 1048576).toFixed(2)} MB`
        : null,
      lastCheckedTime: socket.lastCheckedTime
        ? `${(Date.now() - socket.lastCheckedTime) / 1000}`
        : null,
    };
  };

  closeSockets = () => {
    this.sockets.forEach((socket) => {
      if (this.isSocketOpen(socket)) {
        socket.close();
      }
    });
  };

  allChunksSentOut = () => {
    if (
      this.allChunksCreated &&
      this.chunksSent.length === this.chunksInfo.chunks.length
    ) {
      return true;
    }
    return false;
  };

  sendChunks = (emptySocket) => {
    if (this.isSendingChunks) {
      return;
    }

    // Set guard flag
    this.isSendingChunks = true;

    try {
      this.sendChunksCallCount++;
      this.getSizeOfServerYetToWrite();

      if (this.isPaused) {
        if (this.chunksSent.length === this.chunksAckd.size) {
          setTimeout(() => {
            this.pause();
          }, 1000);
        }
        return;
      }
      if (this.fileClosed || this.uploadDone || this.sockets.length <= 0) {
        return;
      }

      if (this.allChunksSentOut()) {
        this.startZeroByteChunkSending();
      }
      this.uploadHealthCheck();
      let readyChunk = this.findChunksToResend(emptySocket);
      let isRetry = !!readyChunk;
      if (readyChunk && readyChunk.status === "retry") {
        const initTimeStr = readyChunk.init_time
          ? new Date(readyChunk.init_time).toISOString()
          : "N/A";
        const sentAtStr =
          readyChunk.sent_at && readyChunk.sent_at !== null
            ? new Date(readyChunk.sent_at).toISOString()
            : "Queued";
        const waitTime = this.formatWaitTime(
          readyChunk.init_time,
          readyChunk.sent_at
        );
        C.warn(
          `Upload: Resending chunk: ${
            readyChunk.index
          }, Size: ${this.formatChunkSizeMB(readyChunk.size)}, ` +
            `Init time: ${initTimeStr}, ` +
            `Sent at: ${sentAtStr}, ` +
            `Wait time: ${waitTime}, ` +
            `status: ${readyChunk.status}`
        );
        isRetry = true;

        // Special handling for last chunk retry - send to all available sockets with 0 buffer
        const totalChunks = this.chunksInfo.chunks.length;
        const isLastChunk = readyChunk.index === totalChunks - 1;
        const chunksYetToAck = this.getChunksToAck();
        const isOnlyChunkRemaining =
          chunksYetToAck.length === 1 && chunksYetToAck[0] === readyChunk.index;

        if (isLastChunk && isOnlyChunkRemaining) {
          // Safety check: prevent infinite retries for last chunk
          const maxLastChunkRetries = 5; // Maximum 5 retries for last chunk
          if (readyChunk.retries >= maxLastChunkRetries) {
            if (this.shouldLog()) {
              C.error(
                `Upload: Last chunk ${readyChunk.index} has exceeded maximum retries (${maxLastChunkRetries}). Forcing completion to prevent infinite loop.`
              );
            }
            // Force completion to prevent infinite loop
            this.finishedUpload();
            return; // Guard flag released in finally block
          }

          // Cooldown check: prevent rapid multi-socket retries
          const now = Date.now();
          const timeSinceLastMultiSocketRetry = this
            .lastChunkMultiSocketRetryTime
            ? now - this.lastChunkMultiSocketRetryTime
            : this.lastChunkMultiSocketRetryCooldown + 1000; // Allow first attempt

          if (
            timeSinceLastMultiSocketRetry <
            this.lastChunkMultiSocketRetryCooldown
          ) {
            if (this.shouldLog()) {
              C.warn(
                `Upload: Last chunk multi-socket retry in cooldown (${Math.floor(
                  timeSinceLastMultiSocketRetry / 1000
                )}s/${
                  this.lastChunkMultiSocketRetryCooldown / 1000
                }s) - using normal retry logic`
              );
            }
            // Use normal retry logic during cooldown
          } else {
            if (this.shouldLog()) {
              C.warn(
                `Upload: Last chunk ${
                  readyChunk.index
                } is the only remaining chunk - sending to all available sockets with 0 buffer (retry ${
                  readyChunk.retries + 1
                }/${maxLastChunkRetries})`
              );
            }

            // Find all sockets with 0 buffer
            const availableSockets = this.sockets.filter(
              (socket) =>
                this.isSocketOpen(socket) &&
                socket.bufferedAmount === 0 &&
                !socket.isStuck
            );

            if (availableSockets.length > 1) {
              if (this.shouldLog()) {
                C.warn(
                  `Upload: Sending last chunk to ${availableSockets.length} sockets simultaneously`
                );
              }

              // Send to all available sockets
              availableSockets.forEach((targetSocket, index) => {
                targetSocket.send(readyChunk.buffer);
                targetSocket.chunksBeingSent.push(readyChunk.index);

                if (this.shouldLog()) {
                  C.log(
                    `Upload: Sent last chunk ${readyChunk.index} to socket ${
                      targetSocket.index
                    } (${index + 1}/${availableSockets.length})`
                  );
                }
              });

              // Update chunk status and retry count
              if (readyChunk.status === "retry") {
                readyChunk.status = "uploading";
              }
              if (!readyChunk.retries) readyChunk.retries = 0;
              readyChunk.retries += 1;
              readyChunk.real_sent_at = readyChunk.sent_at;

              // Always set/update init_time for multi-socket retry (for fallback if ACKed before sent_at is set)
              const currentTime = Date.now();
              readyChunk.init_time = currentTime;

              // For multi-socket retry, all sockets are empty (checked above), so set sent_at immediately
              readyChunk.sent_at = currentTime;
              if (this.shouldLog()) {
                C.log(
                  `Upload: Multi-socket retry chunk ${
                    readyChunk.index
                  } sent to ${
                    availableSockets.length
                  } empty sockets, sent_at set to ${new Date(
                    readyChunk.sent_at
                  ).toISOString()}`
                );
              }

              // Detailed console log for multi-socket retry chunk
              const sizeMB = this.getChunkSizeMB(readyChunk);
              this.logToConsoleAndDebug(
                `[INFLIGHT] Retry Chunk ${
                  readyChunk.index
                } - Size: ${sizeMB}, Sockets: ${
                  availableSockets.length
                }, Init: ${this.formatSimpleTime(
                  readyChunk.init_time
                )}, Start: ${this.formatSimpleTime(
                  readyChunk.sent_at
                )}, Wait: 0ms`
              );

              this.lastChunkSentTime = Date.now();
              this.lastChunkSentIsRetry = true;
              this.lastChunkMultiSocketRetryTime = now; // Set cooldown timestamp

              // Continue with normal flow but skip the single socket send
              setTimeout(this.sendChunks, 200);
              return; // Guard flag released in finally block
            } else if (availableSockets.length === 1) {
              if (this.shouldLog()) {
                C.warn(
                  `Upload: Only 1 socket available for last chunk - using normal retry logic`
                );
              }
            } else {
              if (this.shouldLog()) {
                C.warn(
                  `Upload: No sockets with 0 buffer available for last chunk - using normal retry logic`
                );
              }
            }
          }
        }
      }

      if (
        !isRetry &&
        this.getSizeOfServerYetToWrite() > this.serverWriteThreshold
      ) {
        if (this.showServerWriteProgress) {
          this.uploadSummary.sendChunksRejections
            .serverWriteThresholdExceeded++;
        }
        return; // Guard flag released in finally block
      }

      const { socket, emptySockets } = this.findMostIdleSocket(isRetry);

      if (!socket) {
        C.warn(
          `Upload: No idle socket found, ${this.getSizeOfServerYetToWrite()}, threshold: ${
            this.serverWriteThreshold
          }`
        );
        if (!isRetry && this.showServerWriteProgress) {
          this.uploadSummary.sendChunksRejections.noIdleSocket++;
        }
        return; // Guard flag released in finally block
      }

      const chunks = this.chunksInfo.chunks;

      if (!readyChunk) {
        // Critical fix: Find chunk with atomic check-and-set to prevent race condition
        // The isSendingChunks guard prevents concurrent execution, but we still need
        // to ensure chunk selection is atomic with status update
        readyChunk = chunks.find(
          (chunk) =>
            chunk.status === "init" && !this.chunksSent.includes(chunk.index)
        );

        // Double-check: ensure chunk wasn't already sent (defensive check)
        // This shouldn't happen with the isSendingChunks guard, but adds extra safety
        if (readyChunk && this.chunksSent.includes(readyChunk.index)) {
          // Chunk was already sent, skip it
          readyChunk = null;
        }
      }

      if (!readyChunk && !this.uploadSummary.allChunksCreated) {
        if (this.shouldLog()) {
          this.logToConsoleAndDebug(
            `Upload: No ready chunk - prepareMore: ${this.shouldItPrepareMoreChunks()}, reading: ${
              this.chunksBeingRead
            }`
          );
        }
        if (this.showServerWriteProgress) {
          this.uploadSummary.sendChunksRejections.noReadyChunk++;
        }
        this.prepareNextChunks();
        return; // Guard flag released in finally block
      }

      if (!readyChunk && this.uploadSummary.allChunksCreated) {
        const allChunksSent = chunks.every(
          (chunk) =>
            chunk.status === "uploading" ||
            chunk.status === "done" ||
            chunk.status === "failed"
        );
      }

      if (!this.isSocketOpen(socket)) {
        C.warn(
          `Upload: Socket not open, ${this.getSizeOfServerYetToWrite()}, threshold: ${
            this.serverWriteThreshold
          }`
        );
        if (this.showServerWriteProgress) {
          this.uploadSummary.sendChunksRejections.socketNotOpen++;
        }
        return; // Guard flag released in finally block
      }

      if (readyChunk && this.isSocketOpen(socket)) {
        let emptySocketToUse = emptySocket;
        if (emptySocket) {
          if (emptySocket instanceof WebSocket) {
            emptySocketToUse = emptySocket;
          } else if (
            emptySocket.socket &&
            emptySocket.socket instanceof WebSocket
          ) {
            emptySocketToUse = emptySocket.socket;
          }
        }
        const socketToUse =
          emptySocketToUse && emptySocketToUse.bufferedAmount <= 0
            ? emptySocketToUse
            : socket;
        // Check if socket queue is empty BEFORE pushing (needed to set sent_at correctly)
        const isSocketQueueEmpty = socketToUse.chunksBeingSent.length === 0;
        const chunksAhead = socketToUse.chunksBeingSent.length;

        socketToUse.send(readyChunk.buffer);
        socketToUse.chunksSent = socketToUse.chunksSent
          ? socketToUse.chunksSent++
          : 1;

        socketToUse.chunksBeingSent.push(readyChunk.index);
        this.lastUsedSocket = socketToUse.index;
        this.lastChunkSentTime = Date.now();
        this.lastChunkSentIsRetry = isRetry;

        if (!isRetry) {
          readyChunk.status = "uploading";
          // Set chunk timing (init_time and sent_at) based on socket queue state
          this.setChunkTiming(readyChunk, socketToUse, false);

          // Check queue health after setting timing
          this.checkQueueHealth({ chunk: readyChunk, socket: socketToUse });
          this.chunksSent.push(readyChunk.index);

          // Log initial chunk send event
          this.logChunkEvent(readyChunk.index, "CHUNK_INITIAL_SENT", {
            socketIndex: socket.index,
            chunkSize: readyChunk.size,
            totalBytesSent: this.chunksInfo.bytesSent,
            totalChunksSent: this.chunksSent.length,
          });

          // Invalidate cache when chunk is sent
          this.cachedUnackdChunks = null;
          this.chunksInfo.bytesSent += readyChunk.size;

          // Log chunk sent details (console only, not in debug logs)
          if (this.shouldLog()) {
            const { sizeMB, initTime, startTime, waitTime } =
              this.formatChunkInfoForLog(readyChunk, false);
            this.logToConsoleAndDebug(
              `Upload: Chunk ${readyChunk.index} sent - Size: ${sizeMB}, Init: ${initTime}, Start: ${startTime}, Wait: ${waitTime}, Sent: ${this.chunksSent.length}, ACKed: ${this.chunksAckd.size}`
            );
          }

          this.prepareNextChunks();
        } else {
          if (readyChunk.status === "retry") {
            readyChunk.status = "uploading";
          }
          if (!readyChunk.retries) readyChunk.retries = 0;
          const oldRetries = readyChunk.retries;
          readyChunk.retries += 1;
          readyChunk.real_sent_at = readyChunk.sent_at;

          // Set chunk timing (init_time and sent_at) based on socket queue state
          // Note: We already pushed the chunk above, so we check if length is 1 (was empty before push)
          const wasEmpty = socketToUse.chunksBeingSent.length === 1;
          const currentTime = Date.now();
          readyChunk.init_time = currentTime;

          if (wasEmpty) {
            // Socket was empty, chunk sent immediately
            readyChunk.sent_at = currentTime;
            if (this.shouldLog()) {
              C.log(
                `Upload: Retry chunk ${
                  readyChunk.index
                } sent immediately on empty socket ${
                  socketToUse.index
                }, sent_at set to ${new Date(readyChunk.sent_at).toISOString()}`
              );
            }

            // Detailed console log for retry chunk sent immediately
            const { sizeMB } = this.formatChunkInfoForLog(readyChunk, false);
            this.logToConsoleAndDebug(
              `[INFLIGHT] Retry Chunk ${
                readyChunk.index
              } - Size: ${sizeMB}, Socket: ${
                socketToUse.index
              }, Init: ${this.formatSimpleTime(
                readyChunk.init_time
              )}, Start: ${this.formatSimpleTime(
                readyChunk.sent_at
              )}, Wait: 0ms`
            );
          } else {
            // Socket had chunks, so this is queued
            readyChunk.sent_at = null;
            if (this.shouldLog()) {
              C.log(
                `Upload: Retry chunk ${readyChunk.index} queued on socket ${
                  socketToUse.index
                } (${
                  socketToUse.chunksBeingSent.length - 1
                } chunks ahead), sent_at set to null, init_time set to ${new Date(
                  readyChunk.init_time
                ).toISOString()}`
              );
            }

            // Detailed console log for queued retry chunk
            const sizeMB = this.getChunkSizeMB(readyChunk);
            const chunksAhead = socketToUse.chunksBeingSent.length - 1;
            this.logToConsoleAndDebug(
              `[QUEUED] Retry Chunk ${
                readyChunk.index
              } - Size: ${sizeMB}, Socket: ${
                socketToUse.index
              }, Init: ${this.formatSimpleTime(
                readyChunk.init_time
              )}, Ahead: ${chunksAhead}`
            );

            // Check queue health for retry chunk
            this.checkQueueHealth({ chunk: readyChunk, socket: socketToUse });
          }

          // Log chunk retry event
          this.logChunkEvent(readyChunk.index, "CHUNK_RETRY_SENT", {
            oldRetries,
            newRetries: readyChunk.retries,
            socketIndex: socket.index,
            isRetry: true,
            timeSinceLastSent: readyChunk.real_sent_at
              ? Date.now() - readyChunk.real_sent_at
              : null,
          });
        }

        if (this.showServerWriteProgress) {
          const socketInfo = this.uploadSummary.socketInfo.find(
            (s) => s.index === socket.index
          );
          if (socketInfo) {
            socketInfo.chunksSent++;
          }
        }

        if (
          this.showServerWriteProgress &&
          !this.uploadSummary.chunkDetails.find(
            (chunk) => chunk.index === readyChunk.index
          )
        ) {
          this.uploadSummary.chunkDetails.push({
            index: readyChunk.index,
            size: readyChunk.size,
            uploadStartTime: Date.now(),
          });
        }
      }
      if (!readyChunk) {
        this.prepareNextChunks();
      }

      if (!emptySocket && emptySockets && emptySockets.length > 0) {
        emptySockets.forEach((emptySocket) => this.sendChunks(emptySocket));
      }

      setTimeout(this.sendChunks, 200);
    } finally {
      this.isSendingChunks = false;
    }
  };

  sendInitialChunks = () => {
    this.prepareNextChunks(() => {
      this.sendChunks();
    });
    this.startPeriodicCheck();
  };

  // Helper method to log comprehensive upload configuration before upload starts
  logUploadConfiguration = () => {
    if (!this.streamingFileInfo) return;

    const file = this.streamingFileInfo;
    const startTime = new Date(Date.now()).toLocaleTimeString();

    this.logToConsoleAndDebug("\n" + "=".repeat(80));
    this.logToConsoleAndDebug("UPLOAD CONFIGURATION");
    this.logToConsoleAndDebug("=".repeat(80));
    this.logToConsoleAndDebug(
      `File Name: ${file.nameF || file.name || "Unknown"}`
    );
    this.logToConsoleAndDebug(
      `File Size: ${utils.formatBytes(file.size || 0)} (${(
        (file.size || 0) / this.MB
      ).toFixed(2)} MB)`
    );
    this.logToConsoleAndDebug(`Start Time: ${startTime}`);
    this.logToConsoleAndDebug(
      `Resume Location: ${utils.formatBytes(this.chunkStart || 0)}`
    );
    this.logToConsoleAndDebug("");
    this.logToConsoleAndDebug("Chunk Configuration:");
    this.logToConsoleAndDebug(
      `  Start Chunk Size: ${this.startChunkSizeMB.toFixed(2)} MB`
    );
    this.logToConsoleAndDebug(
      `  Current Chunk Size: ${(
        this.currentChunkSize || this.startChunkSizeMB
      ).toFixed(2)} MB`
    );
    this.logToConsoleAndDebug(
      `  Max Chunk Size: ${this.maxChunkSizeMB.toFixed(2)} MB`
    );
    this.logToConsoleAndDebug(
      `  Default Chunk Size: ${this.defaultChunkSize} MB`
    );
    this.logToConsoleAndDebug(`  Chunk Timeout: ${this.chunkTimeout} seconds`);
    this.logToConsoleAndDebug("");
    this.logToConsoleAndDebug("Socket Configuration:");
    this.logToConsoleAndDebug(`  Max Sockets: ${this.maxRequest}`);
    this.logToConsoleAndDebug(
      `  Server Write Threshold: ${this.serverWriteThreshold} MB`
    );
    this.logToConsoleAndDebug("");
    this.logToConsoleAndDebug("Retry Configuration:");
    this.logToConsoleAndDebug(`  Max Retries: ${this.maxRetries}`);
    this.logToConsoleAndDebug("");
    this.logToConsoleAndDebug("Other Settings:");
    this.logToConsoleAndDebug(
      `  Show Server Write Progress: ${
        this.showServerWriteProgress ? "Yes" : "No"
      }`
    );
    this.logToConsoleAndDebug(
      `  Chunk Preparation Timeout: ${this.chunkPreparationTimeout} seconds`
    );
    this.logToConsoleAndDebug("=".repeat(80) + "\n");
  };

  startUploading = (resumeLoc) => {
    this.chunkStart = resumeLoc;

    // Log comprehensive upload configuration before upload starts
    this.logUploadConfiguration();

    // Log upload start with initial byte tracking, chunk size, and timeout
    if (this.shouldLog()) {
      this.logAndDebug(
        `Upload: Start - Chunk: ${this.formatChunkSizeMB(
          this.currentChunkSize * this.MB
        )}, Timeout: ${this.chunkTimeout}s`
      );
    }

    this.prepareNextChunks(() => {}, resumeLoc);

    // Only schedule chunk size check if adjustment is possible (min != max)
    if (this.shouldAdjustChunkSize) {
      this.schedulechunkSizeCheck();
    } else {
      this.logAndDebug(
        `Upload: Skipping chunk size check scheduling - Start chunk size (${this.startChunkSizeMB.toFixed(
          2
        )}MB) equals Max chunk size (${this.maxChunkSizeMB.toFixed(
          2
        )}MB). No adjustment possible.`
      );
    }

    this.initializeSockets(() => {
      this.sendInitialChunks();
    });
  };

  convertChunkDataToBuffer = (chunk) => {
    const this_parent = this;
    return new Promise((resolve = () => {}, reject = () => {}) => {
      if (!chunk) reject();
      const startTime = Date.now();
      const reader = new FileReader();
      if (chunk.chunkData instanceof Blob) {
        reader.readAsArrayBuffer(chunk.chunkData);
      } else {
        C.warn(
          `Upload: Chunk data is not of type Blob, it is ${typeof chunk.chunkData}, Chunk ID: ${
            chunk.index
          } Status: ${chunk.status}`
        );
        reject();
      }
      reader.onload = (event) => {
        const chunkDataBuffer = event.target.result;
        const buffer = this_parent.createBuffer(
          chunk.chunkData.size,
          chunk.index,
          chunkDataBuffer
        );
        chunk.buffer = buffer;
        chunk.bufferringTime = (Date.now() - startTime) / 1000;
        resolve();
      };
    });
  };

  shouldItPrepareMoreChunks = () => {
    const lastChunk = this.chunksInfo.chunks[this.chunksInfo.chunks.length - 1];
    const chunks = this.chunksInfo.chunks;
    let haveEnoughReady = false;
    let readyChunks = 0;
    for (let i = chunks.length - 1; i >= 0; i--) {
      if (chunks[i].status === "init") {
        readyChunks++;
        if (readyChunks >= 30) {
          haveEnoughReady = true;
          break;
        }
      }
    }
    if (haveEnoughReady || (lastChunk && lastChunk.isLastChunk)) {
      this.lastChunkGeneratedTime = Date.now();
      return false;
    }
    return true;
  };

  startChunkPreparationStallCheck = () => {
    if (this.chunkPreparationStallCheckInterval) {
      clearInterval(this.chunkPreparationStallCheckInterval);
    }

    this.chunkPreparationStallCheckInterval = setInterval(() => {
      if (!this.lastChunkGeneratedTime) return;

      const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
      const wasStalled = this.chunkPreparationStalled;
      this.chunkPreparationStalled = timeElapsed > 36;
      if (wasStalled !== this.chunkPreparationStalled) {
        if (this.chunkPreparationStalled) {
          C.error(
            `Upload: Chunk preparation stalled. No new chunks generated in ${timeElapsed.toFixed(
              0
            )} seconds`
          );
        }
        this.prepareNextChunks();
        this.sendChunks();
        const file = this.streamingFileInfo;
        store.dispatch("toggleChunkPreparationStalled", {
          fileId: file.id,
          chunkPreparationStalled: this.chunkPreparationStalled,
        });
      }
    }, 1000);
  };

  clearChunkPreparationStallCheck = () => {
    if (this.chunkPreparationStallCheckInterval) {
      clearInterval(this.chunkPreparationStallCheckInterval);
      this.chunkPreparationStallCheckInterval = null;
    }
    this.chunkPreparationStalled = false;
  };

  prepareNextChunks = async (cb = () => {}, resumeLoc) => {
    if (!this.shouldItPrepareMoreChunks()) {
      return;
    }

    if (!this.lastChunkGeneratedTime) {
      this.lastChunkGeneratedTime = Date.now();
      this.startChunkPreparationTimeoutCheck();
      this.startChunkPreparationStallCheck();
    }

    let resume = resumeLoc;
    const file = this.streamingFile;
    const chunks = this.chunksInfo.chunks;
    const lastChunk = chunks[chunks.length - 1] || {};
    // Capture currentChunkSize at the start to ensure consistency for this chunk preparation
    // This prevents race conditions if reCalculateChunkSize runs concurrently
    const chunkSizeMB = this.currentChunkSize;
    let chunkSize = this.MB * chunkSizeMB;
    const chunkNum =
      lastChunk && typeof lastChunk.index !== "undefined"
        ? lastChunk.index + 1
        : 0;
    const chunkStart =
      resume ||
      (lastChunk && typeof lastChunk.start !== "undefined"
        ? lastChunk.start + lastChunk.size
        : 0);
    if (this.chunksBeingRead[chunkNum]) {
      cb();
      return;
    }
    this.chunksBeingRead[chunkNum] = true;

    // Adjust chunkSize for the last chunk
    if (file.size < chunkStart + chunkSize) {
      chunkSize = file.size - chunkStart;
    }

    // Handle precision issues for large files - ensure chunkSize is not negative or zero
    if (chunkSize <= 0) {
      // Check if we're actually at the end of the file
      if (chunkStart >= file.size) {
        if (this.shouldLog()) {
          C.log(
            `All chunks created, no more chunks to create, chunkSize: ${chunkSize}, chunkStart: ${chunkStart}, fileSize: ${file.size}`
          );
        }
        lastChunk.isLastChunk = true;
        this.uploadSummary.allChunksCreated = true;
        this.allChunksCreated = true;
        this.clearChunkPreparationTimeout();
        this.clearChunkPreparationStallCheck();
        cb();
        return;
      } else {
        // This might be a precision issue - try to create a minimal chunk
        // Only for large files to avoid issues with small files
        if (this.isLargeFile(file.size)) {
          const remainingBytes = file.size - chunkStart;
          if (remainingBytes > 0 && remainingBytes <= 1024 * 1024) {
            // Max 1MB for precision fix
            chunkSize = remainingBytes;
            C.warn(
              `Upload: Precision issue detected for large file, adjusting final chunk size to ${chunkSize} bytes`
            );
          } else {
            C.error(
              `Upload: Invalid chunk size calculation - chunkSize: ${chunkSize}, chunkStart: ${chunkStart}, fileSize: ${file.size}, remainingBytes: ${remainingBytes}`
            );
            this.handleChunkPreparationFailure();
            return;
          }
        } else {
          if (this.shouldLog()) {
            C.log(
              `All chunks created, no more chunks to create, chunkSize: ${chunkSize}, chunkStart: ${chunkStart}, fileSize: ${file.size}`
            );
          }
          lastChunk.isLastChunk = true;
          this.uploadSummary.allChunksCreated = true;
          this.allChunksCreated = true;
          this.clearChunkPreparationTimeout();
          this.clearChunkPreparationStallCheck();
          cb();
          return;
        }
      }
    }
    let chunkData = getChunk(file, chunkStart, chunkSize);
    if (!chunkData) {
      C.warn(`Upload: Chunk data is null, Chunk ID: ${chunkNum}`);
      this.handleChunkPreparationFailure();
      return;
    }
    const chunk = {
      index: chunkNum,
      start: chunkStart,
      size: chunkSize,
      status: "init",
      chunkData,
      retries: 0,
    };
    await this.convertChunkDataToBuffer(chunk);
    cb();
    this.chunksInfo.chunks.push(chunk);
    this.lastChunkGeneratedTime = Date.now();
    delete this.chunksBeingRead[chunkNum];
    if (resume) resume = resume + chunkSize;
    if (this.shouldItPrepareMoreChunks()) {
      this.prepareNextChunks(cb);
    }
  };

  startChunkPreparationTimeoutCheck = () => {
    if (this.chunkPreparationTimeoutId) {
      clearTimeout(this.chunkPreparationTimeoutId);
    }

    const checkTimeout = () => {
      if (!this.lastChunkGeneratedTime) return;

      const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
      if (timeElapsed >= this.chunkPreparationTimeout) {
        this.handleChunkPreparationFailure();
      } else {
        this.chunkPreparationTimeoutId = setTimeout(checkTimeout, 10000);
      }
    };
    this.chunkPreparationTimeoutId = setTimeout(checkTimeout, 10000);
  };

  clearChunkPreparationTimeout = () => {
    if (this.chunkPreparationTimeoutId) {
      clearTimeout(this.chunkPreparationTimeoutId);
      this.chunkPreparationTimeoutId = null;
    }
    this.lastChunkGeneratedTime = null;
  };

  handleChunkPreparationFailure = () => {
    const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
    C.error(
      `Upload: Chunk preparation stalled. No new chunks generated in ${timeElapsed.toFixed(
        0
      )} seconds`
    );
    this.stopAllIntervals();

    // Close all sockets
    this.closeSockets();

    // Mark the upload as failed
    const file = this.streamingFileInfo;
    handleFileOpenError(file, t("failedToPrepareFileChunksOperationStalled"));

    // Show error message to user
    invoke("growl", [
      t("failedToPrepareFileChunks"),
      t("operationStalled"),
      5000,
      true,
    ]);

    // Close the file stream
    this.closeFileUsingBlockUnBlock(false, null, {
      status: "failed",
      uploadingError: t("failedToPrepareFileChunksOperationStalled"),
    });

    // Move to next file in queue
    this.nextFileInQueue();
  };

  queueNextChunk = () => {
    if (this.isPaused || this.fileClosed) {
      return;
    }
    this.sendChunks();
  };

  handleError = async (socket) => {
    if (this.isPaused || this.fileClosed || this.retries > this.maxRetries) {
      return;
    }

    C.log(`Upload: Handle Error, Active Sockets: ${this.sockets.length}`);

    try {
      const fileOpen = await this.checkFileOpenStatus();
      if (!fileOpen) {
        return;
      }
      if (this.sockets.length === 0) {
        this.handleRetry(this.retries, () => {
          this.startUploading(this.chunkStart);
        });
      } else {
        C.log(
          `Upload: Handle Error, Active Sockets: ${this.sockets.length}, Max Requests: ${this.maxRequest}`
        );
        if (this.sockets.length >= this.maxRequest) return;
        this.createSocket(() => {
          this.totalSocketsRespawned++;
          C.log(`Upload: Socket created, starting upload`);
          this.sendChunks();
        }, socket.isRetrySocket);
      }
    } catch (error) {}
  };

  handleRetry = async (retries, cb) => {
    if (this.isPaused || this.fileClosed) {
      return;
    }
    const callback = async () => {
      this.closeSockets();
      this.sockets = [];
      this.openSockets = []; // Clear open sockets array
    };

    const retryCallback = () => {
      this.retries++;
    };

    handleRetryUploading(this, retries, cb, callback, retryCallback);
  };

  updateTimeStatusOfUploadingFile = (resumeLoc = 0) => {
    updateTimeStatusOfUploadingFileCommon(resumeLoc, this);
  };

  nextFileInQueue = (canceled) => {
    uploadingQueue.nextFileInQueue(canceled);
  };

  stopFileUploadingQueueOnQuotaStorageExceeded = (message) => {
    uploadingQueue.stopFileUploadingQueueOnQuotaStorageExceeded(message);
  };

  uploadDir = async (nameF, isDir = false) => {
    await ensureUploadDirectory(nameF, isDir, uploadingQueue);
  };

  // open file stream - creating bucket to upload chunks
  openFileStream = async (file) => {
    // Clear all debug logs before starting each new upload to avoid mixing data between files
    // and to prevent memory growth when many files are uploaded sequentially.
    C.clearDebugLogs();
    // Clear debugLogs array to prevent memory leak when starting new upload
    if (this.debugLogs) {
      this.debugLogs = [];
    }

    this.initializeVariables();
    this.setInitialUploadState(file);

    if (file.isDir) {
      await this.handleDirectoryUpload(file);
      return;
    }

    try {
      C.time(`Upload: Checking file partially uploaded`);
      const resp = await checkFilePartiallyUploaded(file);
      let resumeLoc = 0;
      if (!resp) {
        resumeLoc = 0;
      } else {
        let stats = resp.split(" ");
        stats = stats.filter((item) => item !== "");
        resumeLoc = parseInt(stats[4]);
        if (resumeLoc >= file.size) {
          resumeLoc = 0;
        }
        this.chunksInfo.bytesUploaded = resumeLoc;
      }

      C.timeEnd(`Upload: Checking file partially uploaded`);
      C.time(`Upload: Uploading directory`);
      try {
        await this.uploadDir(file.nameF);
      } catch (err) {
        this.errorToConsoleAndDebug(
          `Failed to upload directory: ${err.message || err}`
        );
        failDirectoryPrep(this, file, err);
        C.timeEnd(`Upload: Uploading directory`);
        return;
      }
      C.timeEnd(`Upload: Uploading directory`);
      C.time(`Upload: Opening file for upload`);
      await this.openFileForUpload(file, resumeLoc);
      C.timeEnd(`Upload: Opening file for upload`);
    } catch (error) {
      handleFileOpenError(file, error);
    }
  };

  setInitialUploadState = (file) => {
    this.retries = 0;
    this.isPaused = false;
    this.closeFileCalled = false;
    this.closeFileRetries = 0;
    if (!this.uploadStartTime) {
      this.uploadStartTime = Date.now();
    }
    this.uploadingStartedAt = Date.now();
    this.streamingFileInfo = file;
    this.streamingFile = file.file;
  };

  stopAllIntervals = () => {
    this.clearChunkPreparationStallCheck();
    this.stopServerWriteUpdates();
    this.stopPeriodicCheck();
    this.clearChunkPreparationTimeout();

    this.lastChunkGeneratedTime = null;
    this.lastChunkSentTime = null;
    this.lastLogTime = null;
    this.bufferedAmountUnchangedSince = null;
    this.lastBufferedAmounts = null;
    this.lastPeriodicCheckTime = null;
    this.lastChunkMultiSocketRetryTime = null;
    if (this.chunkSizeTimerId) {
      clearTimeout(this.chunkSizeTimerId);
      this.chunkSizeTimerId = null;
    }

    clearInterval(this.chunksInfo.intervalId);
    clearInterval(this.chunkRetryInterval);
  };

  // Additional cleanup method to ensure all timers are cleared
  cleanupAllTimers = () => {
    // Clear any remaining intervals
    if (this.serverWriteUpdateInterval) {
      clearInterval(this.serverWriteUpdateInterval);
      this.serverWriteUpdateInterval = null;
    }
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
      this.periodicCheckInterval = null;
    }
    if (this.chunkPreparationStallCheckInterval) {
      clearInterval(this.chunkPreparationStallCheckInterval);
      this.chunkPreparationStallCheckInterval = null;
    }
    if (this.chunkPreparationTimeoutId) {
      clearTimeout(this.chunkPreparationTimeoutId);
      this.chunkPreparationTimeoutId = null;
    }
    if (this.chunkRetryInterval) {
      clearInterval(this.chunkRetryInterval);
      this.chunkRetryInterval = null;
    }
    if (this.chunksInfo.intervalId) {
      clearInterval(this.chunksInfo.intervalId);
      this.chunksInfo.intervalId = null;
    }

    // Clear all debounced function timeouts
    Object.values(this.debouncedUpdates).forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    this.debouncedUpdates = {
      summaryPanel: null,
      summaryRow: null,
      serverWriteProgress: null,
    };
  };

  handleDirectoryUpload = async (file) => {
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        status: "uploading",
      },
    });

    try {
      await this.uploadDir(file.nameF, file.isDir);
      this.completeDirectoryUpload(file);
    } catch (error) {
      failDirectoryPrep(this, file, error);
    }
  };

  completeDirectoryUpload = (file) => {
    this.uploadingEndedAt = Date.now();
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        status: "done",
        time: this.uploadingEndedAt - this.uploadingStartedAt,
      },
    });
    this.nextFileInQueue();
  };

  openFileForUpload = async (file, resumeLoc) => {
    const startTime = Date.now();
    this.streamingFileInfo = file;
    this.streamingFile = file.file;
    const customFormData = this.streamingFileInfo?.customFormData || {};
    const formValues = store?.customCommonUploadFormValues || {};

    // Log open file called
    this.logActionAndDebug("file", "Open file called", {
      fileId: file?.reUploadId || file.id,
      fileName: file.nameF,
      fileSize: file.size,
      resumeLoc: resumeLoc,
    });

    try {
      this.openFileStartTime = Date.now();
      const metaUserInfo = invoke("getMetaUserInfo");
      const response = await openFile({
        ...formValues,
        ...customFormData,
        ...metaUserInfo,
        upload_path: file.nameF,
        upload_size: file.size,
        upload_id: file?.reUploadId || file.id,
        start_resume_loc: resumeLoc,
      });
      this.openFileEndTime = Date.now();

      this.uploadSummary.openFileTime = Date.now() - startTime;

      // Log open file success
      this.logActionAndDebug("file", "Open file success", {
        fileId: file?.reUploadId || file.id,
        duration: this.uploadSummary.openFileTime,
      });

      handleFileOpenResponse(this, file, response, resumeLoc);
    } catch (err) {
      this.openFileEndTime = Date.now();

      // Log open file failed
      this.logActionAndDebug("file", "Open file failed", {
        fileId: file?.reUploadId || file.id,
        error: err?.message || String(err),
      });

      handleFileOpenError(file, err);
    }
  };

  failFileOpen = (file, response) => {
    failFileOpenCommon(this, file, response);
  };

  startFileUpload = (file, resumeLoc) => {
    startFileUploadCommon(this, file, resumeLoc);
    this.startServerWriteUpdates();
  };

  handleCloseFileResponse = async (
    response,
    file,
    fileInfo,
    finalizingTimeoutId
  ) => {
    const { id: fileId, reUploadId } = fileInfo || {};
    response =
      typeof response === "string" ? decodeURIComponent(response) : response;

    if (
      typeof response === "string" &&
      response.toLowerCase().includes("error")
    ) {
      this.stopAllIntervals();
      this.stopZeroByteChunkSending();
      this.closeSockets();
      this.fileClosed = true;
      this.uploadDone = true;
      if (finalizingTimeoutId) clearTimeout(finalizingTimeoutId);
      if (this.chunkRetryInterval) {
        clearInterval(this.chunkRetryInterval);
        this.chunkRetryInterval = null;
      }

      handleFileOpenError(file, response);
      invoke("growl", [response, t("fileClosingFailed"), 5000, true]);

      try {
        store.dispatch("disableGlobalUpload");
      } catch (e) {}
      const globalUploadsEl = document.getElementById("globalUploads");
      if (globalUploadsEl) {
        globalUploadsEl.classList.add("is-hidden");
        globalUploadsEl.innerHTML = "";
      }

      try {
        this.nextFileInQueue(true);
      } catch (e) {}

      return { shouldRetry: false, error: response };
    }
    try {
      if (response.startsWith("ERROR:No such upload open") || response === "") {
        const fileSize =
          (fileInfo && fileInfo.size) || (file && file.size) || 0;
        const bytesConfirmed =
          (this.chunksInfo && this.chunksInfo.bytesSent) || 0;
        const mentionsQuota = response.toLowerCase().indexOf("quota") >= 0;
        // Never show success for a non-empty file when no bytes were
        // confirmed written, or when the server mentioned quota: that is a
        // silent failure, not a finished upload.
        if (
          (this.closeFileRetries > 0 || this.closeFileCalled) &&
          !mentionsQuota &&
          !(fileSize > 0 && bytesConfirmed === 0)
        ) {
          if (this.shouldLog()) {
            this.logToConsoleAndDebug(
              `Upload: close file response: ${response}, Marking as success`
            );
          }
          this.logImportantEvent("CLOSE_FILE_CONSIDERED_SUCCESS", {
            uploadId: fileInfo.reUploadId || fileInfo.id,
            response:
              response.trim() +
              " But as either closeFile call was finalizing or its not the first attempt, considering it as success. closeFileRetries: " +
              this.closeFileRetries +
              ", closeFileCalled: " +
              this.closeFileCalled,
            timestamp: new Date().toISOString(),
          });
          response = "Success";
        } else {
          if (this.shouldLog()) {
            this.logToConsoleAndDebug(
              `Upload: Response: ${response}, Treating as error (no confirmed bytes or quota mentioned)`
            );
          }
        }
      } else {
        const pendingBytesResponse = response.match(/Pending bytes: (\d+)/);
        if (
          pendingBytesResponse &&
          parseInt(pendingBytesResponse[1]) === 0 &&
          response.toLowerCase().indexOf("quota") < 0
        ) {
          if (this.shouldLog()) {
            this.logToConsoleAndDebug(
              `Upload: Pending bytes: 0, Response: ${response}`
            );
          }
          response = "Success";
        }
      }
    } catch (err) {}
    if (response.startsWith("Decompressing")) {
      // Log retry decision
      C.logAction(
        "file",
        "CloseFile response: Decompressing - will retry",
        {
          fileId: reUploadId || fileId || "unknown",
          response: response.trim(),
          retryReason: "decompressing",
          timestamp: Date.now(),
        },
        true
      );

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
        },
      });
      return { shouldRetry: true };
    } else if (response.startsWith("Finalizing")) {
      const pendingBytesMatch = response.match(/Pending bytes: (\d+)/);
      let finalizingMsg = "Finalizing";
      let pendingBytes = null;

      if (pendingBytesMatch && pendingBytesMatch[1]) {
        pendingBytes = parseInt(pendingBytesMatch[1]);
        const totalBytes = file.size;
        const pendingFormatted = utils.formatBytes(pendingBytes);
        const totalFormatted = utils.formatBytes(totalBytes);
        finalizingMsg = `Finalizing (Pending: ${pendingFormatted}/${totalFormatted})`;
      }

      // Log retry decision
      C.logAction(
        "file",
        "CloseFile response: Finalizing - will retry",
        {
          fileId: reUploadId || fileId || "unknown",
          response: response.trim(),
          pendingBytes: pendingBytes,
          retryReason: "finalizing",
          timestamp: Date.now(),
        },
        true
      );

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
          finalizingMsg: finalizingMsg,
        },
      });
      return { shouldRetry: true };
    } else if (response.indexOf("ERROR:") >= 0) {
      if (
        (response.toLowerCase().indexOf("quota") >= 0 &&
          response.toLowerCase().indexOf("exceeded") >= 0) ||
        decodeURIComponent(response)
          .toLowerCase()
          .indexOf("no such upload open") >= 0
      ) {
        this.stopAllIntervals();
        handleFileOpenError(file, decodeURIComponent(response));
        invoke("growl", [
          decodeURIComponent(response),
          t("fileClosingFailed"),
          4000,
          true,
        ]);
        this.stopFileUploadingQueueOnQuotaStorageExceeded(
          decodeURIComponent(response)
            .toLowerCase()
            .indexOf("no such upload open") >= 0
            ? decodeURIComponent(response)
            : false
        );
        return { shouldRetry: false, error: response };
      }
      this.uploadSummary.closeFileTime = Date.now() - startTime;
      if (this.shouldLog()) {
        this.logFinalUploadSummary();
      }
      const serverWriteProgress = document.querySelector("#server-write-progress-upload");
      if (serverWriteProgress) {
        _$(serverWriteProgress).hide();
      }
      this.handleCloseFileRetry(response);
      return { shouldRetry: false };
    }

    clearInterval(this.chunkRetryInterval);
    clearTimeout(finalizingTimeoutId);

    this?.sockets?.forEach((socket) => {
      if (this.isSocketOpen(socket)) {
        socket?.close();
      }
    });

    removeEventListener("beforeunload", beforeUnloadEvent);

    this.uploadingEndedAt = Date.now();

    // Ensure uploaded bytes are preserved when marking file as done
    const actualUploadedBytes = this.chunksInfo.bytesUploaded || file.size;
    const updatedFile = {
      ...file,
      status: "done",
      time: this.uploadingEndedAt - this.uploadingStartedAt,
      uploadedBytes: actualUploadedBytes, // Preserve uploaded bytes
    };

    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: updatedFile,
    });

    clearInterval(this.chunksInfo.intervalId);
    C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);

    this.nextFileInQueue();
    return { shouldRetry: false };
  };

  closeFileStream = async (file) => {
    // Increment call counter FIRST (before any early returns)
    this.closeFileStreamCallCount++;
    const callAttempt = this.closeFileStreamCallCount;

    // Log close file called (before early return check)
    const wasAlreadyClosed = this.fileClosed;
    const { id: fileId, nameF, size, lastModified, reUploadId } = file || {};

    // Force log with unique context to prevent deduplication
    const uniqueContext = {
      fileId: reUploadId || fileId,
      fileName: nameF,
      fileSize: size,
      totalChunks: this.chunksInfo?.chunks?.length || 0,
      callAttempt: callAttempt, // Track which attempt this is
      timestamp: Date.now(), // Add timestamp to make it unique
      ...(wasAlreadyClosed && { alreadyClosed: true }),
    };

    this.logActionAndDebug("file", "Close file called", uniqueContext, true); // Skip deduplication completely
    this.logToConsoleAndDebug(
      `[DEBUG] Close file called (attempt #${callAttempt}) with context: ${JSON.stringify(
        uniqueContext
      )}`
    );

    if (this.fileClosed) {
      // Already closed, but log it anyway
      C.logAction(
        "file",
        "Close file skipped (already closed)",
        {
          fileId: reUploadId || fileId,
          callAttempt: callAttempt,
          timestamp: Date.now(),
        },
        true // Skip deduplication for skipped calls too
      );
      return;
    }

    this.fileClosed = true;
    this.stopZeroByteChunkSending(); // Stop zero-byte chunk sending when file stream closes
    const startTime = Date.now();
    const fileInfo = this.streamingFileInfo;

    const finalizingTimeoutId = setTimeout(() => {
      const updatedFileToCanceling = {
        ...fileInfo,
        status: "finalizing",
        finalizingMsg: "Finalizing",
      };

      store.dispatch("updateFileStatusAndRerenderFile", {
        [fileId]: updatedFileToCanceling,
      });

      store.dispatch("updateFileTimeStatus", {
        fileId,
        speed: 0,
        elapsedTime: this.chunksInfo.elapsedTime,
      });
      const actualUploadedBytes = this.chunksInfo.bytesUploaded || file.size;
      store.dispatch("updateFileUploadStatus", {
        fileId,
        size: actualUploadedBytes,
        maxSize: size,
      });
    }, 3000);

    try {
      const chunks_arr_length = this.chunksInfo.chunks.length;
      const total_chunks = !chunks_arr_length && size ? 1 : chunks_arr_length;
      const closeFileStartTime = Date.now();

      // Log closeFile API call before making it
      C.logAction(
        "file",
        "CloseFile API called",
        {
          fileId: reUploadId || fileId,
          totalChunks: total_chunks,
          totalBytes: size,
          callAttempt: callAttempt,
          retryCount: this.closeFileRetries,
          timestamp: Date.now(),
        },
        true
      );

      const response = await closeFile({
        upload_id: reUploadId || fileId,
        total_chunks,
        total_bytes: size,
        filePath: nameF,
        lastModified: lastModified,
      });
      this.closeFileCalled = true;
      const closeFileDuration = Date.now() - closeFileStartTime;

      // Determine response status
      let responseStatus = "unknown";
      if (
        response.trim().toLowerCase() === "closed" ||
        response.trim().toLowerCase() === "success"
      ) {
        responseStatus = "closed";
      } else if (response.startsWith("Decompressing")) {
        responseStatus = "decompressing";
      } else if (response.startsWith("Finalizing")) {
        responseStatus = "finalizing";
      } else if (response.indexOf("ERROR:") >= 0) {
        responseStatus = "error";
      } else {
        responseStatus = "other";
      }

      // Log closeFile API response with status
      C.logAction(
        "file",
        "CloseFile API response",
        {
          fileId: reUploadId || fileId,
          duration: closeFileDuration,
          response: response.trim(),
          responseStatus: responseStatus,
          callAttempt: callAttempt,
          retryCount: this.closeFileRetries,
          timestamp: Date.now(),
        },
        true
      ); // Skip deduplication completely

      C.log(`Upload: File close response: ${response}`);

      // Log important close file operation
      this.logImportantEvent("CLOSE_FILE", {
        uploadId: reUploadId || fileId,
        totalChunks: total_chunks,
        totalBytes: size,
        filePath: nameF,
        response: response.trim(),
        duration: closeFileDuration,
        retryCount: this.closeFileRetries,
        timestamp: new Date().toISOString(),
      });

      const { shouldRetry, errorMsg } = await this.handleCloseFileResponse(
        response,
        file,
        fileInfo,
        finalizingTimeoutId
      );

      // Log retry decision
      if (shouldRetry) {
        C.logAction(
          "file",
          "CloseFile retry scheduled",
          {
            fileId: reUploadId || fileId,
            callAttempt: callAttempt,
            retryCount: this.closeFileRetries,
            response: response.trim(),
            retryDelay: 2000,
            timestamp: Date.now(),
          },
          true
        );
      }

      if (shouldRetry) {
        const retryCloseFile = async () => {
          // Log retry execution started
          C.logAction(
            "file",
            "CloseFile retry execution started",
            {
              fileId: reUploadId || fileId,
              callAttempt: callAttempt,
              retryCount: this.closeFileRetries + 1, // Will be incremented next
              timestamp: Date.now(),
            },
            true
          );
          try {
            this.closeFileRetries++;
            const retryStartTime = Date.now();

            // Log closeFile API retry call before making it
            C.logAction(
              "file",
              "CloseFile API retry called",
              {
                fileId: reUploadId || fileId,
                totalChunks: total_chunks,
                totalBytes: size,
                callAttempt: callAttempt,
                retryCount: this.closeFileRetries,
                timestamp: Date.now(),
              },
              true
            );

            const retryResponse = await closeFile({
              upload_id: reUploadId || fileId,
              total_chunks,
              total_bytes: size,
              filePath: nameF,
              lastModified: lastModified,
            });

            const retryDuration = Date.now() - retryStartTime;

            // Determine retry response status
            let retryResponseStatus = "unknown";
            if (
              retryResponse.trim().toLowerCase() === "closed" ||
              retryResponse.trim().toLowerCase() === "success"
            ) {
              retryResponseStatus = "closed";
            } else if (retryResponse.startsWith("Decompressing")) {
              retryResponseStatus = "decompressing";
            } else if (retryResponse.startsWith("Finalizing")) {
              retryResponseStatus = "finalizing";
            } else if (retryResponse.indexOf("ERROR:") >= 0) {
              retryResponseStatus = "error";
            } else {
              retryResponseStatus = "other";
            }

            // Log closeFile API retry response with status
            C.logAction(
              "file",
              "CloseFile API retry response",
              {
                fileId: reUploadId || fileId,
                duration: retryDuration,
                retryCount: this.closeFileRetries,
                response: retryResponse.trim(),
                responseStatus: retryResponseStatus,
                callAttempt: callAttempt, // Track which call attempt this retry belongs to
                timestamp: Date.now(), // Make it unique
              },
              true // Skip deduplication completely
            );

            C.log(`Upload: File close retry response: ${retryResponse}`);

            // Log important close file retry operation
            this.logImportantEvent("CLOSE_FILE_RETRY", {
              uploadId: reUploadId || fileId,
              totalChunks: total_chunks,
              totalBytes: size,
              filePath: nameF,
              response: retryResponse.trim(),
              duration: retryDuration,
              retryCount: this.closeFileRetries,
              timestamp: new Date().toISOString(),
            });
            const { shouldRetry: shouldRetryAgain, errorMsg: errorMsg2 } =
              await this.handleCloseFileResponse(
                retryResponse,
                file,
                fileInfo,
                finalizingTimeoutId
              );

            if (shouldRetryAgain) {
              // Log retry scheduled again
              C.logAction(
                "file",
                "CloseFile retry scheduled again",
                {
                  fileId: reUploadId || fileId,
                  callAttempt: callAttempt,
                  retryCount: this.closeFileRetries,
                  response: retryResponse.trim(),
                  retryDelay: 2000,
                  timestamp: Date.now(),
                },
                true
              );
              setTimeout(retryCloseFile, 2000);
            }
          } catch (err) {
            // Log closeFile API retry error
            C.logAction(
              "file",
              "CloseFile API retry error",
              {
                fileId: reUploadId || fileId || "unknown",
                retryCount: this.closeFileRetries,
                error: err?.message || String(err),
                responseStatus: "error",
                callAttempt: callAttempt, // Track which call attempt this retry belongs to
                timestamp: Date.now(), // Make it unique
              },
              true // Skip deduplication completely
            );

            // Log important close file retry error
            this.logImportantEvent("CLOSE_FILE_RETRY_ERROR", {
              uploadId: reUploadId || fileId,
              error: err.message,
              retryCount: this.closeFileRetries,
              timestamp: new Date().toISOString(),
            });

            clearTimeout(finalizingTimeoutId);
            clearInterval(this.chunkRetryInterval);
            this.handleCloseFileRetry();
            const messageToShow = errorMsg || errorMsg2 || err.message;
            invoke("growl", [
              "",
              t("somethingWentWrongWhileClosingFile") + " " + messageToShow,
              5000,
              true,
            ]);
            C.error(`Upload:Error occurred during closeFile retry, ${err}`);
          }
        };

        setTimeout(retryCloseFile, 2000);
        return;
      }
    } catch (err) {
      // Log closeFile API error
      C.logAction(
        "file",
        "CloseFile API error",
        {
          fileId: reUploadId || fileId || "unknown",
          error: err?.message || String(err),
          responseStatus: "error",
          callAttempt: callAttempt, // Track which call attempt failed
          timestamp: Date.now(), // Make it unique
        },
        true // Skip deduplication completely
      );

      // Log important close file error
      this.logImportantEvent("CLOSE_FILE_ERROR", {
        uploadId: reUploadId || fileId,
        error: err.message,
        retryCount: this.closeFileRetries,
        timestamp: new Date().toISOString(),
      });

      clearTimeout(finalizingTimeoutId);
      clearInterval(this.chunkRetryInterval);
      this.handleCloseFileRetry();
      const messageToShow = err.message;
      invoke("growl", [
        "",
        t("somethingWentWrongWhileClosingFile") + " " + messageToShow,
        5000,
        true,
      ]);
      C.error(`Upload:Error occurred during closeFile, ${err}`);
    }

    this.uploadSummary.closeFileTime = Date.now() - startTime;
    if (this.shouldLog()) {
      this.logFinalUploadSummary();
    }
    const serverWriteProgress = document.querySelector("#server-write-progress-upload");
    if (serverWriteProgress) {
      _$(serverWriteProgress).hide();
    }
  };

  handleCloseFileRetry = async (err) => {
    await handleCloseFileRetryCommon(this, err);
  };

  pausing = () => {
    pausingUpload(this);
  };

  pause = () => {
    pauseUpload(this);
    this.stopServerWriteUpdates();
    this.stopPeriodicCheck();
    this.stopZeroByteChunkSending(); // Stop zero-byte chunk sending on pause
  };

  resume = () => {
    resumeUpload(this, this.queueNextChunk);
    this.startServerWriteUpdates();
    this.startPeriodicCheck(); // Restart periodic check
  };

  getSocketInfo = () => {
    return this.sockets.map((socket) => ({
      index: socket.index,
      chunksBeingSent: socket.chunksBeingSent.length,
      bufferSize: (socket.bufferedAmount / this.MB).toFixed(2),
      chunksAcked: socket.chunksAckd.length,
      isStuck: socket.isStuck,
    }));
  };

  closeFileUsingBlockUnBlock = (
    preStatus = {
      status: "canceling",
    },
    postStatus = {
      status: "canceled",
    }
  ) => {
    const fileInfo = this.streamingFileInfo;
    const { id: fileId, nameF, size, reUploadId } = fileInfo || {};

    // Log wrapper method call
    C.logAction(
      "file",
      "Close called",
      {
        fileId: reUploadId || fileId || "unknown",
        fileName: nameF || "unknown",
        fileSize: size || 0,
        preStatus: preStatus?.status || "unknown",
        postStatus: postStatus?.status || "unknown",
        timestamp: Date.now(),
      },
      true
    );

    const beforeClosingFile = async () => {
      this.stopAllIntervals();
      this.closeSockets();
    };

    closeFileUsingBlockUnBlockUpload(
      preStatus,
      postStatus,
      beforeClosingFile,
      this
    );
  };

  cancel = async () => {
    C.error(
      `Upload: User canceled, Canceling file: ${this.streamingFileInfo.nameF}`
    );
    this.stopZeroByteChunkSending(); // Stop zero-byte chunk sending on cancel
    this.stopAllIntervals();
    this.closeFileUsingBlockUnBlock();
  };

  uploadTheNextSetOfChunk = (resumeLoc) => {
    this.startUploading(resumeLoc);
    this.handleUpdateUploadingStatus(true);
  };

  handleUpdateUploadingStatus = (uploading) => {
    uploadingQueue.handleUpdateUploadingStatus(uploading);
  };

  logFinalUploadSummary = () => {
    if (!this.showServerWriteProgress) return;

    const totalUploadTime = this.uploadSummary.chunkDetails.reduce(
      (sum, chunk) => sum + (chunk.timeTaken || 0),
      0
    );
    this.uploadSummary.averageUploadTime =
      totalUploadTime / this.uploadSummary.chunkDetails.length;

    this.uploadSummary.totalChunks = this.uploadSummary.chunkDetails.length;

    const chunksWithoutRetries = [];
    const chunksWithRetries = [];

    this.uploadSummary.chunkDetails.forEach((chunk) => {
      const chunkInfo = {
        index: chunk.index,
        size: `${(chunk.size / this.MB).toFixed(2)} MB`,
        retries: chunk.retries,
        bufferringTime: chunk.bufferringTime
          ? `${chunk.bufferringTime.toFixed(2)} seconds`
          : "N/A",
        timeTakenReal: chunk.timeTakenReal
          ? `${chunk.timeTakenReal.toFixed(2)} seconds`
          : "N/A",
        timeTaken: chunk.timeTaken
          ? `${chunk.timeTaken.toFixed(2)} seconds`
          : "N/A",
      };

      if (!chunk.retries) {
        chunksWithoutRetries.push(chunkInfo);
      } else {
        chunksWithRetries.push(chunkInfo);
      }
    });

    // Format socket failures for summary
    const socketFailures = this.uploadSummary.socketFailures.map((failure) => ({
      socketIndex: failure.socketIndex,
      type: failure.type,
      timestamp: failure.timestamp,
      details: {
        chunksBeingSent: failure.details.socket.chunksBeingSent,
        chunksAckd: failure.details.socket.chunksAckd,
        bufferedAmount: `${(
          failure.details.socket.bufferedAmount / this.MB
        ).toFixed(2)} MB`,
      },
    }));

    const summary = {
      totalChunks: this.uploadSummary.totalChunks,
      averageUploadTime: `${this.uploadSummary.averageUploadTime.toFixed(
        2
      )} seconds`,
      openFileTime: `${(this.uploadSummary.openFileTime / 1000).toFixed(
        2
      )} seconds`,
      closeFileTime: `${(this.uploadSummary.closeFileTime / 1000).toFixed(
        2
      )} seconds`,
      socketInfo: this.uploadSummary.socketInfo.map((socket) => ({
        index: socket.index,
        timeTakenToOpen: `${(socket.timeTakenToOpen / 1000).toFixed(
          2
        )} seconds`,
        chunksSent: socket.chunksSent,
      })),
      sendChunksCallCount: this.sendChunksCallCount,
      sendChunksRejections: this.uploadSummary.sendChunksRejections,
      chunksWithoutRetries,
      chunksWithRetries,
      socketFailures, // Add socket failures to summary
    };

    C.log("Final Upload Summary:", summary);

    // Log chunk events summary using common method
    if (this.uploadSummary.chunkEvents.length > 0) {
      const chunkEventsLines = this.formatChunkEventsSummary("lines");
      chunkEventsLines.forEach((line) => {
        if (line.trim()) {
          C.log(line);
        }
      });
    }

    const debugText = this.formatDebugInfo();
    C.log("Detailed Summary:", debugText);
  };

  startServerWriteUpdates = () => {
    if (this.showServerWriteProgress) {
      if (this.serverWriteUpdateInterval) {
        clearInterval(this.serverWriteUpdateInterval);
      }
      this.serverWriteUpdateInterval = setInterval(() => {
        // Stop updates if upload is done to reduce CPU usage
        if (this.isPaused || this.fileClosed || this.uploadDone) {
          this.stopServerWriteUpdates();
          return;
        }

        const serverYetToWrite = this.getSizeOfServerYetToWrite();
        this.debounce(
          () => this.updateServerWriteProgress(serverYetToWrite),
          200,
          "serverWriteProgress"
        );
      }, 1000);
    }
  };

  stopServerWriteUpdates = () => {
    if (this.showServerWriteProgress) {
      if (this.serverWriteUpdateInterval) {
        clearInterval(this.serverWriteUpdateInterval);
        this.serverWriteUpdateInterval = null;
      }
      const progressDiv = document.getElementById(
        "server-write-progress-upload"
      );
      if (progressDiv) {
        _$(progressDiv).hide();
      }
    }
  };

  copyDebugInfo = (event) => {
    if (!this.showServerWriteProgress) {
      invoke("growl", [
        "Debug information is disabled",
        "Enable showServerWriteProgress to view debug info",
        3000,
        false,
      ]);
      return;
    }

    // Get the current uploading file instance to ensure we always copy the right file's debug info
    const currentUploadInstance =
      uploadingQueue.getCurrentUploadingFileInstance();

    // Use the current uploading instance if available, otherwise fall back to this instance
    const uploadInstance = currentUploadInstance || this;
    const debugInfo = uploadInstance.formatDebugInfo();

    // Check if alt key is pressed
    if (event && event.altKey) {
      // Create filename with date-time
      const now = new Date();
      const dateTime = now
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "-")
        .slice(0, 19);
      const filename = `file_upload_stats-${dateTime}.txt`;

      // Create and download file
      const blob = new Blob([debugInfo], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      invoke("growl", [t("debugInformationDownloadedAsFile"), "", 2000, false]);
    } else {
      // Original clipboard copy behavior
      navigator.clipboard
        .writeText(debugInfo)
        .then(() => {
          invoke("growl", [
            t("debugInformationCopiedToClipboard"),
            "",
            2000,
            false,
          ]);
        })
        .catch((err) => {
          this.errorToConsoleAndDebug(
            `Failed to copy debug info: ${err.message || err}`
          );
          invoke("growl", [t("failedToCopyDebugInformation"), "", 2000, true]);
        });
    }
  };

  // Helper method to format console logs section for debug info
  // Returns an array of log lines (more efficient than string splitting)
  formatConsoleLogsSection = () => {
    if (!this.debugLogs || this.debugLogs.length === 0) {
      return ["=== CONSOLE LOGS ===", "No console logs captured yet.", ""];
    }

    // Return array of log lines (more efficient for formatDebugInfo)
    return ["=== CONSOLE LOGS ===", ...this.debugLogs, ""];
  };

  formatDebugInfo = () => {
    if (!this.showServerWriteProgress) {
      return "Debug logging is disabled. Enable showServerWriteProgress to view debug information.";
    }

    const chunks = this.chunksInfo.chunks;
    const chunksYetToAck = this.getChunksToAck();
    const serverYetToWrite = this.getSizeOfServerYetToWrite();
    const socketInfo = this.getSocketInfo();

    // Get progress status from UI
    const fileId = this.streamingFileInfo?.id;
    if (this.shouldLog()) {
      this.logToConsoleAndDebug(`fileId: ${fileId}`);
    }
    const progressStatus = fileId
      ? document.querySelector(
          `#uploadFileSection div#${fileId} .upload-progress-status`
        )?.textContent
      : "N/A";

    // Get recovery information using helper
    const recoveryReasons = this.getRecoveryInformation();

    // Get chunk status counts
    const statusCounts = this.getChunkStatusCounts(chunks);

    // Start with console logs section (mirrors all console.log output)
    const consoleLogsSection = this.formatConsoleLogsSection();

    const debugText = [
      ...consoleLogsSection,
      "=== Upload Debug Information ===",
      `Username: ${invoke("getUserInfo")?.username || "Unknown"}`,
      `URL: ${window.parent.location.href || window.location.href}`,
      `Time: ${new Date().toLocaleString()}`,
      `File: ${this.streamingFileInfo?.nameF || "Unknown"}`,
      `File ID: ${
        this.streamingFileInfo?.reUploadId ||
        this.streamingFileInfo?.id ||
        "Unknown"
      }`,
      `Size: ${utils.formatBytes(this.streamingFileInfo?.size || 0)}`,
      `Upload started: ${new Date(this.uploadStartTime).toLocaleString()}`,
      `Upload ended: ${
        this.uploadEndTime
          ? new Date(this.uploadEndTime).toLocaleString()
          : "In progress"
      }`,
      `Total upload time: ${this.formatDuration(
        this.uploadStartTime,
        this.uploadEndTime
      )}`,
      `Last periodic check: ${this.formatTimeDifference(
        this.lastPeriodicCheckTime
      )}`,
      `Progress info: ${progressStatus}`,
      "",
      "=== Upload Configuration ===",
      `Current chunk size: ${
        this.currentChunkSize
      } MB (${this.formatChunkSizeMB(this.currentChunkSize * this.MB)})`,
      `Start chunk size: ${this.startChunkSizeMB} MB`,
      `Max chunk size: ${this.maxChunkSizeMB} MB`,
      `Default chunk size: ${this.defaultChunkSize} MB`,
      `Chunk timeout: ${this.chunkTimeout} seconds`,
      "",
      "=== Socket Information ===",
      ...socketInfo.map(
        (socket) =>
          `Socket ${socket.index}: Buffer: ${
            socket.bufferSize
          }MB, Chunks being sent: ${socket.chunksBeingSent}, Chunks acked: ${
            socket.chunksAcked
          }, Stuck: ${socket.isStuck ? "Yes" : "No"}`
      ),
      `Sockets respawned: ${this.totalSocketsRespawned}`,
      `Total stuck socket identifications: ${this.totalStuckSocketIdentifications}`,
      "",
      "=== Recovery Status ===",
      recoveryReasons.length > 0
        ? `Recovery in progress: ${recoveryReasons.join(" | ")}`
        : "No recovery actions in progress",
      `Total recovery actions: ${this.totalRecoveryActions}`,
      `Total chunk preparation stalls: ${this.totalChunkPreparationStalls}`,
      `Total chunk resend attempts: ${this.totalChunkResendAttempts}`,
      "",
      "=== Stuck Socket Details ===",
      ...this.sockets
        .filter((socket) => socket.isStuck)
        .map(
          (socket) =>
            `Socket ${socket.index}: Buffer: ${socket.bufferedAmount}MB, Chunks being sent: ${socket.chunksBeingSent}, Last checked: ${socket.lastCheckedTime}s ago`
        ),
      "",
      "=== Chunk Status ===",
      `Total chunks: ${chunks.length}`,
      `Chunks ready in memory: ${statusCounts.init}`,
      `Chunks uploading: ${statusCounts.uploading}`,
      `Chunks done: ${statusCounts.done}`,
      `Chunks failed: ${statusCounts.failed}`,
      `Chunks retrying: ${statusCounts.retrying}`,
      `Chunks sent: ${this.chunksSent.length}`,
      `Chunks sent with retries: ${
        this.chunksSent.filter((chunk) => chunk.status === "retry").length
      }`,
      `Chunks acknowledged: ${this.chunksAckd.size}`,
      `Chunks acknowledged with retries: ${
        Array.from(this.chunksAckd).filter((chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          return chunk && chunk.status === "retry";
        }).length
      }`,
      `Chunks yet to ack: ${chunksYetToAck.length}`,
      `Chunks yet to ack with retries: ${
        chunksYetToAck.filter((chunk) => chunk.status === "retry").length
      }`,
      `Average chunk upload time: ${
        this.uploadSummary.averageChunkUploadTime
          ? this.uploadSummary.averageChunkUploadTime.toFixed(2) + "s"
          : "Not available"
      }`,
      `Upload time samples: ${this.uploadSummary.uploadTimeSamples.length}`,
      "",
      "=== Detailed Chunk List (Retried/Problematic + Last 5% of Chunks) ===",
      ...this.generateDetailedChunkList(chunks),
      "",
      "=== Server Write Status ===",
      `Server yet to write: ${serverYetToWrite.toFixed(2)}MB`,
      `Server write threshold: ${this.serverWriteThreshold}MB`,
      `Server write threshold exceeded: ${
        serverYetToWrite > this.serverWriteThreshold ? "Yes" : "No"
      }`,
      "",
      "=== Upload Status ===",
      `Status: ${this.getUploadStatusString()}`,
      `Bytes uploaded: ${utils.formatBytes(this.chunksInfo.bytesUploaded)}`,
      `Bytes sent: ${utils.formatBytes(this.chunksInfo.bytesSent)}`,
      "",
      "=== PROGRESS BAR INFORMATION ===",
      `Progress bar uses: ACKed bytes (${utils.formatBytes(
        this.chunksInfo.bytesUploaded
      )})`,
      `Progress percentage: ${(
        (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
        100
      ).toFixed(2)}%`,
      `File size: ${utils.formatBytes(this.streamingFileInfo.size)}`,
      `Bytes sent but not ACKed: ${utils.formatBytes(
        this.chunksInfo.bytesSent - this.chunksInfo.bytesUploaded
      )}`,
      `Server yet to write: ${serverYetToWrite.toFixed(2)}MB`,
      `Server written bytes: ${utils.formatBytes(
        this.streamingFileInfo.size - serverYetToWrite * this.MB
      )}`,
      `Chunks sent: ${this.chunksSent.length}, Chunks ACKed: ${this.chunksAckd.size}`,
      `Difference (sent - ACKed): ${
        this.chunksSent.length - this.chunksAckd.size
      } chunks`,
      `Last chunk generated: ${
        this.lastChunkGeneratedTime
          ? Math.floor((Date.now() - this.lastChunkGeneratedTime) / 1000)
          : 0
      } seconds ago`,
      `Last chunk sent: ${
        this.lastChunkSentTime
          ? Math.floor((Date.now() - this.lastChunkSentTime) / 1000)
          : 0
      } seconds ago`,
      `Last chunk sent was retry: ${this.lastChunkSentIsRetry ? "Yes" : "No"}`,
      `Chunk preparation stalled: ${
        this.chunkPreparationStalled ? "Yes" : "No"
      }`,
      "",
      "=== Retry Information ===",
      `On error retries: ${this.retries}`,
      `Max retries: ${this.maxRetries}`,
      `Current chunk size: ${
        this.currentChunkSize
      } MB (${this.formatChunkSizeMB(this.currentChunkSize * this.MB)})`,
      `Default chunk size: ${this.defaultChunkSize} MB`,
      `Chunk timeout: ${this.chunkTimeout} seconds`,
      "",
      "=== Chunk Size Configuration ===",
      `Start chunk size: ${this.startChunkSizeMB}MB`,
      `Max chunk size: ${this.maxChunkSizeMB}MB`,
      `Target chunk size: ${this.chunkSizeConfig.targetChunkSizeMB}MB`,
      `Chunk size re-calculation scheduled: ${
        this.chunkSizeTimerId !== null ? "Yes" : "No"
      }`,
      `Chunk size re-calculation check delay: ${
        this.chunkSizeConfig.checkDelayMs / 1000
      }s`,
      `Chunk size re-calculation minimum ACKed chunks: ${this.chunkSizeConfig.minAckedChunks}`,
      `Chunk size re-calculation maximum avg time per chunk: ${this.chunkSizeConfig.maxAvgSecPerChunk}s`,
      `Chunk size can increase: ${
        this.currentChunkSize < this.chunkSizeConfig.targetChunkSizeMB
          ? "Yes"
          : "No (already at max)"
      }`,
      `Chunk size can decrease: ${
        this.currentChunkSize > this.startChunkSizeMB
          ? "Yes"
          : "No (already at min)"
      }`,
      ...(this.chunkSizeEvaluationResults
        ? [
            `Chunk size re-calculation last run: ${this.chunkSizeEvaluationResults.evaluationTimeLocal}`,
            `Chunks ACKed at last evaluation: ${this.chunkSizeEvaluationResults.acked}`,
            `Average chunk time at last evaluation: ${
              this.chunkSizeEvaluationResults.avgTimeSec !== null
                ? this.chunkSizeEvaluationResults.avgTimeSec.toFixed(2) + "s"
                : "N/A"
            }`,
            `Chunk size before last evaluation: ${
              this.chunkSizeEvaluationResults.previousChunkSizeMB?.toFixed(2) ||
              this.chunkSizeEvaluationResults.currentChunkSizeMB
            }MB`,
            `Chunk size after last evaluation: ${this.chunkSizeEvaluationResults.currentChunkSizeMB.toFixed(
              2
            )}MB`,
            `Chunk size changed: ${
              this.chunkSizeEvaluationResults.changed ? "Yes" : "No"
            }`,
            ...(this.chunkSizeEvaluationResults.changed
              ? [
                  `Change reason: ${
                    this.chunkSizeEvaluationResults.changeReason || "N/A"
                  }`,
                  `Increase approved: ${
                    this.chunkSizeEvaluationResults.increase ? "Yes" : "No"
                  }`,
                  `Decrease triggered: ${
                    this.chunkSizeEvaluationResults.decrease ? "Yes" : "No"
                  }`,
                ]
              : []),
            `Target chunk size: ${this.chunkSizeEvaluationResults.targetChunkSizeMB}MB`,
          ]
        : [
            `Chunk size re-calculation not yet run or upload ended before re-calculation`,
          ]),
      "",
      "=== Connection Information ===",
      `Max requests: ${this.maxRequest}`,
      `Active sockets: ${this.sockets.length}`,
      `Sockets respawned: ${this.totalSocketsRespawned}`,
      `Server write threshold: ${this.serverWriteThreshold}MB`,
      `Connection retries: ${this.connectionRetries}`,
      `Max connection retries: ${this.maxConnectionRetries}`,
      `Close file retries: ${this.closeFileRetries}`,
      "",
      "=== Chunk Acknowledgment Checks ===",
      `API method: XHR-based chunk acknowledgment check`,
      `API endpoint: /api with command=ackd`,
      `Pending acknowledgment checks: ${
        this.pendingAckChecks ? this.pendingAckChecks.size : 0
      }`,
      ...(this.pendingAckChecks && this.pendingAckChecks.size > 0
        ? [
            `Chunks being checked: ${Array.from(this.pendingAckChecks)
              .sort((a, b) => a - b)
              .join(", ")}`,
          ]
        : []),
      `Upload progress: ${
        this.streamingFileInfo?.size
          ? (
              (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
              100
            ).toFixed(2)
          : 0
      }%`,
      `Acknowledgment checks enabled: ${
        (this.streamingFileInfo?.size
          ? (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) * 100
          : 0) >= 98
          ? "Yes (98%+ progress)"
          : "No (below 98%)"
      }`,
      `Retry mechanism: 1-second intervals for unacknowledged chunks`,
      "",
      "=== Important Logs ===",
      ...this.formatImportantLogs(),
      "",
      "=== Upload Statistics ===",
      `Upload start time: ${
        this.uploadStartTime
          ? new Date(this.uploadStartTime).toLocaleString()
          : "N/A"
      }`,
      `Upload end time: ${
        this.uploadEndTime
          ? new Date(this.uploadEndTime).toLocaleString()
          : "N/A"
      }`,
      `Total upload time: ${this.formatDuration(
        this.uploadStartTime,
        this.uploadEndTime
      )}`,
      `Elapsed time: ${
        this.chunksInfo.elapsedTime
          ? Math.floor(this.chunksInfo.elapsedTime / 1000)
          : 0
      } seconds`,
      `Send chunks call count: ${this.sendChunksCallCount}`,
      `Send chunks rejections:`,
      `  - No idle socket: ${this.uploadSummary.sendChunksRejections.noIdleSocket}`,
      `  - No ready chunk: ${this.uploadSummary.sendChunksRejections.noReadyChunk}`,
      `  - Server write threshold exceeded: ${this.uploadSummary.sendChunksRejections.serverWriteThresholdExceeded}`,
      "",
      "=== Zero-Byte Chunk Sending ===",
      ...this.formatZeroByteChunkStats(),
      "",
      "=== Large File Completion Detection ===",
      `File size category: ${
        this.isVeryLargeFile(this.streamingFileInfo?.size || 0)
          ? "Very Large (>10GB)"
          : this.isLargeFile(this.streamingFileInfo?.size || 0)
          ? "Large (>1GB)"
          : "Normal (<1GB)"
      }`,
      `Completion status:`,
      `  - All chunks created: ${
        this.uploadSummary.allChunksCreated ? "Yes" : "No"
      }`,
      `  - Total chunks: ${this.chunksInfo.chunks.length}`,
      `  - Total chunks uploaded: ${this.chunksInfo.totalChunksUploaded}`,
      `  - Bytes uploaded: ${utils.formatBytes(this.chunksInfo.bytesUploaded)}`,
      `  - File size: ${utils.formatBytes(this.streamingFileInfo?.size || 0)}`,
      `  - Completion percentage: ${
        this.streamingFileInfo?.size
          ? (
              (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
              100
            ).toFixed(2)
          : 0
      }%`,
      "",
      "=== Periodic Check Status ===",
      `Periodic check interval: ${
        this.periodicCheckInterval ? "Active" : "Inactive"
      }`,
      `Last periodic check: ${
        this.lastPeriodicCheckTime
          ? Math.floor((Date.now() - this.lastPeriodicCheckTime) / 1000)
          : 0
      } seconds ago`,
      `Fallback completion check: ${
        this.isVeryLargeFile(this.streamingFileInfo?.size || 0)
          ? "Enabled"
          : "Disabled"
      }`,
      "",
      "=== Large File Logging ===",
      `Large file logging: ${
        this.isVeryLargeFile(this.streamingFileInfo?.size || 0)
          ? "Enabled (every 100th chunk)"
          : "Disabled"
      }`,
      `Completion detail logging: ${
        this.isLargeFile(this.streamingFileInfo?.size || 0)
          ? "Enabled"
          : "Disabled"
      }`,
      `Fallback completion logging: ${
        this.isVeryLargeFile(this.streamingFileInfo?.size || 0)
          ? "Enabled"
          : "Disabled"
      }`,
      "",
      "=== Socket Failures ===",
      ...this.uploadSummary.socketFailures.map(
        (failure) =>
          `Socket ${failure.socketIndex} ${failure.type} at ${failure.timestamp}\n` +
          `  Chunks being sent: ${failure.details.socket.chunksBeingSent}\n` +
          `  Chunks acked: ${failure.details.socket.chunksAckd}\n` +
          `  Buffer amount: ${failure.details.socket.bufferedAmount}`
      ),
      "========================",
    ].join("\n");

    return debugText;
  };

  // Helper methods to eliminate redundancy
  isLargeFile = (fileSize) => {
    return fileSize > 1024 * 1024 * 1024; // 1GB
  };

  isVeryLargeFile = (fileSize) => {
    return fileSize > 10 * 1024 * 1024 * 1024; // 10GB
  };

  getCompletionStatus = () => {
    const file = this.streamingFileInfo;
    const bytesUploaded = this.chunksInfo.bytesUploaded;
    const fileSize = file?.size;
    const totalChunks = this.chunksInfo.chunks.length;
    const totalChunksUploaded = this.chunksInfo.totalChunksUploaded;
    const isAllChunksCreated = this.uploadSummary.allChunksCreated;

    return {
      bytesUploaded,
      fileSize,
      totalChunks,
      totalChunksUploaded,
      isAllChunksCreated,
      isLargeFile: this.isLargeFile(fileSize),
      isVeryLargeFile: this.isVeryLargeFile(fileSize),
    };
  };

  isUploadComplete = (completionStatus) => {
    const {
      bytesUploaded,
      fileSize,
      totalChunks,
      totalChunksUploaded,
      isAllChunksCreated,
      isLargeFile,
      isVeryLargeFile,
    } = completionStatus;

    // Primary completion check (original logic)
    const isCompleteByBytes = bytesUploaded >= fileSize;

    // Secondary completion check for large files with precision issues
    const isCompleteByChunks =
      isLargeFile &&
      totalChunks > 0 &&
      totalChunksUploaded >= totalChunks &&
      isAllChunksCreated;

    // Precision tolerance check (only for very large files)
    const isCompleteByPrecisionTolerance =
      isVeryLargeFile &&
      bytesUploaded >= fileSize - 1 &&
      totalChunksUploaded >= totalChunks;

    return {
      isComplete:
        isCompleteByBytes ||
        isCompleteByChunks ||
        isCompleteByPrecisionTolerance,
      isCompleteByBytes,
      isCompleteByChunks,
      isCompleteByPrecisionTolerance,
      isLargeFile,
      isVeryLargeFile,
    };
  };

  logCompletionDetails = (completionStatus, completionResult) => {
    const { isLargeFile } = completionResult;
    if (isLargeFile) {
      const {
        bytesUploaded,
        fileSize,
        totalChunks,
        totalChunksUploaded,
        isAllChunksCreated,
      } = completionStatus;

      C.log(
        `Upload: Large file completion - bytesUploaded: ${bytesUploaded}, fileSize: ${fileSize}, totalChunks: ${totalChunks}, totalChunksUploaded: ${totalChunksUploaded}, allChunksCreated: ${isAllChunksCreated}`
      );
    }
  };

  logFallbackCompletionDetails = (completionStatus) => {
    const { totalChunks, totalChunksUploaded, bytesUploaded, fileSize } =
      completionStatus;

    C.log(
      `Upload: Fallback completion details - totalChunks: ${totalChunks}, totalChunksUploaded: ${totalChunksUploaded}, bytesUploaded: ${bytesUploaded}, fileSize: ${fileSize}, completionPercentage: ${(
        (bytesUploaded / fileSize) *
        100
      ).toFixed(2)}%`
    );
  };

  // Method to get debug information for clipboard copy
  getDebugInfo = () => {
    if (!this.showServerWriteProgress) {
      return "Debug logging is disabled. Enable showServerWriteProgress to view debug information.";
    }

    try {
      const completionStatus = this.getCompletionStatus();
      const completionResult = this.isUploadComplete(completionStatus);

      let debugInfo = "";

      // Essential file information
      debugInfo += `=== FILE UPLOAD DEBUG ===\n`;
      debugInfo += `File: ${this.streamingFileInfo?.nameF || "Unknown"}\n`;
      debugInfo += `Size: ${utils.formatBytes(
        this.streamingFileInfo?.size || 0
      )}\n`;
      debugInfo += `Status: ${this.getUploadStatusString()}\n`;
      debugInfo += `Current chunk size: ${
        this.currentChunkSize
      } MB (${this.formatChunkSizeMB(this.currentChunkSize * this.MB)})\n`;
      debugInfo += `Chunk timeout: ${this.chunkTimeout} seconds\n\n`;

      // CRITICAL: Completion detection status
      debugInfo += `=== COMPLETION DETECTION (THE PROBLEM) ===\n`;
      debugInfo += `Bytes uploaded: ${utils.formatBytes(
        this.chunksInfo.bytesUploaded
      )}\n`;
      debugInfo += `File size: ${utils.formatBytes(
        this.streamingFileInfo?.size || 0
      )}\n`;
      debugInfo += `Progress: ${
        this.streamingFileInfo?.size
          ? (
              (this.chunksInfo.bytesUploaded / this.streamingFileInfo.size) *
              100
            ).toFixed(2)
          : 0
      }%\n`;
      debugInfo += `Missing bytes: ${
        this.streamingFileInfo?.size
          ? this.streamingFileInfo.size - this.chunksInfo.bytesUploaded
          : 0
      } bytes\n\n`;

      // Chunk completion status
      debugInfo += `=== CHUNK STATUS ===\n`;
      debugInfo += `Total chunks: ${this.chunksInfo.chunks.length}\n`;
      debugInfo += `Chunks uploaded: ${this.chunksInfo.totalChunksUploaded}\n`;
      debugInfo += `Chunks remaining: ${
        this.chunksInfo.chunks.length - this.chunksInfo.totalChunksUploaded
      }\n`;
      debugInfo += `All chunks created: ${
        this.uploadSummary.allChunksCreated ? "Yes" : "No"
      }\n\n`;

      // Completion logic results
      debugInfo += `=== COMPLETION LOGIC RESULTS ===\n`;
      debugInfo += `Primary completion (bytes >= size): ${
        completionResult.isCompleteByBytes ? "YES" : "NO"
      }\n`;
      debugInfo += `Chunk-based completion (all chunks done): ${
        completionResult.isCompleteByChunks ? "YES" : "NO"
      }\n`;
      debugInfo += `Precision tolerance (within 1 byte): ${
        completionResult.isCompleteByPrecisionTolerance ? "YES" : "NO"
      }\n`;
      debugInfo += `OVERALL COMPLETION: ${
        completionResult.isComplete ? "YES - SHOULD CLOSE" : "NO - STUCK"
      }\n\n`;

      // File size category and thresholds
      debugInfo += `=== FILE SIZE ANALYSIS ===\n`;
      const fileSize = this.streamingFileInfo?.size || 0;
      debugInfo += `File size category: ${this.getFileSizeCategory(
        fileSize
      )}\n`;
      debugInfo += `Large file logic enabled: ${
        this.isLargeFile(fileSize) ? "Yes" : "No"
      }\n`;
      debugInfo += `Very large file logic enabled: ${
        this.isVeryLargeFile(fileSize) ? "Yes" : "No"
      }\n\n`;

      // Current state
      debugInfo += `=== CURRENT STATE ===\n`;
      debugInfo += `uploadDone flag: ${this.uploadDone ? "TRUE" : "FALSE"}\n`;
      debugInfo += `fileClosed flag: ${this.fileClosed ? "TRUE" : "FALSE"}\n`;
      debugInfo += `Periodic check active: ${
        this.periodicCheckInterval ? "Yes" : "No"
      }\n`;
      debugInfo += `Fallback check enabled: ${
        this.isVeryLargeFile(fileSize) ? "Yes" : "No"
      }\n\n`;

      // If stuck, show what's preventing completion
      if (!completionResult.isComplete && !this.uploadDone) {
        debugInfo += `=== WHY FILE IS STUCK ===\n`;
        if (!completionResult.isCompleteByBytes) {
          debugInfo += `Primary completion failed: bytes uploaded (${this.chunksInfo.bytesUploaded}) < file size (${fileSize})\n`;
        }
        if (!completionResult.isCompleteByChunks) {
          debugInfo += `Chunk completion failed: chunks uploaded (${this.chunksInfo.totalChunksUploaded}) < total chunks (${this.chunksInfo.chunks.length})\n`;
        }
        if (!completionResult.isCompleteByPrecisionTolerance) {
          debugInfo += `Precision tolerance failed: not within 1 byte of completion\n`;
        }
        debugInfo += `\n`;
      }

      // Timing information (only if relevant)
      if (this.lastChunkSentTime) {
        const timeSinceLastChunk = Math.floor(
          (Date.now() - this.lastChunkSentTime) / 1000
        );
        if (timeSinceLastChunk > this.chunkTimeout) {
          debugInfo += `=== TIMING ISSUE ===\n`;
          debugInfo += `Last chunk sent: ${timeSinceLastChunk} seconds ago\n`;
          debugInfo += `This might indicate the upload is stuck waiting for final chunk\n\n`;
        }
      }

      // Multi-socket retry information for last chunk
      if (this.lastChunkMultiSocketRetryTime) {
        const timeSinceLastMultiSocketRetry = Math.floor(
          (Date.now() - this.lastChunkMultiSocketRetryTime) / 1000
        );
        debugInfo += `=== LAST CHUNK MULTI-SOCKET RETRY ===\n`;
        debugInfo += `Last multi-socket retry: ${timeSinceLastMultiSocketRetry} seconds ago\n`;
        debugInfo += `Cooldown period: ${
          this.lastChunkMultiSocketRetryCooldown / 1000
        } seconds\n`;
        debugInfo += `Cooldown status: ${
          timeSinceLastMultiSocketRetry <
          this.lastChunkMultiSocketRetryCooldown / 1000
            ? `Active (${
                this.lastChunkMultiSocketRetryCooldown / 1000 -
                timeSinceLastMultiSocketRetry
              }s remaining)`
            : "Expired - ready for next multi-socket retry"
        }\n\n`;
      } else {
        // Check if we have chunks that might need multi-socket retry
        const chunksYetToAck = this.chunksInfo.chunks.filter(
          (chunk) => !this.chunksAckd.has(chunk.index)
        );
        if (chunksYetToAck.length === 1) {
          const lastChunk = chunksYetToAck[0];
          debugInfo += `=== LAST CHUNK MULTI-SOCKET RETRY ===\n`;
          debugInfo += `Last chunk (${lastChunk.index}) is the only remaining chunk\n`;
          debugInfo += `Multi-socket retry: Not attempted yet\n`;
          debugInfo += `Cooldown period: ${
            this.lastChunkMultiSocketRetryCooldown / 1000
          } seconds\n`;
          debugInfo += `Status: Ready for multi-socket retry if needed\n\n`;
        }
      }

      // Comprehensive chunk list
      debugInfo += `=== DETAILED CHUNK LIST ===\n`;
      const chunks = this.chunksInfo.chunks;
      if (chunks.length > 0) {
        // Sort chunks by index in ascending order
        const sortedChunks = [...chunks].sort((a, b) => a.index - b.index);

        debugInfo += `Total chunks: ${chunks.length}\n`;
        debugInfo += `Format: [Index] Status | Init: HH:MM:SS | Sent: HH:MM:SS | Wait: time | Ack: HH:MM:SS | Duration | Retries\n`;
        debugInfo += `-`.repeat(80) + `\n`;

        sortedChunks.forEach((chunk) => {
          const { line, details } = this.formatChunkForDebug(chunk);
          debugInfo += line + "\n";
          if (details.length > 0) {
            debugInfo += details.join("\n") + "\n";
          }
        });

        debugInfo += `-`.repeat(80) + `\n`;

        // Summary statistics
        const statusCounts = this.getChunkStatusCounts(chunks);
        const totalRetries = chunks.reduce(
          (sum, chunk) => sum + (chunk.retries || 0),
          0
        );
        const avgTimeTaken =
          chunks
            .filter((chunk) => chunk.timeTaken)
            .reduce((sum, chunk) => sum + chunk.timeTaken, 0) /
          chunks.filter((chunk) => chunk.timeTaken).length;

        debugInfo += `Summary: Done: ${statusCounts.done}, Uploading: ${statusCounts.uploading}, Retry: ${statusCounts.retrying}, Init: ${statusCounts.init}\n`;
        debugInfo += `Total retries: ${totalRetries}, Average upload time: ${
          avgTimeTaken ? avgTimeTaken.toFixed(1) + "s" : "N/A"
        }\n`;

        // Chunk timing statistics
        const chunksWithInitTime = chunks.filter((chunk) => chunk.init_time);
        const chunksWithSentAt = chunks.filter(
          (chunk) => chunk.sent_at && chunk.sent_at !== null
        );
        const queuedChunks = chunks.filter(
          (chunk) => chunk.sent_at === null && chunk.status === "uploading"
        );
        const ackedChunks = chunks.filter(
          (chunk) => chunk.status === "done" && chunk.init_time && chunk.sent_at
        );

        debugInfo += `\n=== CHUNK TIMING STATISTICS ===\n`;
        debugInfo += `Chunks with init_time: ${chunksWithInitTime.length}\n`;
        debugInfo += `Chunks with sent_at: ${chunksWithSentAt.length}\n`;
        debugInfo += `Queued chunks (sent_at === null): ${queuedChunks.length}\n`;

        if (ackedChunks.length > 0) {
          const waitTimes = ackedChunks
            .map((chunk) => chunk.sent_at - chunk.init_time)
            .filter((wt) => wt >= 0);
          if (waitTimes.length > 0) {
            const avgWaitTime =
              waitTimes.reduce((sum, wt) => sum + wt, 0) / waitTimes.length;
            const maxWaitTime = Math.max(...waitTimes);
            const minWaitTime = Math.min(...waitTimes);
            debugInfo += `Average wait time (queued): ${(
              avgWaitTime / 1000
            ).toFixed(2)}s\n`;
            debugInfo += `Max wait time: ${(maxWaitTime / 1000).toFixed(2)}s\n`;
            debugInfo += `Min wait time: ${(minWaitTime / 1000).toFixed(2)}s\n`;
          }
        }

        // Show queued chunks details
        if (queuedChunks.length > 0) {
          debugInfo += `\nQueued chunks (waiting to be sent):\n`;
          queuedChunks.forEach((chunk) => {
            const waitTime = chunk.init_time
              ? this.formatWaitTime(chunk.init_time, null)
              : "N/A";
            const timeSinceInit = chunk.init_time
              ? Math.floor((Date.now() - chunk.init_time) / 1000)
              : "N/A";
            debugInfo += `  Chunk ${chunk.index}: waiting ${waitTime} (${timeSinceInit}s since init_time)\n`;
          });
        }
      } else {
        debugInfo += `No chunks available\n`;
      }

      // Upload timing information
      debugInfo += `=== UPLOAD TIMING ===\n`;
      debugInfo += `Start time: ${
        this.uploadStartTime
          ? new Date(this.uploadStartTime).toLocaleString()
          : "N/A"
      }\n`;
      debugInfo += `End time: ${
        this.uploadEndTime
          ? new Date(this.uploadEndTime).toLocaleString()
          : "In progress"
      }\n`;
      debugInfo += `Total upload time: ${this.formatDuration(
        this.uploadStartTime,
        this.uploadEndTime
      )}\n`;
      debugInfo += `Last chunk sent time: ${
        this.lastChunkSentTime
          ? new Date(this.lastChunkSentTime).toLocaleString() +
            ` (${Math.floor(
              (Date.now() - this.lastChunkSentTime) / 1000
            )}s ago)`
          : "N/A"
      }\n`;
      debugInfo += `Last chunk generated time: ${
        this.lastChunkGeneratedTime
          ? new Date(this.lastChunkGeneratedTime).toLocaleString() +
            ` (${Math.floor(
              (Date.now() - this.lastChunkGeneratedTime) / 1000
            )}s ago)`
          : "N/A"
      }\n`;

      // Socket queue information
      debugInfo += `\n=== SOCKET QUEUE INFORMATION ===\n`;
      debugInfo += `Active sockets: ${this.sockets.length}\n`;
      if (this.sockets.length > 0) {
        this.sockets.forEach((socket) => {
          const chunksInQueue = socket.chunksBeingSent?.length || 0;
          const chunksAcked = socket.chunksAckd?.length || 0;
          const bufferedAmount = socket.bufferedAmount || 0;
          const isOpen = this.isSocketOpen(socket);
          debugInfo += `Socket ${socket.index}: `;
          debugInfo += `Queue: ${chunksInQueue} chunks, `;
          debugInfo += `ACKed: ${chunksAcked}, `;
          debugInfo += `Buffer: ${bufferedAmount} bytes, `;
          debugInfo += `Status: ${isOpen ? "Open" : "Closed"}\n`;

          // Show queued chunks on this socket
          if (chunksInQueue > 0 && socket.chunksBeingSent) {
            socket.chunksBeingSent.forEach((chunkIndex) => {
              const chunk = this.chunksInfo.chunks[chunkIndex];
              if (chunk) {
                const { initTimeStr, sentAtStr, waitTime } =
                  this.formatChunkTimeStrings(chunk);
                const queueStatus = chunk.sent_at === null ? "QUEUED" : "SENT";
                debugInfo += `  Chunk ${chunkIndex}: ${queueStatus}, Init: ${initTimeStr}, Sent: ${sentAtStr}, Wait: ${waitTime}\n`;
              }
            });
          }
        });
      }
      if (this.retrySocket) {
        const chunksInQueue = this.retrySocket.chunksBeingSent?.length || 0;
        const chunksAcked = this.retrySocket.chunksAckd?.length || 0;
        const bufferedAmount = this.retrySocket.bufferedAmount || 0;
        const isOpen = this.isSocketOpen(this.retrySocket);
        debugInfo += `Retry socket: Queue: ${chunksInQueue} chunks, ACKed: ${chunksAcked}, Buffer: ${bufferedAmount} bytes, Status: ${
          isOpen ? "Open" : "Closed"
        }\n`;
        if (chunksInQueue > 0 && this.retrySocket.chunksBeingSent) {
          this.retrySocket.chunksBeingSent.forEach((chunkIndex) => {
            const chunk = this.chunksInfo.chunks[chunkIndex];
            if (chunk) {
              const { initTimeStr, sentAtStr, waitTime } =
                this.formatChunkTimeStrings(chunk);
              const queueStatus = chunk.sent_at === null ? "QUEUED" : "SENT";
              debugInfo += `  Chunk ${chunkIndex}: ${queueStatus}, Init: ${initTimeStr}, Sent: ${sentAtStr}, Wait: ${waitTime}\n`;
            }
          });
        }
      }

      // XHR statistics
      debugInfo += `\n=== XHR STATISTICS ===\n`;
      debugInfo += `Total XHR calls: ${this.uploadSummary.xhrStats.totalCalls}\n`;
      debugInfo += `Successful calls: ${this.uploadSummary.xhrStats.successfulCalls}\n`;
      debugInfo += `Failed calls: ${this.uploadSummary.xhrStats.failedCalls}\n`;
      debugInfo += `Retry attempts: ${this.uploadSummary.xhrStats.retryAttempts}\n`;
      debugInfo += `Success rate: ${
        this.uploadSummary.xhrStats.totalCalls > 0
          ? (
              (this.uploadSummary.xhrStats.successfulCalls /
                this.uploadSummary.xhrStats.totalCalls) *
              100
            ).toFixed(1)
          : 0
      }%\n`;
      debugInfo += `Average response time: ${this.uploadSummary.xhrStats.averageResponseTime.toFixed(
        0
      )}ms\n`;

      if (Object.keys(this.uploadSummary.xhrStats.failureReasons).length > 0) {
        debugInfo += `Failure reasons:\n`;
        Object.entries(this.uploadSummary.xhrStats.failureReasons).forEach(
          ([reason, count]) => {
            debugInfo += `  - ${reason}: ${count}\n`;
          }
        );
      }

      // Add chunk events summary using common method
      const chunkEventsLines = this.formatChunkEventsSummary("string");
      debugInfo += chunkEventsLines.join("");

      // Chunk size information
      debugInfo += `\n=== CHUNK SIZE INFORMATION ===\n`;
      debugInfo += `Start chunk size: ${
        this.startChunkSizeMB
      } MB (${this.formatChunkSizeMB(this.startChunkSizeMB * this.MB)})\n`;
      debugInfo += `Current chunk size: ${this.currentChunkSize.toFixed(
        2
      )} MB (${this.formatChunkSizeMB(this.currentChunkSize * this.MB)})\n`;
      debugInfo += `Max chunk size: ${
        this.maxChunkSizeMB
      } MB (${this.formatChunkSizeMB(this.maxChunkSizeMB * this.MB)})\n`;
      debugInfo += `Target chunk size: ${
        this.chunkSizeConfig.targetChunkSizeMB
      } MB (${this.formatChunkSizeMB(
        this.chunkSizeConfig.targetChunkSizeMB * this.MB
      )})\n`;
      debugInfo += `Chunk timeout: ${this.chunkTimeout} seconds\n`;
      debugInfo += `Average chunk upload time: ${
        this.uploadSummary.averageChunkUploadTime
          ? this.uploadSummary.averageChunkUploadTime.toFixed(2) + "s"
          : "Not available"
      }\n`;
      debugInfo += `Max avg time threshold: ${this.chunkSizeConfig.maxAvgSecPerChunk}s\n`;
      debugInfo += `Chunk size can increase: ${
        this.currentChunkSize < this.chunkSizeConfig.targetChunkSizeMB
          ? "Yes"
          : "No (already at max)"
      }\n`;
      debugInfo += `Chunk size can decrease: ${
        this.currentChunkSize > this.startChunkSizeMB
          ? "Yes"
          : "No (already at min)"
      }\n`;
      if (this.chunkSizeEvaluationResults) {
        debugInfo += `Last evaluation: ${this.chunkSizeEvaluationResults.evaluationTimeLocal}\n`;
        debugInfo += `Chunks ACKed at evaluation: ${this.chunkSizeEvaluationResults.acked}\n`;
        debugInfo += `Avg time at evaluation: ${
          this.chunkSizeEvaluationResults.avgTimeSec !== null
            ? this.chunkSizeEvaluationResults.avgTimeSec.toFixed(2) + "s"
            : "N/A"
        }\n`;
        debugInfo += `Previous chunk size: ${
          this.chunkSizeEvaluationResults.previousChunkSizeMB?.toFixed(2) ||
          this.chunkSizeEvaluationResults.currentChunkSizeMB
        }MB\n`;
        debugInfo += `Chunk size changed: ${
          this.chunkSizeEvaluationResults.changed ? "Yes" : "No"
        }\n`;
        if (this.chunkSizeEvaluationResults.changed) {
          debugInfo += `Change reason: ${
            this.chunkSizeEvaluationResults.changeReason || "N/A"
          }\n`;
          debugInfo += `Increase: ${
            this.chunkSizeEvaluationResults.increase ? "Yes" : "No"
          }\n`;
          debugInfo += `Decrease: ${
            this.chunkSizeEvaluationResults.decrease ? "Yes" : "No"
          }\n`;
        }
      } else {
        debugInfo += `Chunk size evaluation: Not yet run\n`;
      }

      debugInfo += `\n`;

      return debugInfo;
    } catch (error) {
      return `Error generating debug info: ${error.message}\n`;
    }
  };

  // Generate detailed chunk list for Upload Debug Information
  generateDetailedChunkList = (chunks) => {
    if (!this.showServerWriteProgress) {
      return ["Debug logging disabled (showServerWriteProgress is false)"];
    }

    if (chunks.length === 0) {
      return ["No chunks available"];
    }

    const lines = [];

    // Sort chunks by index in ascending order
    const sortedChunks = [...chunks].sort((a, b) => a.index - b.index);

    lines.push(`Total chunks: ${chunks.length}`);
    lines.push(
      `Last 5% starts from chunk: ${Math.floor(chunks.length * 0.95)}`
    );
    lines.push(
      `Format: [Index] Status | Init: HH:MM:SS | Sent: HH:MM:SS | Wait: time | Ack: HH:MM:SS | Duration | Retries`
    );
    lines.push(`-`.repeat(80));

    // Calculate the last 5% of chunks
    const totalChunks = chunks.length;
    const last5PercentStart = Math.floor(totalChunks * 0.95);

    sortedChunks.forEach((chunk) => {
      const index = chunk.index;
      const status = chunk.status;
      const retries = chunk.retries || 0;

      // Show chunks that uploaded successfully without retries only if they're in the last 5%
      const isInLast5Percent = index >= last5PercentStart;
      const shouldShow =
        status === "done" && retries === 0 ? isInLast5Percent : true;

      if (!shouldShow) {
        return; // Skip this chunk
      }

      // Format chunk using helper method
      const { line, details } = this.formatChunkForDebug(chunk);
      lines.push(line);
      if (details.length > 0) {
        lines.push(...details);
      }
    });

    lines.push(`-`.repeat(80));

    // Summary statistics
    const statusCounts = this.getChunkStatusCounts(chunks);
    const totalRetries = chunks.reduce(
      (sum, chunk) => sum + (chunk.retries || 0),
      0
    );
    const avgTimeTaken =
      chunks
        .filter((chunk) => chunk.timeTaken)
        .reduce((sum, chunk) => sum + chunk.timeTaken, 0) /
      chunks.filter((chunk) => chunk.timeTaken).length;

    lines.push(
      `Summary: Done: ${statusCounts.done}, Uploading: ${statusCounts.uploading}, Retry: ${statusCounts.retrying}, Init: ${statusCounts.init}`
    );
    lines.push(
      `Total retries: ${totalRetries}, Average upload time: ${
        avgTimeTaken ? avgTimeTaken.toFixed(1) + "s" : "N/A"
      }`
    );

    // Multi-socket retry information for last chunk
    if (this.lastChunkMultiSocketRetryTime) {
      const timeSinceLastMultiSocketRetry = Math.floor(
        (Date.now() - this.lastChunkMultiSocketRetryTime) / 1000
      );
      lines.push(``);
      lines.push(`=== LAST CHUNK MULTI-SOCKET RETRY ===`);
      lines.push(
        `Last multi-socket retry: ${timeSinceLastMultiSocketRetry} seconds ago`
      );
      lines.push(
        `Cooldown period: ${
          this.lastChunkMultiSocketRetryCooldown / 1000
        } seconds`
      );
      lines.push(
        `Cooldown status: ${
          timeSinceLastMultiSocketRetry <
          this.lastChunkMultiSocketRetryCooldown / 1000
            ? `Active (${
                this.lastChunkMultiSocketRetryCooldown / 1000 -
                timeSinceLastMultiSocketRetry
              }s remaining)`
            : "Expired - ready for next multi-socket retry"
        }`
      );
    } else {
      // Check if we have chunks that might need multi-socket retry
      const chunksYetToAck = this.chunksInfo.chunks.filter(
        (chunk) => !this.chunksAckd.has(chunk.index)
      );
      if (chunksYetToAck.length === 1) {
        const lastChunk = chunksYetToAck[0];
        lines.push(``);
        lines.push(`=== LAST CHUNK MULTI-SOCKET RETRY ===`);
        lines.push(
          `Last chunk (${lastChunk.index}) is the only remaining chunk`
        );
        lines.push(`Multi-socket retry: Not attempted yet`);
        lines.push(
          `Cooldown period: ${
            this.lastChunkMultiSocketRetryCooldown / 1000
          } seconds`
        );
        lines.push(`Status: Ready for multi-socket retry if needed`);
      }
    }

    // Add chunk events summary using common method
    const chunkEventsLines = this.formatChunkEventsSummary("lines");
    lines.push(...chunkEventsLines);

    return lines;
  };
}

export default WSUploads;
